import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, Brain, Activity, Database, RefreshCw, Loader2,
  AlertTriangle, Target, Zap, Trophy, Sigma, Layers, Gauge, PlayCircle,
  ShieldCheck, Search, Timer, TrendingUp, Send,
} from 'lucide-react';
import { adminGet, adminPost } from '../data/api';
import SignalQualityBadge from '../components/SignalQualityBadge';
import PersonalSignalModal from '../components/PersonalSignalModal';
import SetProfilePanel from '../components/SetProfilePanel';
import ProfileHitRateWidget from '../components/ProfileHitRateWidget';
import ConfusionMatrixPanel from '../components/ConfusionMatrixPanel';
import SetProfileShadowValidationPanel from '../components/SetProfileShadowValidationPanel';
import SetProfileRoutingValidationPanel from '../components/SetProfileRoutingValidationPanel';
import ModelSignalStatsPanel from '../components/ModelSignalStatsPanel';
import MwCalibrationDriftPanel from '../components/MwCalibrationDriftPanel';
import Form48hValidationPanel from '../components/Form48hValidationPanel';
import SetProfileDebugTracePanel from '../components/SetProfileDebugTracePanel';
import LazyMountGate from '../components/LazyMountGate';

const mono = { fontFamily: 'JetBrains Mono, monospace' };
const heading = { fontFamily: 'Outfit, sans-serif' };
const card = { background: '#18181B', border: '1px solid #27272A' };

function fmtPct(v, d = 1) {
  if (v === null || v === undefined || Number.isNaN(Number(v))) return '—';
  return `${(Number(v) * 100).toFixed(d)}%`;
}
function fmtNum(v) {
  if (v === null || v === undefined) return '—';
  const n = Number(v);
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 10_000) return `${(n / 1_000).toFixed(1)}k`;
  return n.toLocaleString('it-IT');
}
function fmtFloat(v, d = 3) {
  if (v === null || v === undefined) return '—';
  return Number(v).toFixed(d);
}
function fmtDelta(v, d = 3) {
  if (v === null || v === undefined) return '—';
  const n = Number(v);
  const sign = n >= 0 ? '+' : '';
  return `${sign}${n.toFixed(d)}`;
}
function fmtDate(s) {
  if (!s) return '—';
  try { return new Date(s).toLocaleString('it-IT'); } catch { return s; }
}
function shortId(id) {
  if (!id) return '—';
  return id.length > 24 ? `${id.slice(0, 12)}…${id.slice(-8)}` : id;
}

function Pill({ color = 'zinc', children, testid }) {
  const palette = {
    emerald: { c: '#6EE7B7', bg: '#064E3B', border: '#065F46' },
    sky: { c: '#7DD3FC', bg: '#0C4A6E', border: '#0369A1' },
    amber: { c: '#FCD34D', bg: '#78350F', border: '#92400E' },
    rose: { c: '#FCA5A5', bg: '#7F1D1D', border: '#991B1B' },
    zinc: { c: '#D4D4D8', bg: '#27272A', border: '#3F3F46' },
    violet: { c: '#C4B5FD', bg: '#4C1D95', border: '#5B21B6' },
  }[color] || { c: '#D4D4D8', bg: '#27272A', border: '#3F3F46' };
  return (
    <span
      data-testid={testid}
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[10px] font-medium"
      style={{ ...mono, color: palette.c, background: palette.bg, border: `1px solid ${palette.border}` }}
    >
      {children}
    </span>
  );
}

function fmtAge(sec) {
  if (sec === null || sec === undefined) return '—';
  const n = Number(sec);
  if (Number.isNaN(n)) return '—';
  if (n < 60) return `${n}s`;
  if (n < 3600) return `${Math.floor(n / 60)}m`;
  return `${(n / 3600).toFixed(1)}h`;
}

// Inline badge used inside the prediction details panel to surface the
// live book quote + EV next to each SetScore probability. `compact` mode
// is used in tight columns (HC); the full layout shows odd, EV mean and
// EV conservative on two lines.
function BookEvBadge({ odd, ev, evCons, bookmaker, ageSec, isStale, testid, compact }) {
  if (odd === null || odd === undefined) {
    return (
      <span data-testid={testid}
            className="text-[10px] text-zinc-700">
        no book
      </span>
    );
  }
  const evColor =
    ev === null || ev === undefined ? 'text-zinc-600'
    : ev >= 5 ? 'text-emerald-300 font-semibold'
    : ev >= 0 ? 'text-emerald-400'
    : ev >= -5 ? 'text-zinc-400'
    : 'text-rose-400';
  const evText = ev === null || ev === undefined ? '—'
    : `${ev >= 0 ? '+' : ''}${ev.toFixed(1)}%`;
  const evConsText = evCons === null || evCons === undefined ? '—'
    : `${evCons >= 0 ? '+' : ''}${evCons.toFixed(1)}%`;

  if (compact) {
    return (
      <span data-testid={testid}
            className="text-[10px] flex items-center gap-1.5"
            title={`${bookmaker || ''} · age ${fmtAge(ageSec)}${isStale ? ' (STALE)' : ''}`}>
        <span className="text-zinc-500">@</span>
        <span className="text-zinc-200" style={mono}>{Number(odd).toFixed(2)}</span>
        <span className={evColor} style={mono}>{evText}</span>
        {isStale && <span className="text-rose-400">·stale</span>}
      </span>
    );
  }
  return (
    <div data-testid={testid}
         className="mt-1 pt-1 border-t border-zinc-900 text-[10px]"
         style={mono}>
      <div className="flex items-center gap-2">
        <span className="text-zinc-500">book</span>
        <span className="text-zinc-200">{Number(odd).toFixed(2)}</span>
        <span className="text-zinc-600">·</span>
        <span className="text-zinc-500">{bookmaker || '—'}</span>
        <span className={isStale ? 'text-rose-400' : 'text-emerald-400'}>
          {fmtAge(ageSec)}
        </span>
      </div>
      <div className="flex items-center gap-2">
        <span className="text-zinc-500">EV</span>
        <span className={evColor}>{evText}</span>
        <span className="text-zinc-600">·</span>
        <span className="text-zinc-500">cons</span>
        <span className="text-zinc-500">{evConsText}</span>
      </div>
    </div>
  );
}

function StatCard({ label, value, sublabel, icon: Icon, testid, accent = 'zinc' }) {
  const accentColor = {
    emerald: '#34D399', sky: '#38BDF8', amber: '#FBBF24',
    rose: '#F87171', zinc: '#A1A1AA', violet: '#A78BFA',
  }[accent];
  return (
    <div data-testid={testid} className="rounded-sm p-4" style={card}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] uppercase tracking-wider text-zinc-500" style={heading}>
          {label}
        </div>
        {Icon && <Icon size={13} style={{ color: accentColor }} />}
      </div>
      <div className="text-2xl font-bold text-zinc-50" style={mono}>{value}</div>
      {sublabel && (
        <div className="text-[10px] text-zinc-500 mt-1" style={mono}>{sublabel}</div>
      )}
    </div>
  );
}

function SectionTitle({ icon: Icon, title, right }) {
  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        {Icon && <Icon size={14} className="text-zinc-400" />}
        <h2 className="text-sm uppercase tracking-wider text-zinc-300 font-semibold" style={heading}>
          {title}
        </h2>
      </div>
      {right}
    </div>
  );
}

// ------------- Main -------------
export default function AdminSetScoreEngine() {
  const [pin] = useState(() => sessionStorage.getItem('admin_pin') || '');
  const [loadingHealth, setLoadingHealth] = useState(true);
  const [health, setHealth] = useState(null);
  const [calib, setCalib] = useState(null);
  const [dq, setDq] = useState(null);
  const [err, setErr] = useState(null);

  const [upcoming, setUpcoming] = useState([]);
  const [upLoading, setUpLoading] = useState(false);
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [prediction, setPrediction] = useState(null);
  const [predLoading, setPredLoading] = useState(false);
  const [predictionOdds, setPredictionOdds] = useState(null);
  const [nSim, setNSim] = useState(10000);
  const [forceRefresh, setForceRefresh] = useState(false);
  const [searchText, setSearchText] = useState('');
  // Smart Filters (audit/search only — never trigger predict/refresh).
  const [filterStrongOnly, setFilterStrongOnly] = useState(false);
  const [filterProfile, setFilterProfile] = useState('all');
  const [filterMarket, setFilterMarket] = useState('all');
  const [filterEv, setFilterEv] = useState('all');
  const [filterHideNoEdge, setFilterHideNoEdge] = useState(false);
  const [summaries, setSummaries] = useState({}); // match_id -> summary
  const [summariesLoading, setSummariesLoading] = useState(false);
  const [refreshNowRunning, setRefreshNowRunning] = useState(false);
  const [refreshNowResult, setRefreshNowResult] = useState(null);
  // Personal Telegram signals (operator-curated, separate stats)
  const [personalSendOpen, setPersonalSendOpen] = useState(false);
  const [personalSendForm, setPersonalSendForm] = useState(null);
  const [personalSendBusy, setPersonalSendBusy] = useState(false);
  const [personalRecap, setPersonalRecap] = useState(null);
  const [personalRecent, setPersonalRecent] = useState(null);
  // Ticks every 30s so the "last refresh Xm ago" pill recomputes live
  const [nowTick, setNowTick] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNowTick(Date.now()), 30_000);
    return () => clearInterval(id);
  }, []);

  const [recent, setRecent] = useState(null);
  const [recentLoading, setRecentLoading] = useState(false);

  const [btReport, setBtReport] = useState(null);
  const [btRunning, setBtRunning] = useState(false);
  const [fitRunning, setFitRunning] = useState(false);
  const [purgeRunning, setPurgeRunning] = useState(false);

  const [autoStats, setAutoStats] = useState(null);
  const [autoLoading, setAutoLoading] = useState(false);
  const [clvSummary, setClvSummary] = useState(null);
  const [clvLoading, setClvLoading] = useState(false);
  const [autoPredictRunning, setAutoPredictRunning] = useState(false);
  const [clvSettleRunning, setClvSettleRunning] = useState(false);

  const [paperReport, setPaperReport] = useState(null);
  const [paperRecent, setPaperRecent] = useState(null);
  const [paperLoading, setPaperLoading] = useState(false);
  const [paperRunning, setPaperRunning] = useState(false);

  const [clvAudit, setClvAudit] = useState(null);
  const [clvAuditRunning, setClvAuditRunning] = useState(false);
  const [clvAuditSample, setClvAuditSample] = useState(200);
  const [clvResweepResult, setClvResweepResult] = useState(null);
  const [clvResweepRunning, setClvResweepRunning] = useState(false);
  const [clvResweepStatus, setClvResweepStatus] = useState('idle');
  const [clvResweepError, setClvResweepError] = useState(null);
  const [closeCapture, setCloseCapture] = useState(null);
  const [paperSettleRunning, setPaperSettleRunning] = useState(false);

  // Jobs Health watchdog (auto_predict / close_capture stuck detector)
  const [jobsHealth, setJobsHealth] = useState(null);
  const [jobsHealthLoading, setJobsHealthLoading] = useState(false);

  // Real Odds & EV Check (read-only audit table)
  const [realOdds, setRealOdds] = useState(null);
  const [realOddsLoading, setRealOddsLoading] = useState(false);
  const [realOddsHorizon, setRealOddsHorizon] = useState(24);
  const [realOddsLimit, setRealOddsLimit] = useState(30);
  const [realOddsOnlyWithBook, setRealOddsOnlyWithBook] = useState(false);
  const [realOddsMinEv, setRealOddsMinEv] = useState(0); // % filter on best EV
  const [realOddsHideStale, setRealOddsHideStale] = useState(false);

  const loadHealth = useCallback(async () => {
    setLoadingHealth(true); setErr(null);
    try {
      const [h, c, d] = await Promise.all([
        adminGet('/tt/setscore/health', pin),
        adminGet('/tt/setscore/calibration-status', pin),
        adminGet('/tt/setscore/data-quality-audit?lookback_days=540', pin),
      ]);
      setHealth(h); setCalib(c); setDq(d);
    } catch (e) {
      setErr(String(e?.message || e));
    } finally {
      setLoadingHealth(false);
    }
  }, [pin]);

  const loadUpcoming = useCallback(async () => {
    setUpLoading(true);
    try {
      const r = await adminGet('/tt/setscore/upcoming-matches?limit=80', pin);
      setUpcoming(r.matches || []);
    } catch (e) {
      setErr(String(e?.message || e));
    } finally {
      setUpLoading(false);
    }
  }, [pin]);

  // Batch summary fetch for the upcoming list — pure read, never triggers
  // a prediction. Powers the Smart Filters + per-row badges.
  const loadUpcomingSummaries = useCallback(async (matchIds) => {
    if (!matchIds || matchIds.length === 0) {
      setSummaries({});
      return;
    }
    setSummariesLoading(true);
    try {
      const r = await adminPost(
        '/tt/setscore/upcoming-summary', pin,
        { match_ids: matchIds },
      );
      const map = {};
      (r?.summaries || []).forEach((s) => { map[s.match_id] = s; });
      setSummaries(map);
    } catch (e) {
      // Non-fatal: list still works, just without badges/filters.
      // eslint-disable-next-line no-console
      console.warn('upcoming-summary failed', e);
    } finally {
      setSummariesLoading(false);
    }
  }, [pin]);

  const loadRecent = useCallback(async () => {
    setRecentLoading(true);
    try {
      const r = await adminGet('/tt/setscore/recent-predictions?limit=25', pin);
      setRecent(r);
    } catch (e) {
      setErr(String(e?.message || e));
    } finally {
      setRecentLoading(false);
    }
  }, [pin]);

  const loadBacktestReport = useCallback(async () => {
    try {
      const r = await adminGet('/tt/setscore/backtest-report?limit=5', pin);
      setBtReport(r);
    } catch (e) {
      setErr(String(e?.message || e));
    }
  }, [pin]);

  const loadAutoStats = useCallback(async () => {
    setAutoLoading(true);
    try {
      const r = await adminGet('/tt/setscore/auto-predict-stats', pin);
      setAutoStats(r);
    } catch (e) {
      setErr(String(e?.message || e));
    } finally {
      setAutoLoading(false);
    }
  }, [pin]);

  const loadClvSummary = useCallback(async () => {
    setClvLoading(true);
    try {
      const r = await adminGet('/tt/setscore/clv-summary', pin);
      setClvSummary(r);
    } catch (e) {
      setErr(String(e?.message || e));
    } finally {
      setClvLoading(false);
    }
  }, [pin]);

  const loadCloseCapture = useCallback(async () => {
    try {
      const r = await adminGet('/tt/setscore/close-capture-stats', pin);
      setCloseCapture(r);
    } catch (e) {
      // Non-critical: if endpoint missing the strip just stays empty.
      console.warn('close-capture-stats failed:', e?.message);
    }
  }, [pin]);

  const loadJobsHealth = useCallback(async () => {
    setJobsHealthLoading(true);
    try {
      const r = await adminGet('/tt/admin/jobs-health', pin);
      setJobsHealth(r);
    } catch (e) {
      console.warn('jobs-health failed:', e?.message);
    } finally {
      setJobsHealthLoading(false);
    }
  }, [pin]);

  const loadPersonalRecap = useCallback(async () => {
    try {
      const [recap, recent] = await Promise.all([
        adminGet('/tt/admin/personal-signals/recap-today', pin),
        adminGet('/tt/admin/personal-signals/recent?limit=10', pin),
      ]);
      setPersonalRecap(recap);
      setPersonalRecent(recent);
    } catch (e) {
      console.warn('personal-signals load failed:', e?.message);
    }
  }, [pin]);

  const loadPaperSignals = useCallback(async () => {
    setPaperLoading(true);
    try {
      const [report, recent] = await Promise.all([
        adminGet('/tt/setscore/paper-signals-report', pin),
        adminGet('/tt/setscore/paper-signals-recent?limit=15', pin),
      ]);
      setPaperReport(report);
      setPaperRecent(recent);
    } catch (e) {
      setErr(String(e?.message || e));
    } finally {
      setPaperLoading(false);
    }
  }, [pin]);

  const loadRealOddsCheck = useCallback(async () => {
    setRealOddsLoading(true);
    try {
      const qs = new URLSearchParams({
        limit: String(realOddsLimit),
        upcoming_only: 'true',
        horizon_hours: String(realOddsHorizon),
        only_with_book: realOddsOnlyWithBook ? 'true' : 'false',
      });
      const r = await adminGet(`/tt/setscore/real-odds-check?${qs.toString()}`, pin);
      setRealOdds(r);
    } catch (e) {
      setErr(String(e?.message || e));
    } finally {
      setRealOddsLoading(false);
    }
  }, [pin, realOddsHorizon, realOddsLimit, realOddsOnlyWithBook]);

  useEffect(() => {
    loadHealth();
    loadUpcoming();
    loadRecent();
    loadBacktestReport();
    loadAutoStats();
    loadClvSummary();
    loadPaperSignals();
    loadCloseCapture();
    loadRealOddsCheck();
    loadJobsHealth();
    loadPersonalRecap();
  }, [loadHealth, loadUpcoming, loadRecent, loadBacktestReport,
      loadAutoStats, loadClvSummary, loadPaperSignals, loadCloseCapture,
      loadRealOddsCheck, loadJobsHealth, loadPersonalRecap]);

  // Auto-refresh the jobs-health banner every 60s so the operator
  // notices a stuck job within ~1 min of opening the dashboard.
  useEffect(() => {
    const id = setInterval(() => { loadJobsHealth(); }, 60_000);
    return () => clearInterval(id);
  }, [loadJobsHealth]);

  // Refresh summaries whenever the upcoming list changes. Pure read;
  // no predictions are triggered.
  useEffect(() => {
    if (!upcoming || upcoming.length === 0) {
      setSummaries({});
      return;
    }
    const ids = upcoming.slice(0, 80).map((m) => m.id).filter(Boolean);
    loadUpcomingSummaries(ids);
  }, [upcoming, loadUpcomingSummaries]);

  const runPredict = useCallback(async (matchId) => {
    if (!matchId) return;
    setPredLoading(true); setErr(null); setPrediction(null);
    setPredictionOdds(null);
    try {
      const qs = `n_sim=${nSim}&persist=1${forceRefresh ? '&refresh=1' : ''}`;
      const [r, oddsBundle] = await Promise.all([
        adminGet(`/tt/setscore/predict/${encodeURIComponent(matchId)}?${qs}`, pin),
        adminGet(`/tt/setscore/match-odds-bundle/${encodeURIComponent(matchId)}`, pin)
          .catch(() => null),
      ]);
      setPrediction(r);
      setPredictionOdds(oddsBundle);
      // Refresh recent predictions list so new row appears
      loadRecent();
    } catch (e) {
      setErr(String(e?.message || e));
    } finally {
      setPredLoading(false);
    }
  }, [nSim, forceRefresh, pin, loadRecent]);

  const refreshNowNow = useCallback(async (matchId) => {
    if (!matchId) return;
    setRefreshNowRunning(true); setErr(null); setRefreshNowResult(null);
    try {
      const r = await adminPost(
        `/tt/setscore/refresh-now/${encodeURIComponent(matchId)}?n_sim=${nSim}`,
        pin, {}, { timeoutMs: 60_000 },
      );
      setRefreshNowResult(r);
      // Re-pull prediction + odds so the UI reflects the new data
      const qs = `n_sim=${nSim}&persist=1`;
      const [pred, oddsBundle] = await Promise.all([
        adminGet(`/tt/setscore/predict/${encodeURIComponent(matchId)}?${qs}`, pin),
        adminGet(`/tt/setscore/match-odds-bundle/${encodeURIComponent(matchId)}`, pin)
          .catch(() => null),
      ]);
      setPrediction(pred);
      setPredictionOdds(oddsBundle);
      loadRecent();
    } catch (e) {
      setErr(String(e?.message || e));
    } finally {
      setRefreshNowRunning(false);
    }
  }, [nSim, pin, loadRecent]);

  const runFit = useCallback(async () => {
    if (!window.confirm('Avviare fit nightly completo? (~3-5 secondi)')) return;
    setFitRunning(true); setErr(null);
    try {
      await adminPost('/tt/setscore/fit-now?lookback_days=540', pin, {});
      await loadHealth();
    } catch (e) {
      setErr(String(e?.message || e));
    } finally {
      setFitRunning(false);
    }
  }, [pin, loadHealth]);

  const runClvAudit = useCallback(async () => {
    setClvAuditRunning(true); setErr(null); setClvAudit(null);
    try {
      const r = await adminGet(
        `/tt/setscore/clv-no-closing-audit?sample=${clvAuditSample}`,
        pin,
      );
      setClvAudit(r);
    } catch (e) {
      setErr(String(e?.message || e));
    } finally {
      setClvAuditRunning(false);
    }
  }, [pin, clvAuditSample]);

  const runClvResweep = useCallback(async (dryRun = false) => {
    if (!dryRun && !window.confirm(
      'Re-sweep NO_CLOSING_ODDS?\n\n' +
      'Resetta ogni riga clv_status=NO_CLOSING_ODDS a PENDING e ' +
      're-runna il CLV settler con la finestra attuale (120 min).\n\n' +
      'Non tocca AVAILABLE, live betting, Telegram, modelli.'
    )) return;
    setClvResweepRunning(true);
    setClvResweepStatus('running');
    setClvResweepError(null);
    setClvResweepResult(null);
    setErr(null);
    try {
      const path = dryRun
        ? '/tt/setscore/clv-resweep-no-closing?dry_run=1'
        : '/tt/setscore/clv-resweep-no-closing';
      // Re-sweep can iterate hundreds of predictions on Atlas — give it
      // up to 3 minutes before the AbortController kills the request.
      const r = await adminPost(path, pin, {}, { timeoutMs: 180_000 });
      setClvResweepResult(r);
      setClvResweepStatus('success');
      // Refresh dependent strips so CLV Tracker + Audit reflect the
      // new state without forcing a page reload.
      try { await loadClvSummary(); } catch (_e) { /* best-effort */ }
      try {
        if (clvAudit) {
          const fresh = await adminGet(
            `/tt/setscore/clv-no-closing-audit?sample=${clvAuditSample}`,
            pin,
          );
          setClvAudit(fresh);
        }
      } catch (_e) { /* best-effort */ }
    } catch (e) {
      const msg = e?.message || String(e);
      const detail = e?.detail || '';
      const status = e?.status || (e?.isTimeout ? 'TIMEOUT'
        : e?.isNetwork ? 'NETWORK' : 'ERROR');
      setClvResweepError({
        status,
        message: msg,
        detail: detail || null,
        is_timeout: !!e?.isTimeout,
        is_network: !!e?.isNetwork,
      });
      setClvResweepStatus('failed');
      // Keep the global banner too for visibility from the top.
      setErr(msg);
    } finally {
      setClvResweepRunning(false);
    }
  }, [pin, loadClvSummary, clvAudit, clvAuditSample]);


  const runPurgeColdStart = useCallback(async () => {
    if (!window.confirm(
      'Cancellare TUTTE le predictions e paper signals SetScore?\n\n' +
      'Operazione distruttiva. Da usare solo dopo il primo fit nightly per ' +
      'rimuovere predictions generate in cold-start.\n\n' +
      'Non tocca latent_state, tightness, tournament_adjustments, fit_history.'
    )) return;
    setPurgeRunning(true); setErr(null);
    try {
      const r = await adminPost('/tt/setscore/purge-cold-start', pin, {});
      await loadHealth();
      window.alert(
        `Purge completato (${r.mode || 'all'}):\n` +
        `  predictions cancellate: ${r.deleted_predictions ?? r.predictions_deleted ?? 0}\n` +
        `  paper signals cancellati: ${r.deleted_paper_signals ?? r.paper_signals_deleted ?? 0}\n` +
        (r.reason ? `\n${r.reason}` : '')
      );
    } catch (e) {
      setErr(String(e?.message || e));
    } finally {
      setPurgeRunning(false);
    }
  }, [pin, loadHealth]);

  const runBacktest = useCallback(async () => {
    if (!window.confirm('Avviare backtest su ~500 match finished?')) return;
    setBtRunning(true); setErr(null);
    try {
      // Backtest replays 500 matches × 3000 sims each + DB lookups; on
      // Atlas + K8s ingress this can spike past the default 45s timeout.
      // Use a 3-min cap (same as CLV resweep) to absorb tail latency.
      await adminPost(
        '/tt/setscore/backtest-run?max_matches=500&n_sim=3000', pin, {},
        { timeoutMs: 180_000 },
      );
      await loadBacktestReport();
    } catch (e) {
      setErr(String(e?.message || e));
    } finally {
      setBtRunning(false);
    }
  }, [pin, loadBacktestReport]);

  const runAutoPredictNow = useCallback(async () => {
    if (!window.confirm('Triggerare manualmente l\'auto predict job?')) return;
    setAutoPredictRunning(true); setErr(null);
    try {
      await adminPost('/tt/setscore/auto-predict-now?horizon_hours=6&n_sim=10000',
                       pin, {});
      await Promise.all([loadAutoStats(), loadRecent(), loadHealth()]);
    } catch (e) {
      setErr(String(e?.message || e));
    } finally {
      setAutoPredictRunning(false);
    }
  }, [pin, loadAutoStats, loadRecent, loadHealth]);

  const runClvSettleNow = useCallback(async () => {
    setClvSettleRunning(true); setErr(null);
    try {
      await adminPost('/tt/setscore/clv-settle-now', pin, {});
      await Promise.all([loadClvSummary(), loadAutoStats(), loadRecent()]);
    } catch (e) {
      setErr(String(e?.message || e));
    } finally {
      setClvSettleRunning(false);
    }
  }, [pin, loadClvSummary, loadAutoStats, loadRecent]);

  const runPaperSignalsNow = useCallback(async () => {
    if (!window.confirm('Triggerare manualmente paper signals? (max 5 al giorno, rispetta tutti i gate)')) return;
    setPaperRunning(true); setErr(null);
    try {
      const r = await adminPost('/tt/setscore/paper-signals-run-now', pin, {});
      await loadPaperSignals();
      if (r?.status && r.status !== 'OK') {
        setErr(`Paper run: ${r.status} (audit: ${r.audit_verdict || 'unknown'})`);
      }
    } catch (e) {
      setErr(String(e?.message || e));
    } finally {
      setPaperRunning(false);
    }
  }, [pin, loadPaperSignals]);

  const runPaperSettleNow = useCallback(async () => {
    setPaperSettleRunning(true); setErr(null);
    try {
      const r = await adminPost('/tt/setscore/paper-signals-resettle-pending', pin, {});
      await loadPaperSignals();
      if (r) {
        // Brief inline notice so the operator sees the resettle stats.
        const summary = `Resettled: ${r.settled || 0} · stuck recovered: ${r.stuck_status_recovered || 0} · still pending: ${r.still_pending || 0}`;
        // eslint-disable-next-line no-console
        console.info('[paper resettle]', summary, r);
      }
    } catch (e) {
      setErr(String(e?.message || e));
    } finally {
      setPaperSettleRunning(false);
    }
  }, [pin, loadPaperSignals]);

  const [paperDebug, setPaperDebug] = useState(null);
  const debugPaperSignal = useCallback(async (signalId) => {
    setPaperDebug({ signal_id: signalId, loading: true });
    try {
      const r = await adminGet(
        `/tt/setscore/paper-signal-debug/${signalId}`, pin,
      );
      // Backend returns HTTP 200 with `{error: "signal_not_found"}` when
      // the row is gone from DB — surface that as a dedicated state so
      // the panel can show actionable guidance instead of a raw "404".
      if (r && r.error === 'signal_not_found') {
        setPaperDebug({
          signal_id: signalId,
          loading: false,
          signal_id_not_found: true,
        });
        return;
      }
      setPaperDebug({ ...r, loading: false });
    } catch (e) {
      setPaperDebug({
        signal_id: signalId,
        loading: false,
        error: String(e?.message || e),
      });
    }
  }, [pin]);

  const filteredUpcoming = useMemo(() => {
    const q = (searchText || '').toLowerCase();
    const evMin = (() => {
      switch (filterEv) {
        case 'ev_5': return 5;
        case 'ev_8': return 8;
        case 'ev_10': return 10;
        default: return null;
      }
    })();
    const list = upcoming.filter((m) => {
      // Text search
      if (q) {
        const hay = `${m.player1_name || ''} ${m.player2_name || ''} ${m.tournament_name || ''} ${m.id || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      const s = summaries[m.id];
      // Filters that require a summary
      if (filterStrongOnly) {
        if (!s || !s.any_strong_guard) return false;
      }
      if (filterProfile !== 'all') {
        if (!s || s.profile_type !== filterProfile) return false;
      }
      if (filterMarket !== 'all') {
        if (!s) return false;
        if (filterMarket === 'none') {
          if (s.recommended_market) return false;
        } else if (s.recommended_market !== filterMarket) {
          return false;
        }
      }
      if (filterEv === 'neg') {
        if (!s || s.recommended_ev_pct == null || s.recommended_ev_pct >= 0) return false;
      } else if (evMin !== null) {
        if (!s || s.recommended_ev_pct == null || s.recommended_ev_pct < evMin) return false;
      }
      if (filterHideNoEdge) {
        if (!s) return true; // can't decide → keep visible
        const noRec = !s.recommended_market;
        const lowEv = s.recommended_ev_pct == null || s.recommended_ev_pct < 5;
        const noStrong = !s.any_strong_guard;
        if (noRec && lowEv && noStrong) return false;
      }
      return true;
    });
    return list.slice(0, 50);
  }, [upcoming, summaries, searchText, filterStrongOnly,
      filterProfile, filterMarket, filterEv, filterHideNoEdge]);

  const inputs = prediction?.inputs_snapshot;
  const outputs = prediction?.outputs;
  const cmp = prediction?.comparison;

  return (
    <div className="space-y-6 fade-in" data-testid="admin-setscore-engine">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link
            to="/admin"
            data-testid="back-to-admin"
            className="flex items-center gap-1 text-zinc-500 hover:text-zinc-200 text-sm"
          >
            <ArrowLeft size={14} /> Admin
          </Link>
          <div>
            <h1 className="text-3xl font-bold text-zinc-50 tracking-tight"
                style={heading}>
              SetScore Generative Engine
            </h1>
            <p className="text-zinc-400 mt-1 text-xs" style={mono}>
              Phase 2 · PAPER_SIGNAL · read-only dashboard
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Pill color={health?.mode === 'PAPER_SIGNAL' ? 'violet' : 'amber'}
                testid="badge-mode">
            MODE: {health?.mode || '—'}
          </Pill>
          <Pill color="sky" testid="badge-version">
            {health?.model_version || '—'}
          </Pill>
          <button
            onClick={loadHealth}
            disabled={loadingHealth}
            data-testid="refresh-health-btn"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-xs text-zinc-400 hover:text-emerald-300 disabled:opacity-50"
            style={{ border: '1px solid #27272A' }}
          >
            {loadingHealth ? <Loader2 size={12} className="animate-spin" />
              : <RefreshCw size={12} />} Refresh
          </button>
        </div>
      </div>

      {err && (
        <div
          data-testid="error-banner"
          className="flex items-center gap-2 px-3 py-2 rounded-sm text-xs text-rose-300"
          style={{ ...mono, background: '#450A0A', border: '1px solid #7F1D1D' }}
        >
          <AlertTriangle size={12} /> {err}
        </div>
      )}

      {/* Background-jobs watchdog. Surfaces auto_predict / close_capture
          stuck states within 60s so we never relive the 14h silent
          outage of 2026-05-03. Hidden when all jobs are healthy. */}
      {jobsHealth?.any_stuck && (
        <div
          data-testid="jobs-health-banner"
          className="flex items-start gap-3 px-4 py-3 rounded-sm text-xs text-rose-200"
          style={{ ...mono, background: '#450A0A', border: '1px solid #B91C1C' }}
        >
          <AlertTriangle size={16} className="mt-0.5 shrink-0" />
          <div className="space-y-1 flex-1">
            <div className="font-semibold uppercase tracking-wider text-rose-100">
              Background jobs stuck (&gt;{jobsHealth.stuck_threshold_min}m)
            </div>
            <div className="space-y-1">
              {jobsHealth.jobs.filter(j => j.is_stuck).map(j => (
                <div key={j.key}
                     data-testid={`jobs-health-stuck-${j.key}`}
                     className="text-[11px]">
                  <span className="text-rose-300 font-semibold">{j.label}</span>
                  {' · '}
                  <span>elapsed: {
                    j.minutes_since_last_success != null
                      ? `${Math.round(j.minutes_since_last_success)}m`
                      : 'never'
                  }</span>
                  {' · '}
                  <span className="text-rose-400/80">
                    expected every {j.expected_interval_min}m
                  </span>
                  {j.last_error && (
                    <div className="text-[10px] text-rose-400/70 pl-2">
                      err: {j.last_error}
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div className="text-[10px] text-rose-400/60 pt-1">
              Action: check supervisor logs / restart backend if scheduler
              thread died. Telegram alert sent (deduped per {jobsHealth.resend_after_min}m).
            </div>
          </div>
          <button
            onClick={loadJobsHealth}
            disabled={jobsHealthLoading}
            data-testid="jobs-health-refresh-btn"
            className="px-2 py-1 text-[10px] rounded-sm text-rose-200 hover:text-rose-100 disabled:opacity-50"
            style={{ border: '1px solid #7F1D1D' }}
          >
            {jobsHealthLoading ? '…' : 'Refresh'}
          </button>
        </div>
      )}

      {/* Cold-start warning: shown only when at least one of the three core
          fitted collections is empty. Tells the operator that any prediction
          shown below is built on default priors (theta=0, sigma2=0.5, default
          tightness) and must NOT be evaluated as a real signal. */}
      {health && (
        (health.n_players_state ?? 0) === 0 ||
        (health.n_tightness_rows ?? 0) === 0 ||
        (health.n_tournament_adjustments ?? 0) === 0
      ) && (
        <div
          data-testid="setscore-cold-start-banner"
          className="flex items-start gap-3 px-4 py-3 rounded-sm text-xs text-amber-200"
          style={{ ...mono, background: '#451A03', border: '1px solid #92400E' }}
        >
          <AlertTriangle size={16} className="mt-0.5 shrink-0" />
          <div className="space-y-1">
            <div className="font-semibold uppercase tracking-wider text-amber-100">
              Engine cold-start
            </div>
            <div>
              latent state / tightness / tournament adjustments not fitted yet.
              Predictions may use default priors (θ=0, σ²=0.5, default tightness)
              and should not be evaluated. Trigger a nightly fit before reading any
              paper signal output.
            </div>
            <div className="text-[10px] text-amber-300/80 pt-1">
              players={health.n_players_state ?? 0} · tightness={health.n_tightness_rows ?? 0} · tournaments={health.n_tournament_adjustments ?? 0}
            </div>
          </div>
        </div>
      )}

      {/* Engine Health */}
      <SectionTitle icon={Activity} title="Engine Health" right={
        <div className="flex items-center gap-2">
          <button
            onClick={runFit}
            disabled={fitRunning}
            data-testid="run-fit-btn"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-xs text-emerald-300 hover:bg-emerald-950/30 disabled:opacity-50"
            style={{ border: '1px solid #065F46' }}
          >
            {fitRunning ? <Loader2 size={12} className="animate-spin" />
              : <Zap size={12} />}
            Run nightly fit
          </button>
          <button
            onClick={runPurgeColdStart}
            disabled={purgeRunning}
            data-testid="purge-cold-start-btn"
            title="Cancella predictions + paper signals SetScore (cold-start cleanup)"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-xs text-rose-300 hover:bg-rose-950/30 disabled:opacity-50"
            style={{ border: '1px solid #7F1D1D' }}
          >
            {purgeRunning ? <Loader2 size={12} className="animate-spin" />
              : <AlertTriangle size={12} />}
            Purge cold-start
          </button>
        </div>
      } />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard testid="stat-players-tracked" label="Players tracked"
          value={fmtNum(health?.n_players_state)} icon={Database} accent="sky"
          sublabel="rows in latent_state" />
        <StatCard testid="stat-cold-start" label="Cold-start"
          value={fmtNum(health?.n_players_cold_start)}
          sublabel={`${fmtFloat(health?.cold_start_pct, 2)}% del totale`}
          icon={AlertTriangle}
          accent={health?.cold_start_pct > 20 ? 'rose' : 'amber'} />
        <StatCard testid="stat-tightness" label="Tightness rows"
          value={fmtNum(health?.n_tightness_rows)} icon={Sigma}
          sublabel="posterior Dirichlet-Multinomial" />
        <StatCard testid="stat-tournaments" label="Tournament adj"
          value={fmtNum(health?.n_tournament_adjustments)} icon={Trophy}
          sublabel="φ_k residuals fitted" />
        <StatCard testid="stat-predictions-persisted" label="Predictions persisted"
          value={fmtNum(health?.n_predictions_total)} icon={Target} accent="emerald"
          sublabel="tt_setscore_predictions" />
        <StatCard testid="stat-last-fit-elapsed" label="Last fit elapsed"
          value={health?.last_fit?.elapsed_sec != null
            ? `${Number(health.last_fit.elapsed_sec).toFixed(1)}s` : '—'}
          sublabel={fmtDate(health?.last_fit?.completed_at)}
          icon={Gauge} />
        <StatCard testid="stat-last-fit-valid" label="Last fit · valid matches"
          value={fmtNum(health?.last_fit?.n_matches_valid)}
          sublabel={`scanned ${fmtNum(health?.last_fit?.n_matches_scanned)}`}
          icon={ShieldCheck} accent="emerald" />
        <StatCard testid="stat-last-fit-id" label="Last fit run id"
          value={<span className="text-sm">{shortId(health?.last_fit?.fit_run_id)}</span>}
          sublabel={`model ${health?.last_fit?.model_version || '—'}`}
          icon={Layers} />
      </div>

      {/* SET PROFILE FIRST — audit dashboards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <ProfileHitRateWidget pin={pin} days={14} />
        <ProfileHitRateWidget pin={pin} days={30} />
      </div>
      <ConfusionMatrixPanel pin={pin} defaultDays={30} />

      {/* Phase 2 SHADOW validation — legacy vs shadow vs actual outcome */}
      <LazyMountGate
        label="SetProfile Shadow Validation · Phase 2"
        description="Audit pesante — confronta routing legacy vs shadow su settled. Click per caricare."
        testid="lazy-gate-shadow-validation"
      >
        <SetProfileShadowValidationPanel pin={pin} defaultDays={30} />
      </LazyMountGate>

      {/* Routing Validation Dashboard (2026-05-13) — 8-priority analytics */}
      <LazyMountGate
        label="SetProfile Routing Validation Dashboard"
        description="Aggregazione 10 priorità su settled — query pesante. Click per caricare."
        testid="lazy-gate-routing-validation"
      >
        <SetProfileRoutingValidationPanel adminPin={pin} />
      </LazyMountGate>

      {/* Model Signal Stats — guards / blocks / switch impact analytics */}
      <LazyMountGate
        label="Model Signal Stats"
        description="Guards, blocks e impatto switch. Click per caricare."
        testid="lazy-gate-model-signal-stats"
      >
        <ModelSignalStatsPanel pin={pin} />
      </LazyMountGate>

      {/* MW Calibration Drift — EV vs realized ROI buckets */}
      <LazyMountGate
        label="MW Calibration Drift"
        description="EV vs ROI realizzato per bucket. Click per caricare."
        testid="lazy-gate-mw-calibration-drift"
      >
        <MwCalibrationDriftPanel pin={pin} />
      </LazyMountGate>

      {/* Form 48h Validation — Raw vs Form 48h accuracy/Brier on settled */}
      <LazyMountGate
        label="Form 48h Validation"
        description="Raw vs Form 48h accuracy/Brier su settled — query pesante (limit 2000). Click per caricare."
        testid="lazy-gate-form48h-validation"
      >
        <Form48hValidationPanel pin={pin} />
      </LazyMountGate>
      <SetProfileDebugTracePanel pin={pin} />

      {/* Data Quality */}
      <SectionTitle icon={ShieldCheck} title="Data Quality Audit (540d)" />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <div className="rounded-sm p-4" style={card}>
          <div className="text-[10px] uppercase text-zinc-500 mb-2" style={heading}>
            Totals
          </div>
          <div className="space-y-1.5 text-xs" style={mono}>
            <div className="flex justify-between">
              <span className="text-zinc-500">scanned</span>
              <span className="text-zinc-100">{fmtNum(dq?.total_scanned)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-zinc-500">valid</span>
              <span className="text-emerald-300" data-testid="dq-valid">
                {fmtNum(dq?.valid)} ({dq?.total_scanned
                  ? fmtPct(dq.valid / dq.total_scanned, 2) : '—'})
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-zinc-500">excluded</span>
              <span className="text-rose-300" data-testid="dq-excluded">
                {fmtNum(dq?.excluded_total)}
              </span>
            </div>
          </div>
        </div>
        <div className="rounded-sm p-4 lg:col-span-2" style={card}
             data-testid="dq-reasons">
          <div className="text-[10px] uppercase text-zinc-500 mb-2" style={heading}>
            Exclusion reasons
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 text-xs"
               style={mono}>
            {dq?.by_reason && Object.entries(dq.by_reason)
              .filter(([k]) => k !== 'valid')
              .sort((a, b) => b[1] - a[1])
              .map(([reason, count]) => (
                <div key={reason} className="flex justify-between">
                  <span className="text-zinc-500">{reason}</span>
                  <span className={count > 0 ? 'text-amber-300' : 'text-zinc-600'}>
                    {fmtNum(count)}
                  </span>
                </div>
              ))}
          </div>
        </div>
      </div>

      {/* Calibration */}
      <SectionTitle icon={Gauge} title="Calibration Status" right={
        calib?._kill_switch ? (
          <Pill
            testid="calib-killswitch"
            color={calib._kill_switch.setscore_calibration_active
              ? 'emerald' : 'rose'}
          >
            {`KILL SWITCH: ${calib._kill_switch.setscore_calibration_active
              ? 'ACTIVE' : 'OFF'}`}
          </Pill>
        ) : null
      } />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {calib && ['mw', 'hc_1_5', 'hc_2_5', 'ou'].map((market) => {
          const c = calib[market] || {};
          const method = c.calibration_method || 'identity';
          const killOff = calib?._kill_switch
            && calib._kill_switch.setscore_calibration_active === false;
          const isRejected = c.active === false
            || c.validation_status === 'rejected';
          const isNoSnap = method === 'identity' || c.status === 'no_snapshot';
          let pillColor = 'zinc';
          let pillLabel = 'IDENTITY';
          if (isNoSnap) {
            pillColor = 'zinc';
            pillLabel = 'IDENTITY';
          } else if (isRejected) {
            pillColor = 'rose';
            pillLabel = 'REJECTED';
          } else if (killOff) {
            pillColor = 'amber';
            pillLabel = 'MUTED (KS OFF)';
          } else if (c.active === true) {
            pillColor = 'emerald';
            pillLabel = method.toUpperCase();
          } else {
            pillColor = 'zinc';
            pillLabel = method.toUpperCase();
          }
          const brier = c.brier_score
            ?? c.metrics_after?.brier
            ?? c.metrics?.brier;
          const logloss = c.logloss
            ?? c.metrics_after?.logloss
            ?? c.metrics?.logloss;
          const ece = c.metrics_after?.ece;
          const snapId = c.snapshot_id
            || (c.fitted_at ? `fit:${String(c.fitted_at).slice(0, 19)}` : null);
          return (
            <div key={market} className="rounded-sm p-4" style={card}
                 data-testid={`calib-${market}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="text-[10px] uppercase text-zinc-500" style={heading}>
                  {market.toUpperCase()}
                </div>
                <Pill color={pillColor} testid={`calib-${market}-pill`}>
                  {pillLabel}
                </Pill>
              </div>
              <div className="text-[10px] text-zinc-400 space-y-1" style={mono}>
                <div>snapshot: {shortId(snapId)}</div>
                <div>brier: {fmtFloat(brier, 4)}</div>
                <div>logloss: {fmtFloat(logloss, 4)}</div>
                {ece != null && <div>ece: {fmtFloat(ece, 4)}</div>}
                <div>fitted_at: {fmtDate(c.fitted_at)}</div>
                {isRejected && c.rejection_reason && (
                  <div className="text-rose-300 break-words"
                       data-testid={`calib-${market}-reason`}>
                    reason: {c.rejection_reason}
                  </div>
                )}
                {c.active === true && !isRejected && !killOff && (
                  <div className="text-emerald-300"
                       data-testid={`calib-${market}-applied`}>
                    applied to {market.toUpperCase()} only
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Auto Predict Job */}
      <SectionTitle icon={Timer} title="Auto Predict Job" right={
        <div className="flex items-center gap-2">
          <button
            onClick={loadAutoStats}
            disabled={autoLoading}
            data-testid="refresh-auto-btn"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-xs text-zinc-400 hover:text-zinc-200 disabled:opacity-50"
            style={{ border: '1px solid #27272A' }}
          >
            {autoLoading ? <Loader2 size={12} className="animate-spin" />
              : <RefreshCw size={12} />}
          </button>
          <button
            onClick={runAutoPredictNow}
            disabled={autoPredictRunning}
            data-testid="run-auto-predict-btn"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-xs text-violet-300 hover:bg-violet-950/30 disabled:opacity-50"
            style={{ border: '1px solid #5B21B6' }}
          >
            {autoPredictRunning ? <Loader2 size={12} className="animate-spin" />
              : <Zap size={12} />}
            Run now
          </button>
        </div>
      } />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard testid="stat-auto-predictions-24h" label="Predictions · 24h"
          value={fmtNum(autoStats?.predictions_last_24h)}
          sublabel="persisted in tt_setscore_predictions"
          icon={Target} accent="emerald" />
        <StatCard testid="stat-auto-created-24h" label="Auto-created · 24h"
          value={fmtNum(autoStats?.auto_predictions_created_24h)}
          sublabel={`${fmtNum(autoStats?.runs_last_24h)} job runs`}
          icon={Timer} accent="violet" />
        <StatCard testid="stat-auto-duplicates-24h" label="Duplicates skipped"
          value={fmtNum(autoStats?.duplicates_skipped_24h)}
          sublabel={`upcoming seen ${fmtNum(autoStats?.upcoming_matches_seen_24h)}`}
          icon={Layers} />
        <StatCard testid="stat-auto-no-odds-24h" label="No-odds skipped"
          value={fmtNum(autoStats?.no_odds_skipped_24h)}
          sublabel={`errors ${fmtNum(autoStats?.errors_24h)}`}
          icon={AlertTriangle}
          accent={autoStats?.errors_24h > 0 ? 'rose' : 'amber'} />
        <StatCard testid="stat-auto-with-odds" label="Predictions with odds"
          value={fmtNum(autoStats?.predictions_with_odds_total)}
          sublabel="lifetime count" icon={Database} accent="sky" />
        <StatCard testid="stat-auto-pending-settlement"
          label="Pending CLV settle"
          value={fmtNum(autoStats?.predictions_pending_settlement)}
          sublabel="waiting for closing odds" icon={Timer} accent="amber" />
        <div className="rounded-sm p-4 lg:col-span-2" style={card}
             data-testid="last-auto-run">
          <div className="text-[10px] uppercase text-zinc-500 mb-2" style={heading}>
            Last scheduled run
          </div>
          {autoStats?.last_run ? (
            <div className="space-y-1 text-xs" style={mono}>
              <div className="flex justify-between">
                <span className="text-zinc-500">completed_at</span>
                <span className="text-zinc-300">
                  {fmtDate(autoStats.last_run.completed_at)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-zinc-500">upcoming seen / created / dup / no_odds / err</span>
                <span className="text-zinc-200">
                  {autoStats.last_run.upcoming_matches_seen}/
                  <span className="text-emerald-300">
                    {autoStats.last_run.auto_predictions_created}
                  </span>/
                  {autoStats.last_run.skipped_duplicates}/
                  {autoStats.last_run.skipped_no_odds}/
                  <span className={autoStats.last_run.errors > 0
                    ? 'text-rose-300' : 'text-zinc-400'}>
                    {autoStats.last_run.errors}
                  </span>
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-zinc-500">elapsed</span>
                <span className="text-zinc-300">
                  {fmtFloat(autoStats.last_run.elapsed_sec, 2)}s
                </span>
              </div>
            </div>
          ) : (
            <div className="text-[11px] text-zinc-500" style={mono}>
              Nessun run ancora — lo scheduler parte automaticamente ogni 10 min.
            </div>
          )}
        </div>
      </div>

      {/* CLV Tracker */}
      <SectionTitle icon={TrendingUp} title="CLV Tracker (read-only)" right={
        <div className="flex items-center gap-2">
          <button
            onClick={loadClvSummary}
            disabled={clvLoading}
            data-testid="refresh-clv-btn"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-xs text-zinc-400 hover:text-zinc-200 disabled:opacity-50"
            style={{ border: '1px solid #27272A' }}
          >
            {clvLoading ? <Loader2 size={12} className="animate-spin" />
              : <RefreshCw size={12} />}
          </button>
          <button
            onClick={runClvSettleNow}
            disabled={clvSettleRunning}
            data-testid="run-clv-settle-btn"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-xs text-sky-300 hover:bg-sky-950/30 disabled:opacity-50"
            style={{ border: '1px solid #0369A1' }}
          >
            {clvSettleRunning ? <Loader2 size={12} className="animate-spin" />
              : <PlayCircle size={12} />}
            Settle now
          </button>
        </div>
      } />
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <StatCard testid="stat-clv-pending" label="CLV Pending"
          value={fmtNum(clvSummary?.pending)}
          sublabel="waiting match start + snapshot" icon={Timer} accent="amber" />
        <StatCard testid="stat-clv-available" label="CLV Available"
          value={fmtNum(clvSummary?.available)}
          sublabel="closing odds captured" icon={ShieldCheck} accent="emerald" />
        <StatCard testid="stat-clv-noclosing" label="No closing odds"
          value={fmtNum(clvSummary?.no_closing_odds)}
          sublabel="marked stale > 2h" icon={AlertTriangle}
          accent={(clvSummary?.no_closing_odds || 0) > 10 ? 'rose' : 'zinc'} />
        <StatCard testid="stat-clv-mean" label="Mean CLV%"
          value={clvSummary?.mean_clv_pct != null
            ? `${fmtDelta(clvSummary.mean_clv_pct, 2)}%` : '—'}
          sublabel={`n=${fmtNum(clvSummary?.available)}`}
          icon={Sigma}
          accent={(clvSummary?.mean_clv_pct || 0) > 0 ? 'emerald' : 'rose'} />
        <StatCard testid="stat-clv-positive" label="Positive CLV %"
          value={clvSummary?.positive_clv_pct != null
            ? `${Number(clvSummary.positive_clv_pct).toFixed(1)}%` : '—'}
          sublabel="share of predictions" icon={TrendingUp}
          accent={(clvSummary?.positive_clv_pct || 0) >= 50 ? 'emerald' : 'amber'} />
      </div>
      {clvSummary?.closing_quality_breakdown && (
        <div className="rounded-sm p-3 border border-zinc-900"
             style={{ background: '#0A0A0A' }}
             data-testid="clv-closing-quality">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10px] uppercase text-zinc-500 tracking-wider"
                 style={heading}>
              closing_quality breakdown
            </div>
            <div className="text-[10px] text-zinc-500" style={mono}>
              window: <span className="text-zinc-300">
                {clvSummary.closing_window_min ?? 120} min pre-kickoff
              </span>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 text-[10px]" style={mono}>
            {[
              ['EXACT_CLOSE', 'emerald'],
              ['BEST_AVAILABLE_PREMATCH', 'zinc'],
              ['TOO_OLD', 'rose'],
              ['UNAVAILABLE', 'rose'],
            ].map(([q, color]) => {
              const n = clvSummary.closing_quality_breakdown[q] ?? 0;
              const colorClass = color === 'emerald'
                ? 'text-emerald-300'
                : color === 'rose' ? 'text-rose-300' : 'text-zinc-200';
              return (
                <div key={q}
                     data-testid={`clv-closing-quality-${q}`}
                     className="px-2 py-1 rounded-sm border border-zinc-800">
                  <span className={colorClass}>{q}</span>{' '}
                  <span className="text-zinc-400">{n}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
      <div className="rounded-sm p-3" style={card} data-testid="clv-by-tournament">
        <div className="text-[10px] uppercase text-zinc-500 mb-2" style={heading}>
          CLV by Tournament (n ≥ 3)
        </div>
        {clvSummary?.by_tournament?.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-xs" style={mono}>
              <thead>
                <tr className="text-[10px] text-zinc-500 uppercase">
                  <th className="py-1 px-2 text-left">tournament</th>
                  <th className="py-1 px-2 text-right">n</th>
                  <th className="py-1 px-2 text-right">mean CLV%</th>
                  <th className="py-1 px-2 text-right">positive %</th>
                </tr>
              </thead>
              <tbody>
                {clvSummary.by_tournament.map((t) => (
                  <tr key={t.tournament_name} className="border-t border-zinc-900">
                    <td className="py-1 px-2 text-zinc-200">
                      {t.tournament_name || '—'}
                    </td>
                    <td className="py-1 px-2 text-right text-zinc-300">
                      {fmtNum(t.n)}
                    </td>
                    <td className={`py-1 px-2 text-right ${
                      t.avg_clv_pct > 0 ? 'text-emerald-300' : 'text-rose-300'
                    }`}>
                      {fmtDelta(t.avg_clv_pct, 2)}%
                    </td>
                    <td className="py-1 px-2 text-right text-zinc-300">
                      {Number(t.positive_pct).toFixed(1)}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-[11px] text-zinc-500 py-2" style={mono}>
            Nessun tournament con abbastanza campioni CLV ancora.
          </div>
        )}
      </div>


      {/* CLV No-Closing Audit (read-only) */}
      <SectionTitle icon={ShieldCheck} title="CLV No-Closing Audit (read-only)"
        right={
        <div className="flex items-center gap-2" style={mono}>
          <span className="text-[10px] text-zinc-500 uppercase tracking-wider">
            sample
          </span>
          <select
            data-testid="clv-audit-sample-select"
            value={clvAuditSample}
            onChange={(e) => setClvAuditSample(parseInt(e.target.value, 10))}
            disabled={clvAuditRunning}
            className="bg-zinc-950 border border-zinc-800 text-zinc-200 text-xs px-2 py-1 rounded-sm"
          >
            {[50, 100, 200, 500].map(n => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
          <button
            onClick={runClvAudit}
            disabled={clvAuditRunning}
            data-testid="clv-audit-run-btn"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-xs text-zinc-300 hover:bg-zinc-900 disabled:opacity-50"
            style={{ border: '1px solid #3F3F46' }}
          >
            {clvAuditRunning ? <Loader2 size={12} className="animate-spin" />
              : <PlayCircle size={12} />}
            Run audit
          </button>
          <button
            onClick={() => runClvResweep(true)}
            disabled={clvResweepRunning}
            data-testid="clv-resweep-dryrun-btn"
            title="Dry run — does NOT mutate. Verifies the endpoint is reachable and returns the current state."
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-xs text-zinc-300 hover:bg-zinc-900 disabled:opacity-50"
            style={{ border: '1px solid #3F3F46' }}
          >
            {clvResweepRunning ? <Loader2 size={12} className="animate-spin" />
              : <Activity size={12} />}
            Dry run
          </button>
          <button
            onClick={() => runClvResweep(false)}
            disabled={clvResweepRunning}
            data-testid="clv-resweep-btn"
            title="Reset NO_CLOSING_ODDS rows to PENDING and re-run the CLV settler with the current window (120 min)"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-xs text-amber-300 hover:bg-amber-950/30 disabled:opacity-50"
            style={{ border: '1px solid #92400E' }}
          >
            {clvResweepRunning ? <Loader2 size={12} className="animate-spin" />
              : <RefreshCw size={12} />}
            Re-sweep NO_CLOSING
          </button>
        </div>
      } />
      <div className="rounded-sm border border-zinc-900 p-4"
           style={{ background: '#0A0A0A' }}
           data-testid="clv-audit-section">
        {/* Inline status pill so the operator gets feedback even if scrolled. */}
        {clvResweepStatus !== 'idle' && (
          <div className="mb-3 flex items-center gap-2 text-[11px]"
               style={mono} data-testid="clv-resweep-status">
            <span className="text-zinc-500 uppercase tracking-wider">
              re-sweep:
            </span>
            <Pill testid={`clv-resweep-status-${clvResweepStatus}`}
                  color={clvResweepStatus === 'running' ? 'sky'
                    : clvResweepStatus === 'success' ? 'emerald'
                    : 'rose'}>
              {clvResweepStatus === 'running' ? 'RESWEEP_RUNNING'
                : clvResweepStatus === 'success' ? 'RESWEEP_SUCCESS'
                : 'RESWEEP_FAILED'}
            </Pill>
            {clvResweepResult?.status === 'dry_run' && (
              <span className="text-zinc-500">dry_run</span>
            )}
          </div>
        )}

        {/* Failure panel — never silent: shows status code + body/detail. */}
        {clvResweepStatus === 'failed' && clvResweepError && (
          <div className="mb-3 rounded-sm border border-rose-900/40 p-3 text-xs space-y-1"
               style={{ ...mono, background: '#3F0A0A' }}
               data-testid="clv-resweep-error-panel">
            <div className="flex items-center gap-2 text-rose-200">
              <AlertTriangle size={12} />
              <span className="uppercase tracking-wider text-[10px]">
                resweep failed
              </span>
              <span className="text-zinc-400">·</span>
              <span className="text-rose-300">
                status: {String(clvResweepError.status)}
              </span>
              {clvResweepError.is_timeout && (
                <span className="text-amber-200">(timeout 180s)</span>
              )}
              {clvResweepError.is_network && (
                <span className="text-amber-200">(network)</span>
              )}
            </div>
            <div className="text-rose-100/90 break-all">
              {clvResweepError.message}
            </div>
            {clvResweepError.detail && (
              <pre className="text-[10px] text-rose-200/80 whitespace-pre-wrap break-all max-h-40 overflow-y-auto">
                {clvResweepError.detail}
              </pre>
            )}
            <div className="text-[10px] text-rose-300/80 pt-1">
              Tip: run "Dry run" to verify the endpoint is wired without mutating data.
              If it returns 404, backend is not yet deployed. If timeout, the settler
              is taking longer than 180s — re-run.
            </div>
          </div>
        )}

        {!clvAudit && !clvAuditRunning && (
          <div className="text-[11px] text-zinc-500 py-2" style={mono}>
            Read-only forensic audit on predictions with{' '}
            <span className="text-zinc-300">clv_status = NO_CLOSING_ODDS</span>.
            Replays the settler lookup window (match_dt - 60 min → match_dt + 15 min)
            and classifies why each row was closed without closing odds.
            Does not write to the DB.
          </div>
        )}

        {clvAuditRunning && (
          <div className="flex items-center gap-2 text-xs text-zinc-500 py-4" style={mono}>
            <Loader2 size={14} className="animate-spin" />
            Running audit on sample={clvAuditSample}…
          </div>
        )}

        {clvAudit && !clvAuditRunning && (
          <div className="space-y-4" style={mono}>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs"
                 data-testid="clv-audit-headline">
              <div className="rounded-sm p-2 border border-zinc-900">
                <div className="text-[10px] text-zinc-500 uppercase tracking-wider">total NO_CLOSING</div>
                <div className="text-lg font-semibold text-rose-300"
                     data-testid="clv-audit-total">
                  {clvAudit.total_no_closing}
                </div>
              </div>
              <div className="rounded-sm p-2 border border-zinc-900">
                <div className="text-[10px] text-zinc-500 uppercase tracking-wider">sampled</div>
                <div className="text-lg font-semibold text-zinc-200">
                  {clvAudit.sampled}
                </div>
              </div>
              <div className="rounded-sm p-2 border border-zinc-900">
                <div className="text-[10px] text-zinc-500 uppercase tracking-wider">window pre-kickoff</div>
                <div className="text-lg font-semibold text-zinc-200">
                  {clvAudit.window_used?.minutes_before_kickoff} min
                </div>
              </div>
              <div className="rounded-sm p-2 border border-zinc-900">
                <div className="text-[10px] text-zinc-500 uppercase tracking-wider">window post-kickoff</div>
                <div className="text-lg font-semibold text-zinc-200">
                  {clvAudit.window_used?.minutes_after_kickoff} min
                </div>
              </div>
            </div>

            <div data-testid="clv-audit-breakdown">
              <div className="text-[10px] text-zinc-500 uppercase tracking-wider mb-2">
                reason breakdown
              </div>
              <div className="space-y-1">
                {Object.entries(clvAudit.reason_breakdown || {})
                  .sort((a, b) => b[1] - a[1])
                  .map(([reason, count]) => {
                  const pct = clvAudit.sampled > 0
                    ? (count / clvAudit.sampled) * 100 : 0;
                  const isDominant =
                    reason === clvAudit.fix_recommendation?.dominant_reason
                    && count > 0;
                  return (
                    <div key={reason}
                         data-testid={`clv-audit-reason-${reason}`}
                         className="flex items-center gap-2 text-xs">
                      <div className="w-56 truncate" title={reason}>
                        <span className={isDominant
                          ? 'text-amber-300 font-semibold'
                          : count > 0 ? 'text-zinc-300' : 'text-zinc-600'}>
                          {reason}
                        </span>
                      </div>
                      <div className="flex-1 h-2 bg-zinc-900 rounded-sm overflow-hidden">
                        <div
                          className={isDominant
                            ? 'h-full bg-amber-500/70'
                            : 'h-full bg-zinc-600/60'}
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                      <div className="w-12 text-right text-zinc-400">
                        {count}
                      </div>
                      <div className="w-12 text-right text-zinc-600">
                        {pct.toFixed(1)}%
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {clvAudit.fix_recommendation && (
              <div className="rounded-sm p-3 text-xs border border-amber-900/40"
                   style={{ background: '#1A1408' }}
                   data-testid="clv-audit-fix-recommendation">
                <div className="text-[10px] text-amber-300 uppercase tracking-wider mb-1">
                  fix recommendation
                </div>
                <div className="text-amber-100 font-semibold mb-1">
                  Dominant reason:{' '}
                  <span className="text-amber-300">
                    {clvAudit.fix_recommendation.dominant_reason}
                  </span>{' '}
                  ({clvAudit.fix_recommendation.dominant_count} rows)
                </div>
                <div className="text-amber-200/90">
                  {clvAudit.fix_recommendation.advice}
                </div>
              </div>
            )}

            <details data-testid="clv-audit-examples-details" className="text-xs">
              <summary className="cursor-pointer text-zinc-400 hover:text-zinc-200 select-none">
                Examples ({(clvAudit.examples || []).length}) — click to expand
              </summary>
              <div className="mt-2 max-h-96 overflow-y-auto rounded-sm border border-zinc-900">
                <table className="w-full text-[10px]">
                  <thead className="text-zinc-500 uppercase tracking-wider">
                    <tr className="border-b border-zinc-900">
                      <th className="py-1.5 px-2 text-left">match</th>
                      <th className="py-1.5 px-2 text-left">tournament</th>
                      <th className="py-1.5 px-2 text-left">match_datetime</th>
                      <th className="py-1.5 px-2 text-left">reason</th>
                      <th className="py-1.5 px-2 text-right">total snaps</th>
                      <th className="py-1.5 px-2 text-right">in window</th>
                      <th className="py-1.5 px-2 text-left">fix_hint</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(clvAudit.examples || []).map((ex, i) => (
                      <tr key={i}
                          data-testid={`clv-audit-example-${i}`}
                          className="border-b border-zinc-900 hover:bg-zinc-900/30">
                        <td className="py-1.5 px-2 text-zinc-200">
                          {ex.player1_name} vs {ex.player2_name}
                        </td>
                        <td className="py-1.5 px-2 text-zinc-500">
                          {ex.tournament_name || '—'}
                        </td>
                        <td className="py-1.5 px-2 text-zinc-500">
                          {ex.match_datetime}
                        </td>
                        <td className="py-1.5 px-2 text-amber-300">
                          {ex.classified_reason}
                        </td>
                        <td className="py-1.5 px-2 text-right text-zinc-400">
                          {ex.n_total_snapshots ?? 0}
                        </td>
                        <td className="py-1.5 px-2 text-right text-zinc-400">
                          {ex.n_in_window ?? 0}
                        </td>
                        <td className="py-1.5 px-2 text-zinc-500">
                          {ex.fix_hint
                            || (ex.last_snapshot_minutes_before_kickoff
                              ? `last snap ${ex.last_snapshot_minutes_before_kickoff} min pre-kickoff`
                              : (ex.first_snapshot_minutes_after_kickoff
                                ? `first snap ${ex.first_snapshot_minutes_after_kickoff} min post-kickoff`
                                : '—'))}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>
          </div>
        )}

        {/* Re-sweep result panel */}
        {clvResweepResult && (
          <div className="mt-4 rounded-sm border border-emerald-900/40 p-3 text-xs space-y-3"
               style={{ ...mono, background: '#062B1A' }}
               data-testid="clv-resweep-result">
            <div className="flex items-center gap-2">
              <span className="text-[10px] text-emerald-300 uppercase tracking-wider">
                re-sweep result
              </span>
              <span className="text-[10px] text-zinc-500">
                · window: {clvResweepResult.closing_window_min} min ·
                rows reset: {clvResweepResult.rows_reset_to_pending} ·
                scanned: {clvResweepResult.settle_audit?.scanned} ·
                elapsed: {clvResweepResult.settle_audit?.elapsed_sec}s
              </span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <div className="rounded-sm p-2 border border-zinc-900"
                   style={{ background: '#0A0A0A' }}>
                <div className="text-[10px] text-zinc-500 uppercase tracking-wider">
                  NO_CLOSING_ODDS
                </div>
                <div className="text-sm text-zinc-200">
                  <span className="text-rose-400">
                    {clvResweepResult.before?.no_closing_odds}
                  </span>
                  {' → '}
                  <span className="text-emerald-300 font-semibold"
                        data-testid="clv-resweep-after-no-closing">
                    {clvResweepResult.after?.no_closing_odds}
                  </span>
                </div>
              </div>
              <div className="rounded-sm p-2 border border-zinc-900"
                   style={{ background: '#0A0A0A' }}>
                <div className="text-[10px] text-zinc-500 uppercase tracking-wider">
                  AVAILABLE
                </div>
                <div className="text-sm text-zinc-200">
                  <span className="text-zinc-500">
                    {clvResweepResult.before?.available}
                  </span>
                  {' → '}
                  <span className="text-emerald-300 font-semibold"
                        data-testid="clv-resweep-after-available">
                    {clvResweepResult.after?.available}
                  </span>
                </div>
              </div>
              <div className="rounded-sm p-2 border border-zinc-900"
                   style={{ background: '#0A0A0A' }}>
                <div className="text-[10px] text-zinc-500 uppercase tracking-wider">
                  PENDING
                </div>
                <div className="text-sm text-zinc-300">
                  {clvResweepResult.after?.pending}
                </div>
              </div>
              <div className="rounded-sm p-2 border border-zinc-900"
                   style={{ background: '#0A0A0A' }}>
                <div className="text-[10px] text-zinc-500 uppercase tracking-wider">
                  mean CLV%
                </div>
                <div className={`text-sm font-semibold ${
                  Number(clvResweepResult.after?.mean_clv_pct) > 0
                    ? 'text-emerald-300' : 'text-rose-300'}`}>
                  {clvResweepResult.after?.mean_clv_pct == null
                    ? '—'
                    : `${Number(clvResweepResult.after.mean_clv_pct) > 0 ? '+' : ''}${Number(clvResweepResult.after.mean_clv_pct).toFixed(3)}%`}
                </div>
                <div className="text-[10px] text-zinc-500">
                  positive: {clvResweepResult.after?.positive_clv_pct ?? '—'}%
                </div>
              </div>
            </div>
            {clvResweepResult.after?.closing_quality_breakdown && (
              <div data-testid="clv-resweep-quality">
                <div className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1">
                  closing_quality breakdown
                </div>
                <div className="flex flex-wrap gap-2 text-[10px]">
                  {Object.entries(
                    clvResweepResult.after.closing_quality_breakdown
                  ).map(([q, n]) => (
                    <div key={q}
                         data-testid={`clv-resweep-quality-${q}`}
                         className="px-2 py-1 rounded-sm border border-zinc-800"
                         style={{ background: '#0A0A0A' }}>
                      <span className={
                        q === 'EXACT_CLOSE' ? 'text-emerald-300'
                          : q === 'BEST_AVAILABLE_PREMATCH' ? 'text-zinc-200'
                          : 'text-rose-300'
                      }>
                        {q}
                      </span>{' '}
                      <span className="text-zinc-400">{n}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <details data-testid="clv-resweep-raw-details" className="text-[10px]">
              <summary className="cursor-pointer text-zinc-500 hover:text-zinc-300 select-none">
                raw response — click to expand
              </summary>
              <pre className="mt-2 p-2 rounded-sm border border-zinc-900 bg-black text-zinc-300 max-h-72 overflow-auto"
                   data-testid="clv-resweep-raw-json">
                {JSON.stringify(clvResweepResult, null, 2)}
              </pre>
            </details>
          </div>
        )}
      </div>

      {/* Paper Telegram Signals — Phase 3 Lite */}
      <SectionTitle icon={Send} title="Paper Telegram Signals (Phase 3 Lite)"
        right={
        <div className="flex items-center gap-2">
          <Pill color={paperReport?.telegram_chat_configured ? 'emerald' : 'amber'}
                testid="paper-chat-pill">
            {paperReport?.telegram_chat_configured
              ? 'TELEGRAM CHAT OK' : 'LOG-ONLY (no chat_id)'}
          </Pill>
          <button
            onClick={loadPaperSignals}
            disabled={paperLoading}
            data-testid="refresh-paper-btn"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-xs text-zinc-400 hover:text-zinc-200 disabled:opacity-50"
            style={{ border: '1px solid #27272A' }}
          >
            {paperLoading ? <Loader2 size={12} className="animate-spin" />
              : <RefreshCw size={12} />}
          </button>
          <button
            onClick={runPaperSettleNow}
            disabled={paperSettleRunning}
            data-testid="run-paper-settle-btn"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-xs text-sky-300 hover:bg-sky-950/30 disabled:opacity-50"
            style={{ border: '1px solid #0369A1' }}
          >
            {paperSettleRunning ? <Loader2 size={12} className="animate-spin" />
              : <ShieldCheck size={12} />}
            Settle now
          </button>
          <button
            onClick={runPaperSignalsNow}
            disabled={paperRunning}
            data-testid="run-paper-now-btn"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-xs text-violet-300 hover:bg-violet-950/30 disabled:opacity-50"
            style={{ border: '1px solid #5B21B6' }}
          >
            {paperRunning ? <Loader2 size={12} className="animate-spin" />
              : <Send size={12} />}
            Run paper now
          </button>
        </div>
      } />
      {paperDebug && (
        <div className="rounded-sm border border-amber-900/40 p-3 mb-3 text-xs"
             style={{ ...mono, background: '#1A1408' }}
             data-testid="paper-debug-panel">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-[10px] uppercase tracking-wider text-amber-300">
              paper-signal debug
            </span>
            <span className="text-zinc-500">·</span>
            <span className="text-zinc-300">{paperDebug.signal_id}</span>
            <button
              onClick={() => setPaperDebug(null)}
              data-testid="paper-debug-close"
              className="ml-auto text-zinc-500 hover:text-zinc-200 text-[11px]"
            >
              ✕ close
            </button>
          </div>
          {paperDebug.loading && (
            <div className="text-zinc-500 flex items-center gap-2">
              <Loader2 size={12} className="animate-spin" /> Loading…
            </div>
          )}
          {paperDebug.error && (
            <div className="space-y-1.5">
              <div className="text-rose-300 break-all"
                   data-testid="paper-debug-error-msg">
                {paperDebug.error}
              </div>
              {paperDebug.error.includes('404') && (
                <div className="text-zinc-400 text-[11px] leading-relaxed">
                  Possibili cause: la lista paper-signal mostrava un record
                  rimasto in cache nel browser ma rimosso dal DB (purge
                  cold-start, regen del job, refresh stale).
                  <span className="block mt-1">
                    → Premi <span className="text-violet-300">Refresh</span> nel
                    Paper Telegram Signals header per ricaricare la lista
                    aggiornata.
                  </span>
                </div>
              )}
            </div>
          )}
          {paperDebug.signal_id_not_found && !paperDebug.loading && (
            <div className="text-amber-200 text-[11px] leading-relaxed pt-1"
                 data-testid="paper-debug-not-found">
              Segnale non trovato in MongoDB.
              <span className="block mt-1 text-zinc-400">
                Il record potrebbe essere stato cancellato da una purge o
                non essere mai stato persistito (es. fallimento Telegram +
                error in insert). La UI sta mostrando un risultato cached
                lato client. Premi <span className="text-violet-300">
                Refresh</span> nel Paper Telegram Signals header per
                aggiornare.
              </span>
            </div>
          )}
          {!paperDebug.loading && !paperDebug.error && !paperDebug.signal_id_not_found && (
            <div className="space-y-1">
              <div>
                <span className="text-zinc-500">verdict:</span>{' '}
                <span className={
                  paperDebug.verdict?.startsWith('READY_TO_SETTLE')
                    ? 'text-emerald-300 font-semibold'
                    : paperDebug.verdict?.startsWith('ALREADY_SETTLED')
                      ? 'text-zinc-300'
                      : 'text-amber-200 font-semibold'
                }>
                  {paperDebug.verdict}
                </span>
              </div>
              <div>
                <span className="text-zinc-500">selection:</span>{' '}
                {paperDebug.selection_name} ({paperDebug.selection_side})
              </div>
              <div>
                <span className="text-zinc-500">match.status:</span>{' '}
                <span className="text-zinc-200">
                  {paperDebug.match?.status ?? '—'}
                </span>
                {' · '}
                <span className="text-zinc-500">winner_name:</span>{' '}
                <span className="text-zinc-200">
                  {paperDebug.match?.winner_name || '∅'}
                </span>
              </div>
              <div>
                <span className="text-zinc-500">players:</span>{' '}
                {paperDebug.match?.player1_name} vs {paperDebug.match?.player2_name}
              </div>
              <div>
                <span className="text-zinc-500">match_datetime:</span>{' '}
                {paperDebug.match?.match_datetime}
              </div>
              {paperDebug.verdict === 'READY_TO_SETTLE_(re-run settler)' && (
                <div className="pt-1 text-emerald-300">
                  → click <span className="font-semibold">"Settle now"</span> above to settle this signal.
                </div>
              )}
              {paperDebug.verdict?.startsWith('STATUS_') && (
                <div className="pt-1 text-amber-200">
                  → match still flagged as `{paperDebug.match?.status}`,
                  fallback `LATE_STATUS_TOLERANCE_MIN=90` not reached yet
                  OR settler has not been re-run. Once kickoff is older than
                  90 min, click "Settle now" to recover via stuck-status
                  fallback.
                </div>
              )}
              {paperDebug.verdict === 'WINNER_NAME_MISMATCH' && (
                <div className="pt-1 text-rose-300">
                  → winner_name does not match either player. Manual review needed
                  (translit / typo / fixture mismatch).
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <StatCard testid="stat-paper-today" label="Signals today"
          value={fmtNum(paperReport?.signals_sent_today)}
          sublabel={`max ${5} / day`}
          icon={Send} accent="violet" />
        <StatCard testid="stat-paper-7d" label="Signals · 7d"
          value={fmtNum(paperReport?.signals_sent_7d)}
          sublabel={`settled ${fmtNum(paperReport?.settled)}`}
          icon={Timer} accent="sky" />
        <StatCard testid="stat-paper-wr" label="Win rate"
          value={paperReport?.wr_pct != null
            ? `${Number(paperReport.wr_pct).toFixed(1)}%` : '—'}
          sublabel={`avg odds ${
            paperReport?.avg_odds != null
              ? Number(paperReport.avg_odds).toFixed(2) : '—'}`}
          icon={Trophy}
          accent={(paperReport?.wr_pct || 0) >= 50 ? 'emerald' : 'amber'} />
        <StatCard testid="stat-paper-roi-flat" label="ROI flat"
          value={paperReport?.roi_flat_pct != null
            ? `${fmtDelta(paperReport.roi_flat_pct / 100, 4) * 100 || ''}` : '—'}
          sublabel={`PnL ${paperReport?.pnl_flat_1u != null
            ? fmtDelta(paperReport.pnl_flat_1u, 3) : '—'}u`}
          icon={TrendingUp}
          accent={(paperReport?.roi_flat_pct || 0) > 0 ? 'emerald' : 'rose'} />
        <StatCard testid="stat-paper-clv" label="CLV available"
          value={fmtNum(paperReport?.clv_available)}
          sublabel={`mean CLV ${paperReport?.mean_clv_pct != null
            ? `${fmtDelta(paperReport.mean_clv_pct, 2)}%` : '—'}`}
          icon={Sigma}
          accent={(paperReport?.mean_clv_pct || 0) > 0 ? 'emerald' : 'amber'} />
      </div>

      <div className="rounded-sm overflow-hidden" style={card}>
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="paper-signals-table">
            <thead>
              <tr className="text-[10px] text-zinc-500 uppercase tracking-wider">
                <th className="py-2 px-3 text-left" style={heading}>sent</th>
                <th className="py-2 px-3 text-left" style={heading}>match</th>
                <th className="py-2 px-3 text-left" style={heading}>pick</th>
                <th className="py-2 px-3 text-right" style={heading}>odds</th>
                <th className="py-2 px-3 text-right" style={heading}>p cons</th>
                <th className="py-2 px-3 text-right" style={heading}>EV↓</th>
                <th className="py-2 px-3 text-right" style={heading}>Paper stake</th>
                <th className="py-2 px-3 text-right" style={heading}>tg</th>
                <th className="py-2 px-3 text-right" style={heading}>result</th>
                <th className="py-2 px-3 text-right" style={heading}>PnL flat</th>
                <th className="py-2 px-3 text-right" style={heading}>CLV</th>
                <th className="py-2 px-3 text-center" style={heading}>debug</th>
              </tr>
            </thead>
            <tbody>
              {paperRecent?.items?.map((r) => (
                <tr key={r.signal_id}
                    data-testid={`paper-row-${r.signal_id}`}
                    className="border-t border-zinc-900 hover:bg-zinc-900/50">
                  <td className="py-1.5 px-3 text-zinc-500" style={mono}>
                    {fmtDate(r.created_at)}
                  </td>
                  <td className="py-1.5 px-3 text-zinc-200" style={mono}>
                    {r.player1_name} <span className="text-zinc-600">vs</span>{' '}
                    {r.player2_name}
                    <div className="text-[9px] text-zinc-600">
                      {r.tournament_name || '—'}
                    </div>
                  </td>
                  <td className="py-1.5 px-3 text-zinc-300" style={mono}>
                    {r.selection_name}
                    <div className="text-[9px] text-zinc-600">
                      {r.market}
                    </div>
                  </td>
                  <td className="py-1.5 px-3 text-right text-zinc-200" style={mono}>
                    {Number(r.odds).toFixed(2)}
                  </td>
                  <td className="py-1.5 px-3 text-right text-zinc-300" style={mono}>
                    {fmtPct(r.p_conservative)}
                  </td>
                  <td className={`py-1.5 px-3 text-right ${
                    Number(r.ev_lower) > 0 ? 'text-emerald-300' : 'text-rose-300'
                  }`} style={mono}>
                    {fmtDelta(r.ev_lower, 3)}
                  </td>
                  <td className="py-1.5 px-3 text-right text-amber-300" style={mono}>
                    {(Number(r.paper_stake_pct) * 100).toFixed(3)}%
                  </td>
                  <td className="py-1.5 px-3 text-right" style={mono}>
                    <Pill color={r.telegram_sent ? 'emerald' : 'amber'}>
                      {r.telegram_sent ? 'SENT' : (r.telegram_status || 'LOG')}
                    </Pill>
                  </td>
                  <td className="py-1.5 px-3 text-right" style={mono}>
                    {r.result
                      ? (
                        <Pill color={r.result === 'WIN' ? 'emerald' : 'rose'}>
                          {r.result}
                        </Pill>
                      )
                      : <Pill color="zinc">pending</Pill>}
                  </td>
                  <td className={`py-1.5 px-3 text-right ${
                    (r.pnl_flat_1u || 0) > 0 ? 'text-emerald-400'
                      : (r.pnl_flat_1u || 0) < 0 ? 'text-rose-400' : 'text-zinc-500'
                  }`} style={mono}>
                    {r.pnl_flat_1u != null
                      ? fmtDelta(r.pnl_flat_1u, 2) : '—'}
                  </td>
                  <td className={`py-1.5 px-3 text-right ${
                    (r.clv_pct_later || 0) > 0 ? 'text-emerald-400'
                      : (r.clv_pct_later || 0) < 0 ? 'text-rose-400' : 'text-zinc-500'
                  }`} style={mono}>
                    {r.clv_pct_later != null
                      ? `${fmtDelta(r.clv_pct_later, 2)}%` : '—'}
                  </td>
                  <td className="py-1.5 px-3 text-center">
                    {!r.result && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          debugPaperSignal(r.signal_id);
                        }}
                        data-testid={`paper-debug-${r.signal_id}`}
                        title="Inspect why this signal is still pending"
                        className="text-zinc-500 hover:text-amber-300 text-[11px]"
                      >
                        🔍
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {(!paperRecent?.items || paperRecent.items.length === 0) && (
                <tr><td colSpan={12}
                        className="py-6 text-center text-zinc-500 text-xs"
                        style={mono}>
                  Nessun paper signal ancora. Lo scheduler gira ogni 30 min e
                  manda solo segnali che superano tutti i gate.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Match Predict */}
      <SectionTitle icon={Brain} title="Match Prediction" right={
        <div className="flex items-center gap-2">
          <select
            value={nSim}
            onChange={(e) => setNSim(Number(e.target.value))}
            data-testid="n-sim-select"
            className="px-2 py-1 rounded-sm bg-zinc-900 border border-zinc-800 text-zinc-300 text-xs"
            style={mono}
          >
            <option value={2000}>2k sim</option>
            <option value={5000}>5k sim</option>
            <option value={10000}>10k sim</option>
            <option value={20000}>20k sim</option>
          </select>
          <label className="flex items-center gap-1.5 text-[10px] text-zinc-400"
                 style={mono}>
            <input
              type="checkbox"
              data-testid="force-refresh-check"
              checked={forceRefresh}
              onChange={(e) => setForceRefresh(e.target.checked)}
            />
            force refresh
          </label>
        </div>
      } />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <div className="rounded-sm p-3 lg:col-span-1" style={card}>
          <div className="flex items-center gap-2 mb-2 px-1">
            <Search size={12} className="text-zinc-500" />
            <input
              type="text"
              placeholder="Cerca player / torneo / match id"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              data-testid="match-search-input"
              className="w-full bg-transparent text-xs text-zinc-200 placeholder-zinc-600 outline-none"
              style={mono}
            />
            <button
              onClick={loadUpcoming}
              disabled={upLoading}
              data-testid="refresh-matches-btn"
              className="text-zinc-500 hover:text-zinc-300"
              title="Reload upcoming matches"
            >
              {upLoading ? <Loader2 size={12} className="animate-spin" />
                : <RefreshCw size={12} />}
            </button>
          </div>

          {/* Smart Filters */}
          <div
            data-testid="smart-filters"
            className="mb-2 px-1 py-1.5 rounded-sm bg-zinc-950/50 border border-zinc-900 space-y-1.5"
            style={mono}
          >
            <div className="flex items-center justify-between text-[9px] text-zinc-500 tracking-wider">
              <span>SMART FILTERS</span>
              <span data-testid="smart-filters-counter">
                {filteredUpcoming.length}/{upcoming.length}
                {summariesLoading && <Loader2 size={9} className="inline animate-spin ml-1" />}
              </span>
            </div>
            <label className="flex items-center gap-1.5 text-[10px] text-zinc-300 cursor-pointer">
              <input
                type="checkbox"
                data-testid="filter-strong-only"
                checked={filterStrongOnly}
                onChange={(e) => setFilterStrongOnly(e.target.checked)}
              />
              <span>Solo match con STRONG guard attivo</span>
            </label>
            <div className="grid grid-cols-2 gap-1.5">
              <select
                data-testid="filter-profile"
                value={filterProfile}
                onChange={(e) => setFilterProfile(e.target.value)}
                className="px-1.5 py-1 rounded-sm bg-zinc-900 border border-zinc-800 text-zinc-200 text-[10px]"
              >
                <option value="all">Tutti i profili</option>
                <option value="STOMP_CLEAN">STOMP_CLEAN</option>
                <option value="CONTROLLED_FAST">CONTROLLED_FAST</option>
                <option value="CONTROLLED_LONG">CONTROLLED_LONG</option>
                <option value="BALANCED_LONG">BALANCED_LONG</option>
                <option value="TRUE_CHAOS">TRUE_CHAOS</option>
                <option value="BALANCED">BALANCED</option>
              </select>
              <select
                data-testid="filter-market"
                value={filterMarket}
                onChange={(e) => setFilterMarket(e.target.value)}
                className="px-1.5 py-1 rounded-sm bg-zinc-900 border border-zinc-800 text-zinc-200 text-[10px]"
              >
                <option value="all">Tutti i market</option>
                <option value="mw">MW</option>
                <option value="over">Over</option>
                <option value="under">Under</option>
                <option value="hc_fav">HC Favorite</option>
                <option value="hc_dog">HC Underdog</option>
                <option value="none">Nessuno</option>
              </select>
              <select
                data-testid="filter-ev"
                value={filterEv}
                onChange={(e) => setFilterEv(e.target.value)}
                className="px-1.5 py-1 rounded-sm bg-zinc-900 border border-zinc-800 text-zinc-200 text-[10px]"
              >
                <option value="all">EV: Tutti</option>
                <option value="ev_5">EV ≥ +5%</option>
                <option value="ev_8">EV ≥ +8%</option>
                <option value="ev_10">EV ≥ +10%</option>
                <option value="neg">Solo EV negativo</option>
              </select>
              <label className="flex items-center gap-1.5 text-[10px] text-zinc-300 cursor-pointer px-1.5 py-1 rounded-sm bg-zinc-900 border border-zinc-800">
                <input
                  type="checkbox"
                  data-testid="filter-hide-no-edge"
                  checked={filterHideNoEdge}
                  onChange={(e) => setFilterHideNoEdge(e.target.checked)}
                />
                <span>Nascondi no-edge</span>
              </label>
            </div>
            {(filterStrongOnly || filterProfile !== 'all' || filterMarket !== 'all'
              || filterEv !== 'all' || filterHideNoEdge) && (
              <button
                data-testid="filter-reset-btn"
                onClick={() => {
                  setFilterStrongOnly(false);
                  setFilterProfile('all');
                  setFilterMarket('all');
                  setFilterEv('all');
                  setFilterHideNoEdge(false);
                }}
                className="text-[9px] text-zinc-500 hover:text-zinc-300"
              >
                reset filters
              </button>
            )}
          </div>

          <div className="max-h-96 overflow-y-auto"
               data-testid="upcoming-match-list">
            {filteredUpcoming.map((m) => {
              const s = summaries[m.id];
              return (
              <button
                key={m.id}
                onClick={() => { setSelectedMatch(m); runPredict(m.id); }}
                data-testid={`pick-match-${m.id}`}
                className={`w-full text-left px-2 py-1.5 mb-1 rounded-sm text-xs hover:bg-zinc-900 transition-colors ${
                  selectedMatch?.id === m.id ? 'bg-zinc-900 border border-emerald-800'
                    : 'border border-zinc-900'
                }`}
                style={mono}
              >
                <div className="flex items-center justify-between">
                  <div className="text-zinc-200 truncate">
                    {m.player1_name} <span className="text-zinc-600">vs</span>{' '}
                    {m.player2_name}
                  </div>
                  <Pill color={m.status === 'live' ? 'rose' : 'sky'}>
                    {m.status}
                  </Pill>
                </div>
                <div className="text-[10px] text-zinc-500 truncate">
                  {m.tournament_name || '—'} ·{' '}
                  {m.match_datetime ? new Date(m.match_datetime)
                    .toLocaleString('it-IT') : '—'}
                </div>
                <UpcomingBadgeRow summary={s} matchId={m.id} />
              </button>
              );
            })}
            {filteredUpcoming.length === 0 && (
              <div className="text-xs text-zinc-500 px-2 py-4" style={mono}>
                Nessun match trovato.
              </div>
            )}
          </div>
        </div>

        {/* Prediction detail */}
        <div className="rounded-sm p-4 lg:col-span-2 min-h-[16rem]" style={card}
             data-testid="prediction-detail">
          {!prediction && !predLoading && (
            <div className="flex items-center justify-center h-full text-zinc-500 text-xs"
                 style={mono}>
              Seleziona un match dalla lista per generare la prediction.
            </div>
          )}
          {predLoading && (
            <div className="flex items-center justify-center h-full text-zinc-400 text-xs gap-2">
              <Loader2 size={14} className="animate-spin" />
              <span style={mono}>Simulazione Monte Carlo in corso…</span>
            </div>
          )}
          {prediction && !prediction.error && (
            <div className="space-y-3">
              {prediction.regenerating && (
                <div
                  data-testid="prediction-regenerating-banner"
                  className="rounded-sm p-2.5 text-[11px] flex items-center gap-2"
                  style={{
                    ...mono,
                    color: '#FCD34D',
                    background: '#78350F',
                    border: '1px solid #92400E',
                  }}
                >
                  <Loader2 size={12} className="animate-spin" />
                  <span>
                    {prediction.regeneration_reason
                      === 'cold_predict_in_progress'
                      ? (prediction.hint
                        || 'Predizione in calcolo (cold start). '
                          + 'Riprova tra ~30 secondi.')
                      : `Schema legacy rilevato — rigenerazione in `
                        + `background in corso `
                        + `(${prediction.regeneration_reason
                          || 'cache_stale'}). Ricarica tra ~5 secondi `
                        + `per vedere la versione aggiornata.`}
                  </span>
                </div>
              )}
              {prediction.regeneration_reason
                === 'cold_predict_in_progress' ? (
                <div className="text-xs text-zinc-500 px-2 py-6 text-center"
                     style={mono}>
                  Predizione in fase di calcolo. Lascia aperta la pagina o
                  ri-clicca il match tra ~30 secondi.
                </div>
              ) : (
              <>
              {/* Header */}
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm text-zinc-100 font-semibold" style={heading}>
                    {selectedMatch?.player1_name} vs {selectedMatch?.player2_name}
                  </div>
                  <div className="text-[10px] text-zinc-500" style={mono}>
                    {selectedMatch?.tournament_name || '—'} ·{' '}
                    {fmtDate(selectedMatch?.match_datetime)} ·{' '}
                    n_sim={prediction.n_sim}
                  </div>
                </div>
                <div className="flex items-center gap-1.5">
                  {(() => {
                    const predAt = prediction.predicted_at;
                    const ts = predAt ? Date.parse(predAt) : NaN;
                    if (!Number.isFinite(ts)) return null;
                    const ageMin = Math.max(0, (nowTick - ts) / 60000);
                    const color = ageMin < 5 ? 'emerald' : ageMin < 15 ? 'amber' : 'rose';
                    const label = ageMin < 1
                      ? '<1m ago'
                      : ageMin < 60
                        ? `${Math.round(ageMin)}m ago`
                        : `${(ageMin / 60).toFixed(1)}h ago`;
                    return (
                      <Pill color={color} testid="prediction-age-pill">
                        last refresh · {label}
                      </Pill>
                    );
                  })()}
                  <button
                    onClick={() => refreshNowNow(selectedMatch?.id)}
                    disabled={refreshNowRunning || !selectedMatch?.id}
                    data-testid="refresh-now-btn"
                    title="Force full refresh: BetsAPI → features → predict"
                    className="flex items-center gap-1 px-2 py-0.5 rounded-sm text-[10px]
                      bg-emerald-950/60 border border-emerald-800 text-emerald-300
                      hover:bg-emerald-900/40 disabled:opacity-40 disabled:cursor-not-allowed"
                    style={mono}
                  >
                    {refreshNowRunning
                      ? <Loader2 size={10} className="animate-spin" />
                      : <RefreshCw size={10} />}
                    refresh now
                  </button>
                  {prediction.cold_start && (
                    <Pill color="amber" testid="flag-cold-start">cold-start</Pill>
                  )}
                  {prediction.model_uncertainty_flag && (
                    <Pill color="amber" testid="flag-uncertainty">high σ²</Pill>
                  )}
                  {predictionOdds?.mw?.book && (
                    <Pill
                      color={predictionOdds.mw.book.is_stale ? 'rose' : 'emerald'}
                      testid="flag-book-freshness"
                    >
                      book {fmtAge(predictionOdds.mw.book.age_sec)}
                      {predictionOdds.mw.book.is_stale ? ' (stale)' : ''}
                    </Pill>
                  )}
                  <Pill color="violet" testid="flag-mode">{prediction.mode}</Pill>
                </div>
              </div>

              {/* Overconfidence Guard — diagnostic badge + tooltip */}
              {prediction.comparison?.overconfidence_guard?.tagged && (
                <div
                  data-testid="overconfidence-guard-badge"
                  className="rounded-sm px-2 py-1.5 text-[10px] border
                    bg-rose-950/30 border-rose-800 text-rose-300"
                  style={mono}
                  title={(prediction.comparison.overconfidence_guard.reasons || []).join(' · ')}
                >
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold">
                      ⚠ {prediction.comparison.overconfidence_guard.tags?.join(' · ') || 'OVERCONFIDENCE'}
                    </span>
                    <span className="text-rose-400/70">
                      penalty: {((prediction.comparison.overconfidence_guard.confidence_penalty ?? 0) * 100).toFixed(0)}pp
                    </span>
                    {prediction.comparison.overconfidence_guard.shrunk_prob_audit != null && (
                      <span className="text-rose-400/70">
                        shrunk→consensus: {(prediction.comparison.overconfidence_guard.setscore_fav_prob * 100).toFixed(1)}%
                        → {(prediction.comparison.overconfidence_guard.shrunk_prob_audit * 100).toFixed(1)}%
                        {prediction.comparison.overconfidence_guard.shrinkage_applied
                          ? ' (applied)'
                          : ' (audit-only)'}
                      </span>
                    )}
                  </div>
                  {(prediction.comparison.overconfidence_guard.reasons || []).length > 0 && (
                    <div className="text-rose-400/60 mt-0.5">
                      SetScore is more extreme than consensus; balanced H2H and/or
                      weak handicap support indicate high variance.
                    </div>
                  )}
                </div>
              )}

              {/* Signal Governance — audit-only verdict panel (Phase 1-3) */}
              {prediction.comparison?.signal_governance?.verdicts && (() => {
                const g = prediction.comparison.signal_governance;
                const v = g.verdicts || {};
                const flags = g.global_flags || {};
                const ph = g.phases || {};
                const a = ph.alignment_mw || {};
                const so = ph.structural_over || {};
                const fq = ph.favorite_quality || {};

                const tierColor = (t) => ({
                  STRONG_ALIGN: 'text-emerald-300',
                  GOOD_ALIGN: 'text-teal-300',
                  WARNING: 'text-yellow-300',
                  DANGER: 'text-rose-300',
                  HIGH: 'text-emerald-300',
                  MEDIUM: 'text-yellow-300',
                  LOW: 'text-orange-300',
                  OVER_STRONG: 'text-emerald-300',
                  OVER_VALID: 'text-teal-300',
                  FAIL: 'text-rose-300',
                }[t] || 'text-zinc-300');

                const routingColor = (r) => ({
                  full: 'text-emerald-300',
                  scout_only: 'text-yellow-300',
                  paper_only: 'text-rose-300',
                  live_only_small_stake: 'text-orange-300',
                }[r] || 'text-zinc-300');

                return (
                  <div
                    data-testid="signal-governance-card"
                    className="rounded-sm px-2 py-2 text-[10px] border bg-zinc-900/50 border-zinc-700 text-zinc-200"
                    style={mono}
                    title="Signal Governance Layer — audit-only by default. Toggle env SIGNAL_GOVERNANCE_AUDIT_ONLY=false to make verdicts effective."
                  >
                    <div className="flex items-center gap-2 flex-wrap mb-1.5">
                      <span className="font-semibold text-zinc-100">⚙ GOVERNANCE</span>
                      <span className={g.audit_only ? 'text-emerald-400/80' : 'text-rose-400'}>
                        {g.audit_only ? 'AUDIT_ONLY' : 'ACTIVE'}
                      </span>
                      {flags.split_brain && (
                        <span className="text-rose-400" data-testid="gov-flag-split-brain">⚠ split-brain</span>
                      )}
                      {flags.danger && (
                        <span className="text-rose-400" data-testid="gov-flag-danger">⚠ danger</span>
                      )}
                      {flags.downgrade_recommended && !flags.danger && !flags.split_brain && (
                        <span className="text-yellow-400/80" data-testid="gov-flag-downgrade">↓ downgrade</span>
                      )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                      {/* Phase 1 — alignment */}
                      <div className="rounded-sm bg-zinc-950/40 px-2 py-1.5">
                        <div className="text-zinc-500 uppercase tracking-wide text-[9px] mb-0.5">model dispersion (mw)</div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`font-semibold ${tierColor(a.alignment_tier)}`} data-testid="gov-align-tier">
                            {a.alignment_tier || '—'}
                          </span>
                          <span className="text-zinc-400">gap {a.alignment_gap_pp != null ? a.alignment_gap_pp.toFixed(1) : '—'}pp</span>
                          <span className="text-zinc-500">·</span>
                          <span className="text-zinc-400">x{(a.confidence_governor_multiplier ?? 1).toFixed(2)}</span>
                        </div>
                      </div>

                      {/* Phase 2 — structural O/U */}
                      <div className="rounded-sm bg-zinc-950/40 px-2 py-1.5">
                        <div className="text-zinc-500 uppercase tracking-wide text-[9px] mb-0.5">structural o/u</div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`font-semibold ${tierColor(so.verdict)}`} data-testid="gov-ou-verdict">
                            {so.verdict || (so.applicable === false ? 'N/A' : '—')}
                          </span>
                          {so.metrics && (
                            <>
                              <span className="text-zinc-400">
                                Δline {so.metrics.expected_total_minus_line != null ? so.metrics.expected_total_minus_line.toFixed(1) : '—'}
                              </span>
                              <span className="text-zinc-500">·</span>
                              <span className="text-zinc-400">
                                E[sets] {so.metrics.expected_sets != null ? so.metrics.expected_sets.toFixed(2) : '—'}
                              </span>
                            </>
                          )}
                        </div>
                        {(so.reasons || []).length > 0 && (
                          <div className="text-zinc-500 mt-0.5">{so.reasons.join(' · ')}</div>
                        )}
                      </div>

                      {/* Phase 3 — favorite quality */}
                      <div className="rounded-sm bg-zinc-950/40 px-2 py-1.5">
                        <div className="text-zinc-500 uppercase tracking-wide text-[9px] mb-0.5">favorite quality</div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`font-semibold ${tierColor(fq.tier)}`} data-testid="gov-fq-tier">
                            {fq.tier || (fq.applicable === false ? 'N/A' : '—')}
                          </span>
                          <span className="text-zinc-400">
                            score {fq.score != null ? fq.score.toFixed(2) : '—'}
                          </span>
                          <span className="text-zinc-500">·</span>
                          <span className="text-zinc-400">
                            cov {Math.round((fq.weight_coverage ?? 0) * 100)}%
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Verdicts row */}
                    <div className="flex items-center gap-3 flex-wrap mt-1.5 pt-1.5 border-t border-zinc-800">
                      <span className="text-zinc-500 uppercase tracking-wide text-[9px]">routing</span>
                      <span data-testid="gov-route-mw" title={v.governance_final_reason_mw}>
                        MW: <span className={routingColor(v.mw_routing)}>{v.mw_routing}</span>
                        <span className="text-zinc-500"> · k{(v.kelly_multiplier_mw ?? 1).toFixed(2)}</span>
                      </span>
                      <span data-testid="gov-route-hc" title={v.governance_final_reason_hc}>
                        HC: <span className={routingColor(v.hc_routing)}>{v.hc_routing}</span>
                        <span className="text-zinc-500"> · k{(v.kelly_multiplier_hc ?? 1).toFixed(2)}</span>
                      </span>
                      <span data-testid="gov-route-ou" title={v.governance_final_reason_ou}>
                        O/U: <span className={routingColor(v.ou_routing)}>{v.ou_routing}</span>
                        <span className="text-zinc-500"> · k{(v.kelly_multiplier_ou ?? 1).toFixed(2)}</span>
                      </span>
                    </div>

                    {/* Final reason explanations — one short line per market */}
                    {(v.governance_final_reason_mw || v.governance_final_reason_hc || v.governance_final_reason_ou) && (
                      <div className="mt-1 space-y-0.5 text-zinc-400" data-testid="gov-final-reasons">
                        {v.governance_final_reason_mw && (
                          <div data-testid="gov-final-reason-mw">· {v.governance_final_reason_mw}</div>
                        )}
                        {v.governance_final_reason_hc && (
                          <div data-testid="gov-final-reason-hc">· {v.governance_final_reason_hc}</div>
                        )}
                        {v.governance_final_reason_ou && (
                          <div data-testid="gov-final-reason-ou">· {v.governance_final_reason_ou}</div>
                        )}
                      </div>
                    )}

                    {(g.explicit_reasons || []).length > 0 && (
                      <div className="mt-1 text-zinc-500" data-testid="gov-reasons">
                        reasons: {g.explicit_reasons.join(' · ')}
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* True Long Score — audit-only fake-long detector */}
              {prediction.comparison?.true_long_score?.tier && (() => {
                const t = prediction.comparison.true_long_score;
                const c = t.components || {};
                const palette = {
                  WEAK: { text: 'text-rose-300', accent: 'text-rose-400/70', bg: 'bg-rose-950/30', border: 'border-rose-800' },
                  MEDIUM: { text: 'text-amber-200', accent: 'text-amber-300/70', bg: 'bg-amber-950/30', border: 'border-amber-800' },
                  GOOD: { text: 'text-teal-300', accent: 'text-teal-400/70', bg: 'bg-teal-950/30', border: 'border-teal-800' },
                  STRONG: { text: 'text-emerald-300', accent: 'text-emerald-400/70', bg: 'bg-emerald-950/30', border: 'border-emerald-800' },
                }[t.tier] || { text: 'text-zinc-300', accent: 'text-zinc-500', bg: 'bg-zinc-900/30', border: 'border-zinc-800' };
                return (
                  <div
                    data-testid="true-long-score-card"
                    className={`rounded-sm px-2 py-1.5 text-[10px] border ${palette.bg} ${palette.border} ${palette.text}`}
                    style={mono}
                    title="Audit-only: distinguishes real long/over pressure from fake long profiles caused by 3-1 dominance. Does NOT affect EV/Kelly/routing/Telegram."
                  >
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold" data-testid="tls-tier">
                        ⌁ TRUE LONG SCORE: {t.tier} · {(t.true_long_score ?? 0).toFixed(3)}
                      </span>
                      {t.fake_long_risk && (
                        <span className="text-rose-400 font-bold animate-pulse" data-testid="tls-fake-long-risk">
                          ⚠ FAKE_LONG_RISK
                        </span>
                      )}
                    </div>
                    <div className={`flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5 ${palette.accent}`}>
                      <span data-testid="tls-five-set">5-set: {(c.five_set_prob ?? 0).toFixed(3)}</span>
                      <span data-testid="tls-three-one">3-1: {(c.three_one_prob ?? 0).toFixed(3)}</span>
                      <span data-testid="tls-short-penalty">short_pen: {(c.short_penalty ?? 0).toFixed(3)}</span>
                      <span data-testid="tls-density-edge">density: {(c.density_norm ?? 0).toFixed(3)}</span>
                    </div>
                  </div>
                );
              })()}

              {/* Player Behavior Layer — audit-only behavioral correction */}
              {prediction.comparison?.player_behavior?.p1 && (() => {
                const pb = prediction.comparison.player_behavior;
                const p1 = pb.p1 || {};
                const p2 = pb.p2 || {};
                const adj = pb.adjusted_true_long || {};
                const er = pb.edge_robustness || {};
                const tierPalette = {
                  ROBUST: { bg: 'bg-emerald-950/30', border: 'border-emerald-800', text: 'text-emerald-300', accent: 'text-emerald-400/70' },
                  MODERATE: { bg: 'bg-teal-950/30', border: 'border-teal-800', text: 'text-teal-300', accent: 'text-teal-400/70' },
                  FRAGILE: { bg: 'bg-amber-950/30', border: 'border-amber-800', text: 'text-amber-200', accent: 'text-amber-300/70' },
                  VERY_FRAGILE: { bg: 'bg-rose-950/30', border: 'border-rose-800', text: 'text-rose-300', accent: 'text-rose-400/70' },
                }[er.tier] || { bg: 'bg-zinc-900/30', border: 'border-zinc-800', text: 'text-zinc-300', accent: 'text-zinc-500' };
                return (
                  <div
                    data-testid="player-behavior-card"
                    className={`rounded-sm px-2 py-1.5 text-[10px] border ${tierPalette.bg} ${tierPalette.border} ${tierPalette.text}`}
                    style={mono}
                    title="Audit-only: aggregates last ~30 settled matches per player to assess long/short/stomp/collapse tendencies and derive edge robustness. Does NOT modify EV / Kelly / routing / Telegram."
                  >
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold" data-testid="pb-tier">
                        ⌬ PLAYER BEHAVIOR · EDGE: {er.tier || 'n/a'}
                        {er.edge_robustness != null && ` · ${(er.edge_robustness ?? 0).toFixed(3)}`}
                      </span>
                      {pb.selected_market && (
                        <span className={tierPalette.accent} data-testid="pb-selected-market">
                          on {String(pb.selected_market).toUpperCase()}{pb.selected_side ? ` / ${pb.selected_side}` : ''}
                        </span>
                      )}
                      {er.downgrade_suggestion && (
                        <span className="text-rose-400 font-bold" data-testid="pb-downgrade">
                          ↘ {er.downgrade_suggestion}
                        </span>
                      )}
                    </div>
                    <div className={`flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5 ${tierPalette.accent}`}>
                      <span data-testid="pb-p1">
                        P1 [n={p1.sample_size ?? 0}] long {(p1.long_tendency ?? 0).toFixed(2)} · short {(p1.short_tendency ?? 0).toFixed(2)} · stomp {(p1.stomp_tendency ?? 0).toFixed(2)} · coll {(p1.collapse_tendency ?? 0).toFixed(2)}
                      </span>
                      <span data-testid="pb-p2">
                        P2 [n={p2.sample_size ?? 0}] long {(p2.long_tendency ?? 0).toFixed(2)} · short {(p2.short_tendency ?? 0).toFixed(2)} · stomp {(p2.stomp_tendency ?? 0).toFixed(2)} · coll {(p2.collapse_tendency ?? 0).toFixed(2)}
                      </span>
                    </div>
                    {adj.adjusted_true_long_score != null && (
                      <div className={`mt-0.5 ${tierPalette.accent}`} data-testid="pb-adj-tls">
                        adj_TLS: {(adj.raw_true_long_score ?? 0).toFixed(3)} → {(adj.adjusted_true_long_score ?? 0).toFixed(3)} ({adj.adjusted_tier})
                        {adj.reasons && adj.reasons.length > 0 && ` · ${adj.reasons.join(' · ')}`}
                      </div>
                    )}
                    {/* Reason-attribution trace */}
                    {(er.selected_side || er.behavior_supported_side || er.current_form_side) && (
                      <div
                        className={`mt-1 border-t pt-1 grid grid-cols-2 gap-x-3 gap-y-0.5 text-[9px] ${er.reason_attribution_conflict ? 'border-rose-800 text-rose-200' : `border-zinc-800 ${tierPalette.accent}`}`}
                        data-testid="pb-trace"
                      >
                        <span data-testid="pb-trace-selected">selected → {er.selected_side ?? '—'}</span>
                        <span data-testid="pb-trace-behavior">behavior_supported → {er.behavior_supported_side ?? '—'}</span>
                        <span data-testid="pb-trace-current-form">
                          current_form → {er.current_form_side ?? '—'}
                          {er.current_form_strength && ` (${er.current_form_strength})`}
                        </span>
                        <span data-testid="pb-trace-agree">
                          beh↔sel: {er.agrees?.behavior_with_selection ? '✓' : '✗'} ·
                          form↔sel: {er.agrees?.current_form_with_selection == null ? '—' : (er.agrees.current_form_with_selection ? '✓' : '✗')}
                        </span>
                        {er.reason_attribution_conflict && (
                          <span className="col-span-2 text-rose-300 font-bold" data-testid="pb-trace-conflict">
                            ⚠ REASON_ATTRIBUTION_CONFLICT — same-day form opposes selected side
                          </span>
                        )}
                      </div>
                    )}
                    {er.reasons && er.reasons.length > 0 && (
                      <div
                        className={`mt-0.5 ${er.reason_attribution_conflict ? 'text-rose-300' : tierPalette.accent}`}
                        data-testid="pb-reasons"
                      >
                        {er.reasons.join(' · ')}
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Unified Match Distribution MVP (2026-05-17) — audit-only */}
              {prediction.comparison?.unified_match_distribution?.distribution_confidence && (() => {
                const u = prediction.comparison.unified_match_distribution;
                const confPalette = {
                  HIGH: 'bg-emerald-950/40 border-emerald-800 text-emerald-200',
                  MEDIUM: 'bg-amber-950/30 border-amber-800 text-amber-200',
                  LOW: 'bg-rose-950/30 border-rose-800 text-rose-200',
                  VERY_LOW: 'bg-rose-950/50 border-rose-700 text-rose-100',
                }[u.distribution_confidence] || 'bg-zinc-900/30 border-zinc-800 text-zinc-200';
                const classPalette = {
                  BEST_SCENARIO_MARKET: 'bg-emerald-700 text-emerald-50',
                  BEST_AVAILABLE_MARKET: 'bg-amber-700 text-amber-50',
                  RAW_EV_MARKET_BUT_SCENARIO_FRAGILE: 'bg-orange-700 text-orange-50',
                  THEORETICAL_LEAN_NO_PLAYABLE_ODDS: 'bg-zinc-700 text-zinc-100',
                  NO_PLAY: 'bg-rose-800 text-rose-50',
                }[u.selected_market_classification] || 'bg-zinc-700 text-zinc-100';
                const top3 = (u.market_projection || [])
                  .filter((p) => p.is_real_odds && p.robust_ev != null)
                  .sort((a, b) => (b.robust_ev || 0) - (a.robust_ev || 0))
                  .slice(0, 3);
                const fmt = (v, d = 3) => v == null ? '—' : Number(v).toFixed(d);
                const fmtPct = (v) => v == null ? '—' : (Number(v) * 100).toFixed(1) + '%';
                const fmtEV = (v) => v == null ? '—' : (v >= 0 ? '+' : '') + (v * 100).toFixed(2) + '%';
                return (
                  <div
                    data-testid="unified-match-distribution-card"
                    className={`rounded-sm px-2 py-2 text-[10px] border ${confPalette}`}
                    style={mono}
                    title="Audit-only Unified Match Distribution: consolidates every audit layer into one coherent scoreline distribution and projects MW/HC/OU from the same source. Does NOT modify EV/Kelly/routing/Telegram."
                  >
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-[11px]" data-testid="umd-title">
                        ⌬ UNIFIED MATCH DISTRIBUTION
                      </span>
                      <span data-testid="umd-confidence">
                        confidence: <span className="font-mono font-semibold">{u.distribution_confidence}</span>
                      </span>
                      <span data-testid="umd-scenario">
                        scenario: <span className="font-semibold">{u.dominant_scenario}</span>
                      </span>
                      <span className={`px-1.5 py-0.5 rounded-sm text-[9px] font-bold ${classPalette}`} data-testid="umd-best-class">
                        {u.selected_market_classification}
                      </span>
                      <span data-testid="umd-best-action" className="font-mono">
                        action: {u.best_action}
                      </span>
                    </div>

                    {u.scenario_summary && (
                      <div className="mt-1 text-[9px] italic opacity-80" data-testid="umd-scenario-summary">
                        {u.scenario_summary}
                      </div>
                    )}

                    {/* Market ranking */}
                    {top3.length > 0 && (
                      <div className="mt-2 border-t border-current/20 pt-1.5">
                        <div className="text-[9px] uppercase tracking-wider opacity-70 mb-0.5">
                          Top markets · robust EV
                        </div>
                        <div className="space-y-0.5" data-testid="umd-market-ranking">
                          {top3.map((p, i) => (
                            <div
                              key={`${p.market_family}-${p.side}`}
                              data-testid={`umd-rank-${i + 1}`}
                              className="flex items-center gap-2 font-mono"
                            >
                              <span className="opacity-50">#{i + 1}</span>
                              <span className="font-semibold">
                                {p.market_family} {p.side}
                              </span>
                              <span className="opacity-70">@ {p.odds?.toFixed(2)}</span>
                              <span className={p.robust_ev >= 0 ? 'text-emerald-300' : 'text-rose-300'}>
                                robust {fmtEV(p.robust_ev)}
                              </span>
                              <span className="opacity-60">
                                adj {fmtEV(p.adjusted_ev)} · str {fmtEV(p.stressed_ev)}
                              </span>
                              {p.market_classification && (
                                <span className="text-[8px] opacity-50">{p.market_classification}</span>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Conclusion */}
                    {u.why_best_market && (
                      <div className="mt-1.5 text-[9px] border-l-2 border-current/30 pl-2" data-testid="umd-why-best">
                        ✓ {u.why_best_market}
                      </div>
                    )}
                    {u.why_not_raw_market && (
                      <div className="mt-0.5 text-[9px] border-l-2 border-rose-400/40 pl-2 italic opacity-80" data-testid="umd-why-not-raw">
                        ✗ {u.why_not_raw_market}
                      </div>
                    )}

                    {/* Adjustment audit */}
                    {(u.adjustment_audit || []).length > 0 && (
                      <details className="mt-1.5 text-[9px]" data-testid="umd-audit">
                        <summary className="cursor-pointer opacity-70 hover:opacity-100">
                          {u.adjustment_audit.length} adjustment(s) applied · {(u.total_mass_moved * 100).toFixed(1)}pp moved
                        </summary>
                        <div className="mt-1 space-y-0.5 pl-3">
                          {u.adjustment_audit.map((a, idx) => (
                            <div key={idx} className="opacity-80">
                              · <span className="font-semibold">{a.reason_code}</span>{' '}
                              ({(a.mass_moved * 100).toFixed(2)}pp) — {a.explanation}
                            </div>
                          ))}
                        </div>
                      </details>
                    )}

                    {/* Scenario rows table */}
                    {(u.scenario_rows || []).length > 0 && (
                      <details className="mt-1 text-[9px]" data-testid="umd-scenarios">
                        <summary className="cursor-pointer opacity-70 hover:opacity-100">
                          Scenario rows ({u.scenario_rows.length})
                        </summary>
                        <table className="mt-1 w-full">
                          <thead className="opacity-50">
                            <tr>
                              <th className="text-left pr-2">scoreline</th>
                              <th className="text-left pr-2">shape</th>
                              <th className="text-right pr-2">raw</th>
                              <th className="text-right pr-2">adj</th>
                              <th className="text-right pr-2">stress</th>
                              <th className="text-right pr-2">~pts</th>
                              <th className="text-left">supports</th>
                            </tr>
                          </thead>
                          <tbody>
                            {u.scenario_rows.map((r) => {
                              // 2026-05-21 — for 3-1 / 1-3 rows, expand
                              // with under / borderline / over buckets
                              // computed from the MC samples against
                              // the operator-selected O/U line. NO
                              // hardcoded splits.
                              const isThreeOne = (r.cell || '').match(/_3_1|3_1_/);
                              const slc = (prediction.outputs?.scoreline_conditional || {})[r.cell] || {};
                              const line = slc.selected_ou_line;
                              const totalProb = slc.total_prob ?? r.probability_adjusted;
                              const fmtBucket = (p) => (p == null ? '—' : (p * 100).toFixed(2) + '%');
                              return (
                                <React.Fragment key={r.cell}>
                                  <tr className="opacity-80">
                                    <td className="pr-2 font-semibold">{r.scoreline}</td>
                                    <td className="pr-2 opacity-70">{r.point_shape_type}</td>
                                    <td className="text-right pr-2">{fmtPct(r.probability_raw)}</td>
                                    <td className="text-right pr-2 font-semibold">{fmtPct(r.probability_adjusted)}</td>
                                    <td className="text-right pr-2">{fmtPct(r.probability_stressed)}</td>
                                    <td className="text-right pr-2">{fmt(r.estimated_total_points_adjusted, 0)}</td>
                                    <td className="opacity-60">
                                      MW:{r.supports.mw} · HC:{r.supports.hc} · OU:{r.supports.ou}
                                    </td>
                                  </tr>
                                  {isThreeOne && slc.total_points_mean != null && (
                                    <tr className="opacity-70 text-[8.5px]" data-testid={`umd-31-buckets-${r.cell}`}>
                                      <td colSpan={7} className="pl-3 pb-1">
                                        <span className="text-zinc-400">
                                          {r.cell} buckets vs line <b>{line ?? '—'}</b> (±{slc.borderline_pp ?? 2}):
                                        </span>
                                        {' '}
                                        <span className="text-rose-300">under {fmt(line, 1)}: <b>{fmtBucket(slc.under_bucket_prob)}</b></span>
                                        {' · '}
                                        <span className="text-amber-300">borderline: <b>{fmtBucket(slc.borderline_bucket_prob)}</b></span>
                                        {' · '}
                                        <span className="text-emerald-300">over {fmt(line, 1)}: <b>{fmtBucket(slc.over_bucket_prob)}</b></span>
                                        {' · '}
                                        <span className="opacity-60">
                                          mean {fmt(slc.total_points_mean, 1)} pts · p25/p50/p75: {slc.total_points_p25}/{slc.total_points_p50}/{slc.total_points_p75}
                                        </span>
                                        {(() => {
                                          const sum = (slc.under_bucket_prob || 0) + (slc.borderline_bucket_prob || 0) + (slc.over_bucket_prob || 0);
                                          const drift = Math.abs(sum - (totalProb || 0));
                                          return drift > 0.005 ? (
                                            <span className="ml-1 text-rose-400 italic">⚠ mass drift {(drift * 100).toFixed(2)}pp</span>
                                          ) : null;
                                        })()}
                                      </td>
                                    </tr>
                                  )}
                                </React.Fragment>
                              );
                            })}
                          </tbody>
                        </table>
                      </details>
                    )}
                  </div>
                );
              })()}

              {/* Point Shape Realism — audit/shadow O/U realism cross-check */}
              {prediction.comparison?.point_shape_realism?.mode && (() => {
                const ps = prediction.comparison.point_shape_realism;
                const sample = ps.sample_size || {};
                const tags = ps.tags || [];
                const reasons = ps.reason_codes || [];
                const hasFragility = tags.some((t) =>
                  ['SIM_POINTS_OVERSTATED_RISK',
                    'UNVERIFIED_DENSITY',
                    'LOW_DENSITY_OVER_FRAGILE'].includes(t),
                );
                const palette = hasFragility
                  ? { bg: 'bg-orange-950/30', border: 'border-orange-800', text: 'text-orange-200', accent: 'text-orange-400/70' }
                  : tags.includes('POINT_SHAPE_CONFIRMED')
                    ? { bg: 'bg-emerald-950/30', border: 'border-emerald-800', text: 'text-emerald-300', accent: 'text-emerald-400/70' }
                    : { bg: 'bg-zinc-900/30', border: 'border-zinc-800', text: 'text-zinc-300', accent: 'text-zinc-500' };
                const fmt = (v, d = 2) => (v == null ? '—' : Number(v).toFixed(d));
                const fmtPct = (v) => (v == null ? '—' : (Number(v) * 100).toFixed(1) + '%');
                return (
                  <div
                    data-testid="point-shape-realism-card"
                    className={`rounded-sm px-2 py-1.5 text-[10px] border ${palette.bg} ${palette.border} ${palette.text}`}
                    style={mono}
                    title="Audit-only: cross-checks model pts/set vs historical set-shape and tournament baseline. Does NOT modify EV / Kelly / probabilities / routing."
                  >
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold" data-testid="ps-title">
                        ▦ POINT SHAPE REALISM · density_conf {ps.density_confidence ?? '—'}
                      </span>
                      <span className={palette.accent} data-testid="ps-samples">
                        P1 n={sample.p1_sets ?? 0} sets · P2 n={sample.p2_sets ?? 0} sets
                      </span>
                      {tags.map((t) => (
                        <span
                          key={t}
                          data-testid={`ps-tag-${t}`}
                          className={`px-1.5 py-0.5 rounded-sm text-[9px] font-semibold border ${t === 'POINT_SHAPE_CONFIRMED' ? 'bg-emerald-900/40 text-emerald-200 border-emerald-800' : 'bg-orange-900/40 text-orange-100 border-orange-800'}`}
                        >
                          {t}
                        </span>
                      ))}
                    </div>
                    <div className={`grid grid-cols-2 gap-x-3 gap-y-0.5 mt-1 ${palette.accent}`}>
                      <span data-testid="ps-model-pps">
                        model pts/set: <span className={palette.text}>{fmt(ps.model_expected_pts_per_set)}</span>
                      </span>
                      <span data-testid="ps-hist-pps">
                        historical pts/set: <span className={palette.text}>{fmt(ps.combined_historical_pts_per_set)}</span>
                        {ps.pps_blend_audit?.applied && (
                          <span className="ml-1 text-[9px] text-emerald-400" title={`pre-blend: ${ps.combined_historical_pts_per_set_pre_h2h_blend}`}>
                            ⤴ H2H blended
                          </span>
                        )}
                      </span>
                      <span data-testid="ps-tour-pps">
                        tournament baseline: <span className={palette.text}>{fmt(ps.tournament_pts_per_set)}</span>
                      </span>
                      <span data-testid="ps-gap">
                        gap: <span className={`${palette.text} ${ps.point_shape_gap != null && Math.abs(ps.point_shape_gap) >= 0.70 ? 'text-orange-300 font-semibold' : ''}`}>
                          {ps.point_shape_gap == null
                            ? '—'
                            : (ps.point_shape_gap >= 0 ? '+' : '') + fmt(ps.point_shape_gap)}
                        </span>
                      </span>
                      <span data-testid="ps-close">
                        hist close-set: <span className={palette.text}>{fmtPct(ps.combined_historical_close_set_rate)}</span>
                      </span>
                      <span data-testid="ps-stomp">
                        hist stomp: <span className={palette.text}>{fmtPct(ps.combined_historical_stomp_rate)}</span>
                      </span>
                    </div>
                    <div className={`mt-1 grid grid-cols-2 gap-x-3 gap-y-0.5 text-[9px] border-t pt-1 ${palette.accent} ${hasFragility ? 'border-orange-900/60' : 'border-zinc-800'}`}>
                      <span data-testid="ps-raw-total" title="BO5 Monte Carlo mean of total points sampled from real per-set tightness. Differs from Behavior Correction's scoreline-derived E[total] which uses hardcoded 58/78/97 averages.">
                        raw E[total] <span className="text-[9px] text-zinc-500">(BO5 MC)</span>: <span className={palette.text}>{fmt(ps.raw_expected_total)}</span>
                      </span>
                      <span data-testid="ps-adj-total">
                        adj E[total] <span className="text-[9px] text-zinc-500">(BO5 MC)</span>: <span className={palette.text}>{fmt(ps.adjusted_expected_total)}</span>
                        {ps.adjusted_total_delta != null && Math.abs(ps.adjusted_total_delta) > 0.05 && (
                          <span className="ml-1 text-orange-300">
                            ({ps.adjusted_total_delta >= 0 ? '+' : ''}{fmt(ps.adjusted_total_delta)})
                          </span>
                        )}
                      </span>
                      <span data-testid="ps-prob-status" className="col-span-2 italic text-[9px]">
                        probability_adjustment_status: {ps.probability_adjustment_status}
                      </span>
                    </div>
                    {/* H2H pts/set blending audit (Option B, 2026-05-21) */}
                    {ps.pps_blend_audit && (
                      <div className="mt-1 text-[9px] border-t border-zinc-800/60 pt-1 text-zinc-400" data-testid="ps-pps-blend-audit">
                        <div className="flex items-center gap-2 flex-wrap mb-0.5">
                          <span className="font-semibold text-zinc-300">pts/set H2H blend:</span>
                          <span className={`px-1.5 py-0.5 rounded-sm text-[9px] font-semibold border ${
                            ps.pps_blend_audit.applied
                              ? 'bg-emerald-900/40 text-emerald-100 border-emerald-700'
                              : 'bg-zinc-800 text-zinc-400 border-zinc-700'
                          }`}>
                            {ps.pps_blend_audit.applied ? `APPLIED · ${ps.pps_blend_audit.h2h_strength}` : `NOT APPLIED (${ps.pps_blend_audit.h2h_strength || 'NONE'})`}
                          </span>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-x-3 gap-y-0.5">
                          <span className="text-zinc-500">w_historical:</span>
                          <span className="text-zinc-200" data-testid="ps-blend-w-hist">{(ps.pps_blend_audit.w_historical * 100).toFixed(0)}%</span>
                          <span className="text-zinc-500">w_h2h:</span>
                          <span className="text-zinc-200" data-testid="ps-blend-w-h2h">{(ps.pps_blend_audit.w_h2h * 100).toFixed(0)}%</span>
                          <span className="text-zinc-500">historical_pps_pre_blend:</span>
                          <span className="text-zinc-200">{fmt(ps.pps_blend_audit.historical_pps_pre_blend)}</span>
                          <span className="text-zinc-500">h2h_pts_per_set:</span>
                          <span className="text-zinc-200">
                            {ps.pps_blend_audit.h2h_pts_per_set == null
                              ? '—'
                              : `${fmt(ps.pps_blend_audit.h2h_pts_per_set)} (n=${ps.pps_blend_audit.h2h_n}, mapping=${ps.pps_blend_audit.h2h_mapping_confidence?.toFixed(2)})`}
                          </span>
                          <span className="text-zinc-500">combined_pps_post_blend:</span>
                          <span className="text-emerald-300 font-semibold">{fmt(ps.pps_blend_audit.combined_pps_post_blend)}</span>
                        </div>
                        {ps.pps_blend_audit.skip_reason && (
                          <div className="text-[9px] italic text-zinc-500 mt-0.5">
                            skip reason: {ps.pps_blend_audit.skip_reason}
                          </div>
                        )}
                        {ps.pps_blend_audit.applied && ps.pps_blend_audit.formula && (
                          <div className="text-[9px] italic text-zinc-500 mt-0.5">
                            {ps.pps_blend_audit.formula}
                          </div>
                        )}
                      </div>
                    )}
                    {reasons.length > 0 && (
                      <div className={`mt-0.5 text-[9px] ${palette.accent}`} data-testid="ps-reasons">
                        {reasons.join(' · ')}
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* 2026-05-27 — O/U Line Integrity Panel.
                  Surfaces the line each module is evaluating and emits
                  `OU_BUCKET_LINE_MISMATCH` when divergences are detected
                  between OU trace / resolver / 4-set bucket / Final
                  Scorer / recommendation. */}
              {prediction.comparison?.ou_line_integrity && (() => {
                const oli = prediction.comparison.ou_line_integrity;
                const src = oli.sources || {};
                const mismatch = !!oli.mismatch_detected;
                const palette = mismatch
                  ? { bg: 'bg-amber-950/30', border: 'border-amber-700', text: 'text-amber-100' }
                  : { bg: 'bg-zinc-900/30', border: 'border-zinc-700', text: 'text-zinc-200' };
                const fmtLine = (v) => v == null ? '—' : Number(v).toFixed(1);
                return (
                  <div
                    data-testid="ou-line-integrity"
                    className={`rounded-sm px-2 py-1.5 text-[10px] border ${palette.bg} ${palette.border} ${palette.text}`}
                    style={mono}
                  >
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="font-semibold">⛓ O/U LINE INTEGRITY</span>
                      {mismatch ? (
                        <span
                          data-testid="ou-line-integrity-flag"
                          className="px-1.5 py-0.5 rounded-sm text-[9px] font-semibold bg-amber-900/40 text-amber-100 border border-amber-700"
                        >
                          {oli.integrity_flag || 'OU_BUCKET_LINE_MISMATCH'}
                        </span>
                      ) : (
                        <span className="px-1.5 py-0.5 rounded-sm text-[9px] font-semibold bg-emerald-900/40 text-emerald-100 border border-emerald-700">
                          ALIGNED
                        </span>
                      )}
                      <span className="text-[9px] text-zinc-400">
                        authoritative line:{' '}
                        <b
                          className="text-emerald-300"
                          data-testid="ou-line-integrity-authoritative"
                        >
                          {fmtLine(oli.authoritative_line)}
                        </b>
                      </span>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-x-3 gap-y-0.5 text-[9px] border-t border-zinc-800/50 pt-1">
                      <span className="text-zinc-500">real bookmaker line:</span>
                      <span data-testid="ou-line-src-real-bookmaker"
                            className={oli.bookmaker_line_unavailable
                              ? 'text-rose-300' : 'text-emerald-300'}>
                        {oli.bookmaker_line_unavailable
                          ? 'UNAVAILABLE → fallback 75.5'
                          : fmtLine(src.real_bookmaker_line)}
                      </span>
                      <span className="text-zinc-500">OU trace line:</span>
                      <span data-testid="ou-line-src-ou-trace">{fmtLine(src.ou_trace_selected_line)}</span>
                      <span className="text-zinc-500">Final Scorer line:</span>
                      <span data-testid="ou-line-src-final-scorer">{fmtLine(src.final_scorer_selected_line)}</span>
                      <span className="text-zinc-500">Resolver 4-set bucket line:</span>
                      <span data-testid="ou-line-src-resolver">{fmtLine(src.resolver_4set_bucket_line)}</span>
                      <span className="text-zinc-500">Final recommendation line:</span>
                      <span data-testid="ou-line-src-final-reco">{fmtLine(src.final_recommendation_line)}</span>
                      <span className="text-zinc-500">
                        baseline (closest to E[total], sensitivity only):
                      </span>
                      <span
                        data-testid="ou-line-src-baseline"
                        className={mismatch ? 'text-amber-200' : ''}
                      >
                        {fmtLine(src.baseline_expected_total_closest_line)}
                      </span>
                      <span className="text-zinc-500 italic">
                        model first simulated line (debug only):
                      </span>
                      <span
                        data-testid="ou-line-src-model-first"
                        className="text-zinc-600 italic"
                      >
                        {fmtLine(src.model_first_simulated_line)}
                      </span>
                    </div>
                    <div className="text-[9px] italic text-zinc-500 mt-0.5">
                      {oli.explanation}
                    </div>
                  </div>
                );
              })()}

              {/* 2026-05-27 — Phase A: O/U REAL PREDICTION LAYER.
                  Two evaluation views (authoritative bookmaker line and
                  operator practical baseline O75.5) side-by-side, with
                  duration decomposition, density-tagged path table,
                  H2H Over/Under by duration, line-sensitivity CDF and
                  calibration integrity audit. */}
              {prediction.comparison?.ou_real_prediction && !prediction.comparison.ou_real_prediction.error && (() => {
                const orp = prediction.comparison.ou_real_prediction;
                const ci = orp.calibration_integrity || {};
                const av = orp.authoritative_view;
                const pv = orp.practical_baseline_view;
                const flags = orp.integrity_flags || [];
                const fmtP = (v) => v == null ? '—' : `${(Number(v) * 100).toFixed(2)}%`;
                const fmtN = (v) => v == null ? '—' : Number(v).toFixed(2);
                const densBg = (d) => d === 'DENSE' ? 'bg-emerald-900/30 text-emerald-200' :
                                       d === 'BORDERLINE_HIGH' ? 'bg-lime-900/30 text-lime-200' :
                                       d === 'BORDERLINE_LOW' ? 'bg-amber-900/30 text-amber-200' :
                                       d === 'SHORT' ? 'bg-rose-900/30 text-rose-200' :
                                       'bg-zinc-900/30 text-zinc-400';
                const driverColor = (d) => d === '5set' ? 'text-emerald-300' :
                                            d === '4set' ? 'text-lime-300' :
                                            d === '3set' ? 'text-rose-300' :
                                            d === 'density' ? 'text-cyan-300' :
                                            d?.startsWith('low_density') ? 'text-amber-300' :
                                            'text-zinc-400';

                const ViewBlock = ({title, view, verdict, isAuthoritative}) => {
                  if (!view) return null;
                  const dd = view.duration_decomposition || {};
                  const ls = view.line_sensitivity || {};
                  const h2h = view.h2h_over_under_by_duration || {};
                  return (
                    <div
                      data-testid={`ou-real-pred-${isAuthoritative ? 'authoritative' : 'practical'}`}
                      className={`rounded-sm border ${isAuthoritative ? 'border-emerald-700 bg-emerald-950/10' : 'border-cyan-700 bg-cyan-950/10'} p-2 text-[10px] flex-1`}
                      style={mono}
                    >
                      <div className="flex items-baseline justify-between mb-1">
                        <span className="font-bold">{title}</span>
                        <span className="text-zinc-500">@ line <b className={isAuthoritative ? 'text-emerald-300' : 'text-cyan-300'}>{fmtN(view.selected_ou_line)}</b></span>
                      </div>
                      <div className="mb-1.5">
                        <span className="text-zinc-500">verdict: </span>
                        <span className={`px-1.5 py-0.5 rounded-sm font-bold ${verdict?.includes('OVER') ? 'bg-emerald-900/40 text-emerald-100 border border-emerald-700' : verdict?.includes('UNDER') ? 'bg-rose-900/40 text-rose-100 border border-rose-700' : 'bg-zinc-800 text-zinc-300'}`}>
                          {verdict || '—'}
                        </span>
                      </div>
                      {/* Over / Under totals */}
                      <div className="grid grid-cols-3 gap-1 text-[9px] mb-1 border-t border-zinc-800/50 pt-1">
                        <div><span className="text-zinc-500">P(Over):</span> <b className="text-emerald-300">{fmtP(dd.total_over_probability)}</b></div>
                        <div><span className="text-zinc-500">P(Bord.):</span> <b className="text-amber-300">{fmtP(dd.total_borderline_probability)}</b></div>
                        <div><span className="text-zinc-500">P(Under):</span> <b className="text-rose-300">{fmtP(dd.total_under_probability)}</b></div>
                      </div>
                      {/* Duration decomposition */}
                      <div className="grid grid-cols-3 gap-1 text-[9px] mb-1">
                        <div><span className="text-zinc-500">3-set Over/Under:</span><br/>{fmtP(dd.over_from_3set_paths)} / {fmtP(dd.under_from_3set_paths)}</div>
                        <div><span className="text-zinc-500">4-set Over/Under:</span><br/>{fmtP(dd.over_from_4set_paths)} / {fmtP(dd.under_from_4set_paths)}</div>
                        <div><span className="text-zinc-500">5-set Over/Under:</span><br/>{fmtP(dd.over_from_5set_paths)} / {fmtP(dd.under_from_5set_paths)}</div>
                      </div>
                      <div className="text-[9px] mb-1">
                        <span className="text-zinc-500">main Over driver: </span>
                        <span className={driverColor(dd.main_over_driver)}>{dd.main_over_driver || '—'}</span>
                        <span className="text-zinc-500"> · main Under driver: </span>
                        <span className={driverColor(dd.main_under_driver)}>{dd.main_under_driver || '—'}</span>
                        <span className="text-zinc-600 ml-1">(Σ={fmtN(dd.mass_invariance_check)})</span>
                      </div>
                      {/* Path decomposition table */}
                      {(dd.path_decomposition || []).length > 0 && (
                        <div className="mt-1 border-t border-zinc-800/50 pt-1">
                          <div className="text-[9px] text-zinc-400 font-semibold mb-0.5">Scoreline path breakdown:</div>
                          <table className="w-full text-[9px]" data-testid={`path-decomposition-${isAuthoritative ? 'auth' : 'prac'}`}>
                            <thead>
                              <tr className="text-zinc-500 border-b border-zinc-800/50">
                                <th className="text-left">path</th><th>prob</th><th>mean</th><th>p25</th><th>p75</th>
                                <th>density</th><th>O</th><th>U</th><th>±95%</th>
                              </tr>
                            </thead>
                            <tbody>
                              {dd.path_decomposition.map((p) => (
                                <tr key={p.path} className="border-b border-zinc-900/30">
                                  <td className="font-semibold">{p.path}</td>
                                  <td className="text-center">{fmtP(p.total_prob)}</td>
                                  <td className="text-center">{fmtN(p.total_points_mean)}</td>
                                  <td className="text-center text-zinc-500">{fmtN(p.total_points_p25)}</td>
                                  <td className="text-center text-zinc-500">{fmtN(p.total_points_p75)}</td>
                                  <td className="text-center">
                                    <span className={`px-1 rounded-sm ${densBg(p.density_classification)}`}>{p.density_classification}</span>
                                  </td>
                                  <td className="text-center text-emerald-300">{fmtP(p.over_bucket_prob)}</td>
                                  <td className="text-center text-rose-300">{fmtP(p.under_bucket_prob)}</td>
                                  <td className="text-center text-zinc-600">{fmtN(p.ci_half_width_95)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                      {/* H2H Over/Under by duration */}
                      {h2h && (h2h.h2h_all?.n || 0) > 0 && (
                        <div className="mt-1 border-t border-zinc-800/50 pt-1 text-[9px]">
                          <div className="text-zinc-400 font-semibold mb-0.5">H2H O/U @ this line:</div>
                          <div className="grid grid-cols-4 gap-1">
                            {['h2h_3set','h2h_4set','h2h_5set','h2h_all'].map((k) => {
                              const v = h2h[k] || {};
                              const label = k === 'h2h_all' ? 'ALL' : k.replace('h2h_', '');
                              return (
                                <div key={k} className="bg-zinc-900/30 rounded-sm px-1 py-0.5">
                                  <div className="text-zinc-500">{label} (n={v.n ?? 0})</div>
                                  <div>O <b className="text-emerald-300">{fmtP(v.over_rate)}</b></div>
                                  <div>U <b className="text-rose-300">{fmtP(v.under_rate)}</b></div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                      {/* Line sensitivity CDF + market odds + EV */}
                      {(ls.lines || []).length > 0 && (
                        <div className="mt-1 border-t border-zinc-800/50 pt-1 text-[9px]">
                          <div className="flex items-center gap-2 mb-0.5">
                            <span className="text-zinc-400 font-semibold">Line sensitivity (CDF + EV):</span>
                            {ls.best_ev_in_window?.side && (
                              <span data-testid={`best-ev-${isAuthoritative ? 'auth' : 'prac'}`} className={`px-1.5 py-0.5 rounded-sm text-[9px] font-semibold ${ls.best_ev_in_window.ev > 0 ? 'bg-emerald-900/40 text-emerald-100 border border-emerald-700' : 'bg-zinc-800 text-zinc-400 border border-zinc-700'}`}>
                                Best edge: {ls.best_ev_in_window.side.toUpperCase()} {ls.best_ev_in_window.line} EV {(ls.best_ev_in_window.ev * 100).toFixed(2)}%
                              </span>
                            )}
                          </div>
                          <table className="w-full text-[9px]" data-testid={`line-sensitivity-${isAuthoritative ? 'auth' : 'prac'}`}>
                            <thead>
                              <tr className="text-zinc-500">
                                <th>line</th><th>P(Over)</th><th>fair O</th><th>mkt O</th><th>EV O</th>
                                <th>mkt U</th><th>EV U</th><th>book</th>
                              </tr>
                            </thead>
                            <tbody>
                              {ls.lines.map((r) => {
                                const evCellO = r.ev_over == null ? '—' : `${(r.ev_over * 100).toFixed(2)}%`;
                                const evCellU = r.ev_under == null ? '—' : `${(r.ev_under * 100).toFixed(2)}%`;
                                const evColorO = r.ev_over == null ? 'text-zinc-600' :
                                                  r.ev_over > 0.05 ? 'text-emerald-300 font-bold' :
                                                  r.ev_over > 0 ? 'text-emerald-400' :
                                                  'text-rose-400';
                                const evColorU = r.ev_under == null ? 'text-zinc-600' :
                                                  r.ev_under > 0.05 ? 'text-emerald-300 font-bold' :
                                                  r.ev_under > 0 ? 'text-emerald-400' :
                                                  'text-rose-400';
                                return (
                                  <tr key={r.line} className={r.is_selected ? 'bg-emerald-950/30 font-semibold' : ''}>
                                    <td className="text-center">{fmtN(r.line)}{r.is_selected && ' ★'}</td>
                                    <td className="text-center text-emerald-300">{fmtP(r.p_over)}</td>
                                    <td className="text-center text-zinc-500">{fmtN(r.fair_odds_over)}</td>
                                    <td className="text-center text-zinc-300">{r.market_odds_over == null ? '—' : Number(r.market_odds_over).toFixed(3)}</td>
                                    <td className={`text-center ${evColorO}`}>{evCellO}</td>
                                    <td className="text-center text-zinc-300">{r.market_odds_under == null ? '—' : Number(r.market_odds_under).toFixed(3)}</td>
                                    <td className={`text-center ${evColorU}`}>{evCellU}</td>
                                    <td className="text-center text-zinc-500 text-[8px]">{r.bookmaker || '—'}</td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  );
                };

                return (
                  <div
                    data-testid="ou-real-prediction"
                    className="rounded-sm border border-emerald-800/60 bg-zinc-900/30 p-2"
                    style={mono}
                  >
                    <div className="flex items-baseline justify-between mb-1.5">
                      <span className="text-[11px] font-bold text-emerald-300">🎯 O/U REAL PREDICTION LAYER</span>
                      <div className="flex gap-1">
                        {flags.length === 0 && (
                          <span className="px-1.5 py-0.5 rounded-sm text-[9px] bg-emerald-900/40 text-emerald-100 border border-emerald-700">CLEAN</span>
                        )}
                        {flags.map((f) => (
                          <span key={f} data-testid={`ou-flag-${f}`} className="px-1.5 py-0.5 rounded-sm text-[9px] bg-amber-900/40 text-amber-100 border border-amber-700 font-semibold">{f}</span>
                        ))}
                      </div>
                    </div>
                    {/* 2026-05-27 — Final Scorer integrity audit:
                        snapshot of signal_governance.structural_over +
                        cross-check vs decomposition probability */}
                    {orp.final_scorer_view && (() => {
                      const fs = orp.final_scorer_view;
                      const m = fs.metrics || {};
                      const delta = fs.final_scorer_minus_decomposition_delta;
                      const flagged = !!fs.integrity_flag;
                      const verdictTone = fs.verdict?.includes('OVER') ? 'text-emerald-300' :
                                           fs.verdict?.includes('UNDER') ? 'text-rose-300' :
                                           'text-zinc-300';
                      return (
                        <div
                          data-testid="ou-final-scorer-view"
                          className={`mb-2 text-[9px] rounded-sm border ${flagged ? 'border-amber-700 bg-amber-950/15' : 'border-zinc-700 bg-zinc-900/30'} px-2 py-1`}
                        >
                          <div className="flex flex-wrap items-baseline gap-2 mb-1">
                            <span className="text-zinc-400 font-semibold">📡 FINAL SCORER (signal_governance.structural_over)</span>
                            <span className="text-zinc-500">verdict:</span>
                            <span className={`px-1.5 py-0.5 rounded-sm font-bold ${flagged ? 'bg-amber-900/40 border border-amber-700' : 'bg-zinc-800'} ${verdictTone}`} data-testid="fs-verdict">{fs.verdict || '—'}</span>
                            <span className="text-zinc-500">routing:</span>
                            <span className="text-zinc-300">{fs.routing || '—'}</span>
                            {flagged && (
                              <span data-testid="fs-divergent-flag" className="px-1.5 py-0.5 rounded-sm bg-amber-900/40 text-amber-100 border border-amber-700 font-semibold">
                                {fs.integrity_flag}
                              </span>
                            )}
                          </div>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-x-3 gap-y-0.5 border-t border-zinc-800/50 pt-1">
                            <span className="text-zinc-500">over_probability:</span>
                            <span data-testid="fs-over-prob"><b className="text-emerald-300">{m.over_probability != null ? `${(m.over_probability * 100).toFixed(2)}%` : '—'}</b></span>
                            <span className="text-zinc-500">selected_line:</span>
                            <span><b className="text-emerald-300">{m.selected_line ?? '—'}</b></span>
                            <span className="text-zinc-500">expected_total:</span>
                            <span>{m.expected_total != null ? Number(m.expected_total).toFixed(2) : '—'}</span>
                            <span className="text-zinc-500">expected_sets:</span>
                            <span>{m.expected_sets != null ? Number(m.expected_sets).toFixed(2) : '—'}</span>
                            <span className="text-zinc-500">long_match_prob:</span>
                            <span>{m.long_match_prob != null ? `${(m.long_match_prob * 100).toFixed(1)}%` : '—'}</span>
                            <span className="text-zinc-500">blowout_risk:</span>
                            <span>{m.blowout_risk != null ? `${(m.blowout_risk * 100).toFixed(1)}%` : '—'}</span>
                            <span className="text-zinc-500">density_edge:</span>
                            <span>{m.density_edge != null ? Number(m.density_edge).toFixed(2) : '—'}</span>
                            <span className="text-zinc-500">E[total] − line:</span>
                            <span>{m.expected_total_minus_line != null ? `+${Number(m.expected_total_minus_line).toFixed(2)}` : '—'}</span>
                          </div>
                          {/* Cross-check row */}
                          <div className="border-t border-zinc-800/50 mt-1 pt-1 flex flex-wrap items-center gap-2">
                            <span className="text-zinc-400 font-semibold">⚖ Cross-check vs MC decomposition:</span>
                            <span className="text-zinc-500">FS P(Over):</span>
                            <span className="text-emerald-300">{m.over_probability != null ? `${(m.over_probability * 100).toFixed(2)}%` : '—'}</span>
                            <span className="text-zinc-500">↔ decomp P(Over):</span>
                            <span className="text-cyan-300">{fs.decomposition_total_over_probability != null ? `${(fs.decomposition_total_over_probability * 100).toFixed(2)}%` : '—'}</span>
                            <span className="text-zinc-500">Δ:</span>
                            <span
                              data-testid="fs-vs-dd-delta"
                              className={flagged ? 'text-amber-300 font-bold' : 'text-emerald-300'}
                            >
                              {delta == null ? '—' : `${(delta * 100).toFixed(2)}pp`}
                            </span>
                          </div>
                          {fs.reasons?.length > 0 && (
                            <div className="text-[9px] text-zinc-500 italic mt-0.5">
                              reasons: {fs.reasons.join(' · ')}
                            </div>
                          )}
                        </div>
                      );
                    })()}
                    {/* Calibration integrity */}
                    <div className="mb-2 text-[9px] flex flex-wrap items-center gap-2 border-b border-zinc-800/50 pb-1.5">
                      <span className="text-zinc-400 font-semibold">⚖ Calibration:</span>
                      <span className="text-zinc-500">p_raw:</span>
                      <span data-testid="calib-p-raw">{fmtP(ci.p_raw)}</span>
                      <span className="text-zinc-500">→ p_calibrated:</span>
                      <span data-testid="calib-p-cal">{fmtP(ci.p_calibrated)}</span>
                      <span className="text-zinc-500">delta:</span>
                      <span
                        data-testid="calib-delta"
                        className={(ci.calibration_delta != null && Math.abs(ci.calibration_delta) > ci.drift_threshold) ? 'text-amber-300 font-bold' : 'text-emerald-300'}
                      >
                        {ci.calibration_delta == null ? '—' : `${(ci.calibration_delta * 100).toFixed(2)}pp`}
                      </span>
                      <span className="text-zinc-500">method:</span>
                      <span className="text-zinc-300">{ci.calibration_method || 'identity'}</span>
                    </div>
                    {/* Two side-by-side views */}
                    <div className="flex flex-col md:flex-row gap-2">
                      <ViewBlock title="AUTHORITATIVE (bookmaker)" view={av} verdict={orp.authoritative_verdict} isAuthoritative={true}/>
                      <ViewBlock title={`PRACTICAL BASELINE (O${fmtN(orp.practical_baseline_line)})`} view={pv} verdict={orp.practical_verdict} isAuthoritative={false}/>
                    </div>
                    <div className="text-[9px] italic text-zinc-500 mt-1.5">
                      {orp.explanation}
                    </div>
                  </div>
                );
              })()}

              {/* 2026-05-20 — FINAL MATCH SHAPE RESOLVER (F3 shadow) */}
              {prediction.comparison?.match_shape_resolver && (() => {
                const r = prediction.comparison.match_shape_resolver;
                if (r.error) {
                  return (
                    <div className="rounded-sm px-2 py-1.5 text-[10px] border bg-rose-950/30 border-rose-800 text-rose-100" style={mono} data-testid="resolver-error">
                      ▦ MATCH SHAPE RESOLVER — error: {r.error}
                    </div>
                  );
                }
                const inp = r.inputs || {};
                const w = r.weights || {};
                const h2h = r.direct_h2h_shape || {};
                const tags = r.integrity_tags || [];
                const isLive = r.audit_only_so_far === false;
                const has5setMove = inp.historical_structure_5set != null && r.resolved_5set_prob != null
                  && Math.abs(r.resolved_5set_prob - inp.historical_structure_5set) > 0.05;
                const palette = tags.includes('RESOLVER_SAFETY_CLAMP_APPLIED') || tags.includes('PARTIAL_REFRESH_BLOCK')
                  ? { bg: 'bg-rose-950/30', border: 'border-rose-800', text: 'text-rose-100' }
                  : (tags.includes('H2H_LONG_RAISES_RESOLVED_5SET') || tags.includes('H2H_SHORT_LOWERS_RESOLVED_5SET'))
                  ? { bg: 'bg-violet-950/30', border: 'border-violet-700', text: 'text-violet-100' }
                  : tags.includes('H2H_INSUFFICIENT_SAMPLE')
                  ? { bg: 'bg-amber-950/30', border: 'border-amber-700', text: 'text-amber-100' }
                  : { bg: 'bg-emerald-950/20', border: 'border-emerald-900', text: 'text-emerald-100' };
                const fmtPct = (v) => v == null ? '—' : (v * 100).toFixed(2) + '%';
                const fmtPp = (v) => v == null ? '—' : ((v > 0 ? '+' : '') + (v * 100).toFixed(2) + 'pp');
                const fmtN = (v) => v == null ? '—' : Number(v).toFixed(3);
                return (
                  <div data-testid="match-shape-resolver-card" className={`rounded-sm px-2 py-1.5 text-[10px] border ${palette.bg} ${palette.border} ${palette.text}`} style={mono}>
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="font-semibold">▦ FINAL MATCH SHAPE RESOLVER</span>
                      <span className={`px-1.5 py-0.5 rounded-sm text-[9px] font-semibold border ${isLive ? 'bg-emerald-900/40 text-emerald-100 border-emerald-700' : 'bg-zinc-800 text-zinc-300 border-zinc-700'}`}>
                        {isLive ? 'LIVE' : 'shadow audit'}
                      </span>
                      <span className="text-[9px] text-emerald-300 italic" data-testid="resolver-role-label">
                        live value used by O/U scorer
                      </span>
                      <span className="text-[9px] text-zinc-500">· data: {r.data_completeness}</span>
                      {tags.map((t) => (
                        <span key={t} data-testid={`resolver-tag-${t}`} className="px-1.5 py-0.5 rounded-sm text-[9px] font-semibold bg-violet-900/40 text-violet-100 border border-violet-700">
                          {t}
                        </span>
                      ))}
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-x-3 gap-y-0.5 text-[9px] border-t border-zinc-800/50 pt-1">
                      <span className="text-zinc-500">BO5 5-set:</span>
                      <span data-testid="res-bo5-5set">{fmtPct(inp.bo5_5set)}</span>
                      <span className="text-zinc-500">BO5 short:</span>
                      <span>{fmtPct(inp.bo5_short)}</span>
                      <span className="text-zinc-500">HS 5-set:</span>
                      <span data-testid="res-hs-5set">{fmtPct(inp.historical_structure_5set)}</span>
                      <span className="text-zinc-500">HS short:</span>
                      <span>{fmtPct(inp.historical_structure_short)}</span>
                      <span className="text-zinc-500">H2H 5-set:</span>
                      <span data-testid="res-h2h-5set">{fmtPct(inp.direct_h2h_5set)} <span className="text-zinc-600">(n={inp.direct_h2h_n}, {h2h.direct_h2h_mapping_label})</span></span>
                      <span className="text-zinc-500">H2H short:</span>
                      <span>{fmtPct(inp.direct_h2h_short)}</span>
                      <span className="text-emerald-400 font-bold">RESOLVED 5-set:</span>
                      <span data-testid="res-resolved-5set" className="font-bold text-emerald-300">
                        {fmtPct(r.resolved_5set_prob)}
                        {has5setMove && (
                          <span className="ml-1 text-[9px] text-amber-300">(Δ vs HS {fmtPp(r.resolved_5set_prob - inp.historical_structure_5set)})</span>
                        )}
                      </span>
                      <span className="text-emerald-400 font-bold">RESOLVED short:</span>
                      <span data-testid="res-resolved-short" className="font-bold text-emerald-300">{fmtPct(r.resolved_short_prob)}</span>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-x-3 gap-y-0.5 text-[9px] border-t border-zinc-800/50 mt-1 pt-1">
                      <span className="text-zinc-500">BO5 pts/set:</span><span>{fmtN(inp.bo5_pts_per_set)}</span>
                      <span className="text-zinc-500">H2H pts/set:</span><span>{fmtN(inp.direct_h2h_pts_per_set)}</span>
                      <span className="text-zinc-500">resolved pts/set:</span><span data-testid="res-resolved-pps" className="text-emerald-300 font-semibold">{fmtN(r.resolved_pts_per_set)}</span>
                      <span className="text-zinc-500">resolved E[sets]:</span><span>{fmtN(r.resolved_expected_sets)}</span>
                      <span className="text-zinc-500">resolved E[total]:</span><span data-testid="res-resolved-etot" className="text-emerald-300 font-semibold">{fmtN(r.resolved_expected_total)}</span>
                      <span className="text-zinc-500">H2H same-tour:</span>
                      <span>{h2h.direct_h2h_same_tournament_n ?? '—'} of {h2h.direct_h2h_n ?? '—'}</span>
                    </div>
                    {/* 2026-05-26 — H2H Duration Distribution + Profile Label */}
                    {(inp.direct_h2h_3set_count != null || inp.direct_h2h_4set_count != null) && (
                      <div className="mt-1 text-[9px] border-t border-zinc-800/50 pt-1" data-testid="res-h2h-duration">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="text-zinc-400 font-semibold">H2H duration:</span>
                          <span className="text-zinc-500">3-set:</span>
                          <span className="text-zinc-200" data-testid="res-h2h-3set">
                            {fmtPct(inp.direct_h2h_3set_rate)}
                            <span className="text-zinc-600"> (n={inp.direct_h2h_3set_count ?? '—'})</span>
                          </span>
                          <span className="text-zinc-500">·</span>
                          <span className="text-zinc-500">4-set:</span>
                          <span className="text-zinc-200" data-testid="res-h2h-4set">
                            {fmtPct(inp.direct_h2h_4set_rate)}
                            <span className="text-zinc-600"> (n={inp.direct_h2h_4set_count ?? '—'})</span>
                          </span>
                          <span className="text-zinc-500">·</span>
                          <span className="text-zinc-500">5-set:</span>
                          <span className="text-zinc-200" data-testid="res-h2h-5set-explicit">
                            {fmtPct(inp.direct_h2h_5set_rate_explicit ?? inp.direct_h2h_5set)}
                            <span className="text-zinc-600"> (n={inp.direct_h2h_5set_count ?? '—'})</span>
                          </span>
                          <span className="text-zinc-500">·</span>
                          <span className="text-zinc-500">avg sets:</span>
                          <span className="text-emerald-300 font-bold" data-testid="res-h2h-avg-sets">
                            {fmtN(inp.direct_h2h_avg_sets_per_match)}
                          </span>
                        </div>
                        {r.h2h_duration_profile?.label && (
                          <div className="mt-0.5 flex flex-wrap items-center gap-2">
                            <span className="text-zinc-500">profile:</span>
                            <span
                              data-testid={`res-h2h-profile-${r.h2h_duration_profile.label}`}
                              className={
                                r.h2h_duration_profile.label === 'H2H_TRUE_LONG_5SET_HEAVY'
                                  ? 'px-1.5 py-0.5 rounded-sm font-semibold bg-rose-900/40 text-rose-100 border border-rose-700'
                                  : r.h2h_duration_profile.label === 'H2H_TRUE_SHORT_3SET_HEAVY'
                                  ? 'px-1.5 py-0.5 rounded-sm font-semibold bg-emerald-900/40 text-emerald-100 border border-emerald-700'
                                  : r.h2h_duration_profile.label === 'H2H_FOUR_SET_HEAVY'
                                  ? 'px-1.5 py-0.5 rounded-sm font-semibold bg-amber-900/40 text-amber-100 border border-amber-700'
                                  : 'px-1.5 py-0.5 rounded-sm font-semibold bg-zinc-800/40 text-zinc-400 border border-zinc-700'
                              }
                            >
                              {r.h2h_duration_profile.label}
                            </span>
                            <span className="text-zinc-500 italic">
                              {r.h2h_duration_profile.rationale}
                            </span>
                          </div>
                        )}
                      </div>
                    )}
                    {/* 2026-05-26 — 4-set bucket Over/Under context at selected line */}
                    {r.h2h_4set_bucket_at_selected_line && r.h2h_4set_bucket_at_selected_line.h2h_4set_n > 0 && (
                      <div className="mt-1 text-[9px] border-t border-zinc-800/50 pt-1" data-testid="res-h2h-4set-bucket">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="text-zinc-400 font-semibold">4-set bucket @ line {inp.selected_ou_line ?? '—'}:</span>
                          <span className="text-zinc-500">avg pts (4-set):</span>
                          <span className="text-zinc-200">{fmtN(r.h2h_4set_bucket_at_selected_line.h2h_4set_avg_total_points)}</span>
                          <span className="text-zinc-500">·</span>
                          <span className="text-zinc-500">need pts/set:</span>
                          <span className="text-zinc-200">{fmtN(r.h2h_4set_bucket_at_selected_line.required_pts_per_set_if_4sets)}</span>
                          <span className="text-zinc-500">·</span>
                          <span className="text-zinc-500">Over rate:</span>
                          <span
                            className={(r.h2h_4set_bucket_at_selected_line.h2h_4set_over_rate_at_selected_line ?? 0) >= 0.5 ? 'text-emerald-300 font-semibold' : 'text-rose-300 font-semibold'}
                            data-testid="res-h2h-4set-over-rate"
                          >
                            {fmtPct(r.h2h_4set_bucket_at_selected_line.h2h_4set_over_rate_at_selected_line)}
                          </span>
                          <span className="text-zinc-500">·</span>
                          <span className="text-zinc-500">Under rate:</span>
                          <span
                            className={(r.h2h_4set_bucket_at_selected_line.h2h_4set_under_rate_at_selected_line ?? 0) >= 0.5 ? 'text-rose-300 font-semibold' : 'text-emerald-300 font-semibold'}
                            data-testid="res-h2h-4set-under-rate"
                          >
                            {fmtPct(r.h2h_4set_bucket_at_selected_line.h2h_4set_under_rate_at_selected_line)}
                          </span>
                        </div>
                        <div className="text-[9px] italic text-zinc-500 mt-0.5">
                          {r.h2h_4set_bucket_at_selected_line.explanation}
                        </div>
                      </div>
                    )}
                    {/* 2026-05-26 — Expected sets audit (sources feeding resolved_expected_sets) */}
                    {r.expected_sets_audit && (
                      <div className="mt-1 text-[9px] border-t border-zinc-800/50 pt-1" data-testid="res-exp-sets-audit">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="text-zinc-400 font-semibold">E[sets] sources:</span>
                          <span className="text-zinc-500">BO5:</span>
                          <span className="text-zinc-200">{fmtN(r.expected_sets_audit.bo5_expected_sets) || '—'} <span className="text-zinc-600">(w {(r.expected_sets_audit.w_bo5 * 100).toFixed(1)}%)</span></span>
                          <span className="text-zinc-500">·</span>
                          <span className="text-zinc-500">HS:</span>
                          <span className="text-zinc-200">{fmtN(r.expected_sets_audit.hs_expected_sets) || '—'} <span className="text-zinc-600">(w {(r.expected_sets_audit.w_hs * 100).toFixed(1)}%)</span></span>
                          <span className="text-zinc-500">·</span>
                          <span className="text-zinc-500">H2H avg sets:</span>
                          <span className="text-zinc-200">{fmtN(r.expected_sets_audit.h2h_avg_sets_per_match) || '—'} <span className="text-zinc-600">(w {(r.expected_sets_audit.w_h2h * 100).toFixed(1)}%)</span></span>
                          <span className="text-zinc-500">→</span>
                          <span className="text-emerald-300 font-bold" data-testid="res-resolved-esets-final">
                            resolved E[sets] = {fmtN(r.expected_sets_audit.resolved_expected_sets)}
                          </span>
                        </div>
                        <div className="text-[9px] italic text-zinc-500 mt-0.5">
                          {r.expected_sets_audit.formula}
                        </div>
                      </div>
                    )}
                    {/* resolved pts/set audit (2026-05-21 — user spec #2) */}
                    {r.resolved_pts_per_set_audit && (
                      <div className="mt-1 text-[9px] border-t border-zinc-800/50 pt-1 text-zinc-400" data-testid="res-pps-audit">
                        <div>
                          <b>resolved pts/set source:</b>{' '}
                          <span className={r.resolved_pts_per_set_audit.resolved_pts_source === 'direct_h2h_override' ? 'text-amber-300' : 'text-zinc-200'}>
                            {r.resolved_pts_per_set_audit.resolved_pts_source}
                          </span>
                          {' · model_pts_weight='}
                          <b className="text-zinc-200" data-testid="res-pps-model-w">{(r.resolved_pts_per_set_audit.model_pts_weight * 100).toFixed(1)}%</b>
                          {' · historical_pts_weight='}
                          <b className="text-zinc-200" data-testid="res-pps-hist-w">{(r.resolved_pts_per_set_audit.historical_pts_weight * 100).toFixed(1)}%</b>
                          {' · h2h_pts_weight='}
                          <b className="text-zinc-200" data-testid="res-pps-h2h-w">{(r.resolved_pts_per_set_audit.h2h_pts_weight * 100).toFixed(1)}%</b>
                        </div>
                        <div className="text-[9px] text-zinc-500 mt-0.5">
                          available inputs:{' '}
                          model_pts_available=<b className={r.resolved_pts_per_set_audit.model_pts_available ? 'text-emerald-300' : 'text-rose-300'}>{String(r.resolved_pts_per_set_audit.model_pts_available)}</b>
                          {' · '}
                          historical_pts_available=<b className={r.resolved_pts_per_set_audit.historical_pts_available ? 'text-emerald-300' : 'text-rose-300'}>{String(r.resolved_pts_per_set_audit.historical_pts_available)}</b>
                          {' · '}
                          h2h_pts_available=<b className={r.resolved_pts_per_set_audit.h2h_pts_available ? 'text-emerald-300' : 'text-rose-300'}>{String(r.resolved_pts_per_set_audit.h2h_pts_available)}</b>
                        </div>
                        {r.resolved_pts_per_set_audit.explainer && (
                          <div className="text-[9px] italic text-zinc-400 mt-0.5" data-testid="res-pps-explainer">
                            <b className="text-zinc-300">why:</b>{' '}
                            {r.resolved_pts_per_set_audit.explainer}
                          </div>
                        )}
                        <div className="text-[9px] italic text-zinc-500 mt-0.5">
                          formula: {r.resolved_pts_per_set_audit.resolved_pts_formula}
                        </div>
                        {(r.resolved_pts_per_set_audit.missing_inputs || []).length > 0 && (
                          <div className="text-[9px] italic text-amber-300/80">
                            missing inputs: {r.resolved_pts_per_set_audit.missing_inputs.join(', ')} → effective weights renormalised over available sources
                          </div>
                        )}
                      </div>
                    )}
                    <div className="mt-1 text-[9px] text-zinc-400 border-t border-zinc-800/50 pt-1" data-testid="res-weights">
                      <b>Blending</b> · w_BO5 = <b className="text-zinc-200">{(w.w_bo5 * 100).toFixed(1)}%</b>
                      · w_HS = <b className="text-zinc-200">{(w.w_hs * 100).toFixed(1)}%</b>
                      · w_H2H = <b className="text-zinc-200">{(w.w_h2h * 100).toFixed(1)}%</b>
                      {w.breakdown?.hard_gate_applied && (
                        <span className="ml-2 text-amber-300">(hard gate: n={w.breakdown.n}, mapping={w.breakdown.mapping_confidence})</span>
                      )}
                      {w.breakdown && !w.breakdown.hard_gate_applied && (
                        <span className="ml-2 text-zinc-500">
                          bonuses: {[
                            w.breakdown.same_tournament_bonus && 'same-tour',
                            w.breakdown.recency_bonus && 'recent',
                            w.breakdown.conflict_bonus && 'conflict',
                            w.breakdown.cap_applied && 'cap@55%',
                          ].filter(Boolean).join(', ') || 'none'}
                        </span>
                      )}
                    </div>
                    {!isLive && (
                      <div className="mt-1 text-[9px] italic text-zinc-500 border-t border-zinc-800/50 pt-1">
                        Shadow audit — resolver does NOT modify engine probabilities yet. Activate via MATCH_SHAPE_RESOLVER_LIVE env.
                      </div>
                    )}
                    {/* F5 LIVE — engine-level decision block */}
                    {r.live_decision && (
                      <div className="mt-1 text-[9px] border-t border-zinc-800/50 pt-1" data-testid="res-live-decision">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <span className="font-semibold text-emerald-400">▣ LIVE DECISION</span>
                          <span className={`px-1.5 py-0.5 rounded-sm text-[9px] font-semibold border ${r.live_decision.live_enabled ? 'bg-emerald-900/40 text-emerald-100 border-emerald-700' : 'bg-zinc-800 text-zinc-400 border-zinc-700'}`}>
                            {r.live_decision.live_enabled ? 'ENGINE ACTIVE' : 'kill-switch OFF'}
                          </span>
                          <span className="px-1.5 py-0.5 rounded-sm text-[9px] bg-zinc-800 text-zinc-300 border border-zinc-700">
                            H2H: {r.live_decision.h2h_strength}
                          </span>
                          <span className="text-zinc-500">w_resolved = <b className="text-zinc-200">{(r.live_decision.applied_w_resolved * 100).toFixed(0)}%</b></span>
                          {r.live_decision.hard_block_short_bias && (
                            <span data-testid="res-hard-block-short" className="px-1.5 py-0.5 rounded-sm text-[9px] font-bold bg-rose-900/40 text-rose-100 border border-rose-700">
                              H2H BLOCKS BO5 SHORT BIAS
                            </span>
                          )}
                          {(r.live_decision.live_tags || []).map((t) => (
                            <span key={t} data-testid={`live-tag-${t}`} className="px-1.5 py-0.5 rounded-sm text-[9px] bg-rose-900/30 text-rose-200 border border-rose-800">
                              {t}
                            </span>
                          ))}
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-x-3 gap-y-0.5">
                          <span className="text-zinc-500">resolved_5set_full:</span>
                          <span data-testid="res-resolved-5set-full" className="text-emerald-200 font-semibold">{fmtPct(r.live_decision.resolved_5set_full ?? r.resolved_5set_prob)}</span>
                          <span className="text-zinc-500">live_5set_used:</span>
                          <span data-testid="res-live-5set-used" className="text-emerald-300 font-bold">{fmtPct(r.live_decision.live_5set_used ?? r.live_decision.live_5set_prob)}</span>
                          <span className="text-zinc-500">safety_blend_weight:</span>
                          <span data-testid="res-safety-blend-weight" className="text-zinc-200">{((r.live_decision.safety_blend_weight ?? r.live_decision.applied_w_resolved) * 100).toFixed(0)}%</span>
                          <span className="text-zinc-500">live short:</span>
                          <span className="text-emerald-300">{fmtPct(r.live_decision.live_short_prob)}</span>
                          <span className="text-zinc-500">Δ resolved_full vs BO5:</span>
                          <span className={r.live_decision.delta_resolved_full_minus_bo5_pp > 0 ? 'text-emerald-300' : (r.live_decision.delta_resolved_full_minus_bo5_pp < 0 ? 'text-rose-300' : 'text-zinc-400')}>
                            {r.live_decision.delta_resolved_full_minus_bo5_pp == null ? '—' :
                              `${r.live_decision.delta_resolved_full_minus_bo5_pp > 0 ? '+' : ''}${r.live_decision.delta_resolved_full_minus_bo5_pp.toFixed(2)}pp`}
                          </span>
                          <span className="text-zinc-500">Δ live vs BO5:</span>
                          <span className={r.live_decision.live_5set_minus_bo5_pp > 0 ? 'text-emerald-300' : (r.live_decision.live_5set_minus_bo5_pp < 0 ? 'text-rose-300' : 'text-zinc-400')}>
                            {r.live_decision.live_5set_minus_bo5_pp == null ? '—' :
                              `${r.live_decision.live_5set_minus_bo5_pp > 0 ? '+' : ''}${r.live_decision.live_5set_minus_bo5_pp.toFixed(2)}pp`}
                          </span>
                          <span className="text-zinc-500">Δ live vs resolved_full:</span>
                          <span className="text-zinc-300">
                            {r.live_decision.delta_live_minus_resolved_full_pp == null ? '—' :
                              `${r.live_decision.delta_live_minus_resolved_full_pp > 0 ? '+' : ''}${r.live_decision.delta_live_minus_resolved_full_pp.toFixed(2)}pp`}
                          </span>
                          {r.live_decision.scoreline_rescaling?.applied && (
                            <>
                              <span className="text-zinc-500">scoreline rescale:</span>
                              <span className="text-zinc-300">
                                {(r.live_decision.scoreline_rescaling.capped_delta_pp > 0 ? '+' : '')}
                                {r.live_decision.scoreline_rescaling.capped_delta_pp}pp
                                {r.live_decision.scoreline_rescaling.capped_delta_pp !== r.live_decision.scoreline_rescaling.raw_delta_pp && (
                                  <span className="text-zinc-500"> (raw {r.live_decision.scoreline_rescaling.raw_delta_pp}pp, capped ±10pp)</span>
                                )}
                              </span>
                            </>
                          )}
                        </div>
                        {/* Why live differs from resolved_full (operator clarity) */}
                        {r.live_decision.reason_live_differs_from_resolved && (
                          <div className="mt-1 text-[9px] italic text-zinc-400 border-t border-zinc-900 pt-1" data-testid="res-reason-live-differs">
                            <b className="text-zinc-300">why live ≠ resolved_full:</b>{' '}
                            {r.live_decision.reason_live_differs_from_resolved}
                          </div>
                        )}
                        {(r.live_decision.block_reasons || []).length > 0 && (
                          <div className="mt-1 text-[9px] text-rose-200 italic">
                            {r.live_decision.block_reasons.join(' · ')}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* SetProfile Phase 2 — BEHAVIOR CORRECTION (audit-only) */}
              {prediction.comparison?.behavior_correction && (() => {
                const bc = prediction.comparison.behavior_correction;
                const raw = bc.raw_scoreline_probs || {};
                const adj = bc.adjusted_scoreline_probs || {};
                const rawTot = bc.raw_expected_total;
                const adjTot = bc.adjusted_expected_total;
                const raw5 = bc.raw_5set_prob;
                const adj5 = bc.adjusted_5set_prob;
                const fired = bc.rules_fired || [];
                const inv = bc.invariants || {};
                const baselineSource = bc.baseline_source || 'unknown';
                const integrityFlags = bc.integrity_flags || [];
                const schemaLabel = bc.scoreline_key_schema;
                const baselineMissing = !bc.adjusted_scoreline_probs;
                const baselineWarn = baselineMissing
                  || baselineSource === 'historical_structure_v25'
                  || baselineSource === 'fallback_uniform'
                  || baselineSource === 'missing'
                  || integrityFlags.includes('UNIFORM_SCORELINE_FALLBACK_DETECTED')
                  || integrityFlags.includes('UNRECOGNISED_SCORELINE_KEY_SCHEMA')
                  || integrityFlags.includes('MIXED_SCORELINE_KEY_SCHEMAS');
                const palette = baselineWarn
                  ? { bg: 'bg-amber-950/30', border: 'border-amber-700', text: 'text-amber-100', accent: 'text-amber-300/80' }
                  : fired.length > 0
                  ? { bg: 'bg-fuchsia-950/30', border: 'border-fuchsia-800', text: 'text-fuchsia-200', accent: 'text-fuchsia-400/70' }
                  : { bg: 'bg-zinc-900/30', border: 'border-zinc-800', text: 'text-zinc-300', accent: 'text-zinc-500' };
                const baselineChip = {
                  setscore_scoreline_distribution: { label: 'engine BO5', cls: 'bg-emerald-900/40 text-emerald-100 border-emerald-700' },
                  historical_structure_v25:        { label: 'HISTORICAL_v25 fallback', cls: 'bg-amber-900/40 text-amber-100 border-amber-700' },
                  fallback_uniform:                { label: 'UNIFORM FALLBACK', cls: 'bg-rose-900/40 text-rose-100 border-rose-700' },
                  missing:                         { label: 'BASELINE_UNAVAILABLE', cls: 'bg-rose-900/40 text-rose-100 border-rose-700' },
                }[baselineSource] || { label: baselineSource, cls: 'bg-zinc-800 text-zinc-100 border-zinc-700' };
                const fmt = (v) => (v == null ? '—' : Number(v).toFixed(4));
                const fmtPct = (v) => (v == null ? '—' : (Number(v) * 100).toFixed(2) + '%');
                return (
                  <div
                    data-testid="behavior-correction-card"
                    className={`rounded-sm px-2 py-1.5 text-[10px] border ${palette.bg} ${palette.border} ${palette.text}`}
                    style={mono}
                    title="Phase 2 audit-only: applies small (≤8% per cell) corrections to the scoreline distribution based on historical player behavior. Does NOT modify EV / Kelly / routing / Telegram / profile classification."
                  >
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold" data-testid="bc-title">
                        ▦ BEHAVIOR CORRECTION (Phase 2 · audit)
                      </span>
                      <span className="text-[9px] text-zinc-400 italic" data-testid="bc-role-label">
                        pre-resolver BO5 audit
                      </span>
                      <span
                        data-testid="bc-baseline-source"
                        className={`px-1.5 py-0.5 rounded-sm text-[9px] font-semibold border ${baselineChip.cls}`}
                        title={`scoreline_key_schema=${schemaLabel || 'n/a'}`}
                      >
                        {baselineChip.label}
                      </span>
                      {!baselineMissing && fired.length === 0 && (
                        <span className={palette.accent} data-testid="bc-noop">
                          no rules fired
                        </span>
                      )}
                      {fired.map((r) => (
                        <span
                          key={r}
                          data-testid={`bc-rule-${r}`}
                          className="px-1.5 py-0.5 rounded-sm text-[9px] font-semibold bg-fuchsia-900/40 text-fuchsia-100 border border-fuchsia-800"
                        >
                          {r}
                        </span>
                      ))}
                      {inv.ok === false && (
                        <span className="text-rose-400 font-bold" data-testid="bc-invariant">
                          ⚠ {inv.reason}
                        </span>
                      )}
                    </div>
                    {baselineMissing && (
                      <div className="mt-1 text-[10px] text-rose-300 font-semibold" data-testid="bc-baseline-unavailable">
                        ⚠ scoreline baseline unavailable — no model-derived 5-set probability to display
                      </div>
                    )}
                    {!baselineMissing && baselineSource === 'historical_structure_v25' && (
                      <div className="mt-1 text-[10px] text-amber-200" data-testid="bc-historical-fallback">
                        ⚠ Using Historical Structure v25 fallback — 5-set probability is synthesised from history, not from the engine BO5.
                      </div>
                    )}
                    {integrityFlags.includes('UNIFORM_SCORELINE_FALLBACK_DETECTED') && (
                      <div className="mt-1 text-[10px] text-rose-200 font-semibold" data-testid="bc-uniform-warning">
                        ⚠ UNIFORM_SCORELINE_FALLBACK_DETECTED — 5-set probability is not model-derived.
                      </div>
                    )}
                    {integrityFlags.includes('UNRECOGNISED_SCORELINE_KEY_SCHEMA') && (
                      <div className="mt-1 text-[10px] text-rose-200 font-semibold" data-testid="bc-schema-warning">
                        ⚠ UNRECOGNISED_SCORELINE_KEY_SCHEMA — caller passed unknown keys.
                      </div>
                    )}
                    {integrityFlags.includes('MIXED_SCORELINE_KEY_SCHEMAS') && (
                      <div className="mt-1 text-[10px] text-amber-300" data-testid="bc-mixed-warning">
                        ⚠ MIXED_SCORELINE_KEY_SCHEMAS — caller mixed `p1_3_0` and `3_0_p1` key formats.
                      </div>
                    )}
                    {!baselineMissing && (
                    <div className={`grid grid-cols-2 gap-x-3 gap-y-0.5 mt-1 ${palette.accent}`}>
                      <span data-testid="bc-raw-5set" title="P(match goes 5 sets) = P(3-2 P1) + P(3-2 P2) — directly comparable with Historical Structure combined 5-set.">
                        raw 5-set <span className="text-[9px] text-zinc-500">(combined P1+P2)</span>: <span className={palette.text}>{fmtPct(raw5)}</span>
                      </span>
                      <span data-testid="bc-adj-5set" title="adjusted P(match goes 5 sets) = adj P(3-2 P1) + adj P(3-2 P2).">
                        adj 5-set <span className="text-[9px] text-zinc-500">(combined P1+P2)</span>: <span className={palette.text}>{fmtPct(adj5)}</span>
                        {raw5 != null && adj5 != null && Math.abs(adj5 - raw5) > 0.001 && (
                          <span className="ml-1 text-[9px] text-amber-300">
                            ({adj5 > raw5 ? '+' : ''}{((adj5 - raw5) * 100).toFixed(2)}pp)
                          </span>
                        )}
                      </span>
                      <span data-testid="bc-raw-et">
                        raw E[total] <span className="text-[9px] text-zinc-500">(scoreline-derived)</span>: <span className={palette.text}>{fmt(rawTot)}</span>
                      </span>
                      <span data-testid="bc-adj-et">
                        adj E[total] <span className="text-[9px] text-zinc-500">(scoreline-derived)</span>: <span className={palette.text}>{fmt(adjTot)}</span>
                        {rawTot != null && adjTot != null && Math.abs(adjTot - rawTot) > 0.05 && (
                          <span className="ml-1 text-[9px] text-amber-300">
                            ({adjTot > rawTot ? '+' : ''}{(adjTot - rawTot).toFixed(2)})
                          </span>
                        )}
                      </span>
                    </div>
                    )}
                    {!baselineMissing && bc.raw_5set_breakdown && (
                      <div
                        data-testid="bc-side-breakdown"
                        className="mt-1 grid grid-cols-2 gap-x-3 gap-y-0.5 text-[9px] text-zinc-400 border-t border-zinc-800/70 pt-1"
                        title="Per-side mass behind the combined aggregates. Use this to spot asymmetry between P1 and P2."
                      >
                        <span data-testid="bc-p1-3-2">
                          P1 3-2: <span className="text-zinc-200">{fmtPct(bc.raw_5set_breakdown.p1_only)}</span>
                        </span>
                        <span data-testid="bc-p2-3-2">
                          P2 3-2: <span className="text-zinc-200">{fmtPct(bc.raw_5set_breakdown.p2_only)}</span>
                        </span>
                        {bc.raw_short_breakdown && (
                          <>
                            <span data-testid="bc-p1-short">
                              P1 short (3-0+3-1): <span className="text-zinc-200">{fmtPct(bc.raw_short_breakdown.p1_only)}</span>
                            </span>
                            <span data-testid="bc-p2-short">
                              P2 short (3-0+3-1): <span className="text-zinc-200">{fmtPct(bc.raw_short_breakdown.p2_only)}</span>
                            </span>
                          </>
                        )}
                      </div>
                    )}
                    {!baselineMissing && bc.hs_combined_5set != null && bc.bo5_combined_5set != null && (
                      <div
                        data-testid="bc-hs-comparison"
                        className={`mt-1 text-[9px] border-t pt-1 ${
                          integrityFlags.includes('BO5_HS_5SET_MISMATCH')
                            ? 'border-rose-900/70 text-rose-200'
                            : 'border-zinc-800/70 text-zinc-400'
                        }`}
                        title={bc.scope_definitions?.scope_note || 'BO5 5-set is combined P1+P2 and directly comparable with Historical Structure combined 5-set.'}
                      >
                        BO5 combined 5-set: <span className="text-zinc-200 font-semibold">{fmtPct(bc.bo5_combined_5set)}</span>
                        <span className="mx-1">·</span>
                        HS combined 5-set: <span className="text-zinc-200 font-semibold">{fmtPct(bc.hs_combined_5set)}</span>
                        {bc.bo5_hs_5set_delta_pp != null && (
                          <span className={`ml-1 ${Math.abs(bc.bo5_hs_5set_delta_pp) > 15 ? 'text-rose-300 font-bold' : Math.abs(bc.bo5_hs_5set_delta_pp) > 5 ? 'text-amber-300' : 'text-emerald-300'}`}>
                            (Δ {bc.bo5_hs_5set_delta_pp > 0 ? '+' : ''}{Number(bc.bo5_hs_5set_delta_pp).toFixed(2)}pp)
                          </span>
                        )}
                      </div>
                    )}
                    {integrityFlags.includes('BO5_HS_5SET_MISMATCH') && (
                      <div className="mt-1 text-[10px] text-rose-200 font-semibold" data-testid="bc-5set-mismatch-warning">
                        ⚠ BO5_HS_5SET_MISMATCH — engine BO5 disagrees with Historical Structure by &gt;15pp.
                      </div>
                    )}
                    {!baselineMissing && bc.scope_definitions?.scope_note && (
                      <div className="mt-1 text-[9px] italic text-zinc-500" data-testid="bc-scope-note">
                        {bc.scope_definitions.scope_note} (scope = {bc.scoreline_scope || 'unconditional_match_combined'})
                      </div>
                    )}
                    {!baselineMissing && bc.engine_bo5_expected_total != null && (
                      <div
                        data-testid="bc-engine-bo5-comparison"
                        className="mt-1 text-[9px] text-zinc-400 border-t border-zinc-800/70 pt-1"
                        title="The BC raw_expected_total is derived from the scoreline distribution × hardcoded TT averages (3-0≈58pts, 3-1≈78pts, 3-2≈97pts). The engine BO5 expected_total is the Monte Carlo mean from real per-set point sampling. They can differ by ±3pts when player tightness is skewed toward stomps or close-set duels."
                      >
                        engine BO5 E[total] (MC sampled):{' '}
                        <span className="text-zinc-200 font-semibold">{fmt(bc.engine_bo5_expected_total)}</span>
                        {bc.engine_bo5_vs_scoreline_delta != null && (
                          <span className={`ml-1 ${Math.abs(bc.engine_bo5_vs_scoreline_delta) > 1.5 ? 'text-amber-300' : 'text-zinc-500'}`}>
                            (Δ vs scoreline: {bc.engine_bo5_vs_scoreline_delta > 0 ? '+' : ''}{Number(bc.engine_bo5_vs_scoreline_delta).toFixed(2)})
                          </span>
                        )}
                        <span className="ml-1 text-zinc-600">· method = {bc.expected_total_method || 'scoreline_avg_hardcoded'}</span>
                      </div>
                    )}
                    {!baselineMissing && bc.reasons && bc.reasons.length > 0 && (
                      <div className={`mt-1 ${palette.accent} text-[9px]`} data-testid="bc-reasons">
                        {bc.reasons.map((r, i) => (
                          <div key={i}>· {r}</div>
                        ))}
                      </div>
                    )}
                    {!baselineMissing && fired.length > 0 && (
                      <div className="mt-1 grid grid-cols-3 gap-x-2 text-[9px] text-fuchsia-300/80 border-t border-fuchsia-900/70 pt-1" title="All rows show COMBINED P1+P2 mass for each scoreline length.">
                        <span>3-0 (combined): raw {fmtPct(raw.p1_3_0 + raw.p2_3_0)} → adj {fmtPct(adj.p1_3_0 + adj.p2_3_0)}</span>
                        <span>3-1 (combined): raw {fmtPct(raw.p1_3_1 + raw.p2_3_1)} → adj {fmtPct(adj.p1_3_1 + adj.p2_3_1)}</span>
                        <span>3-2 (combined): raw {fmtPct(raw5)} → adj {fmtPct(adj5)}</span>
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* SetProfile Phase 1 — HISTORICAL STRUCTURE trace (audit-only) */}
              {prediction.comparison?.behavior_features?.p1 && (() => {
                const bf = prediction.comparison.behavior_features;
                const p1w = (bf.p1?.windows?.['25']) || {};
                const p2w = (bf.p2?.windows?.['25']) || {};
                const m25 = bf.matchup_25 || {};
                const m10 = bf.matchup_10 || {};
                const buckets = bf.audit_buckets || [];
                const fmt = (v) => (v == null ? '—' : (v * 100).toFixed(1) + '%');
                return (
                  <div
                    data-testid="historical-structure-card"
                    className="rounded-sm px-2 py-1.5 text-[10px] border bg-slate-950/40 border-slate-800 text-slate-200"
                    style={mono}
                    title="Audit-only: minimal structural behavior features (5-set tendency, short tendency, close-set density) over the last 10/25 settled matches per player. Does NOT modify EV / Kelly / routing / profile classification."
                  >
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-slate-100" data-testid="hs-title">
                        ▦ HISTORICAL STRUCTURE [w25]
                      </span>
                      <span className="text-slate-400" data-testid="hs-samples">
                        P1 n={p1w.sample_size ?? 0} · P2 n={p2w.sample_size ?? 0}
                      </span>
                      <span className="text-slate-500 text-[9px]">audit-only</span>
                    </div>
                    <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 mt-1 text-slate-400">
                      <span data-testid="hs-p1-5set">P1 5-set: <span className="text-slate-100">{fmt(p1w.recent_5set_rate)}</span></span>
                      <span data-testid="hs-p2-5set">P2 5-set: <span className="text-slate-100">{fmt(p2w.recent_5set_rate)}</span></span>
                      <span data-testid="hs-p1-short">P1 short: <span className="text-slate-100">{fmt(p1w.recent_short_rate)}</span></span>
                      <span data-testid="hs-p2-short">P2 short: <span className="text-slate-100">{fmt(p2w.recent_short_rate)}</span></span>
                      <span data-testid="hs-p1-close">P1 close-set: <span className="text-slate-100">{fmt(p1w.close_set_rate)}</span></span>
                      <span data-testid="hs-p2-close">P2 close-set: <span className="text-slate-100">{fmt(p2w.close_set_rate)}</span></span>
                    </div>
                    <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1 text-slate-400 border-t border-slate-800 pt-1">
                      <span data-testid="hs-combined-5set">
                        combined 5-set: <span className="text-slate-100">{fmt(m25.combined_5set_support)}</span>
                        <span className="text-slate-600 ml-1">(w10 {fmt(m10.combined_5set_support)})</span>
                      </span>
                      <span data-testid="hs-combined-short">
                        combined short: <span className="text-slate-100">{fmt(m25.combined_short_support)}</span>
                      </span>
                      <span data-testid="hs-combined-density">
                        close-set density: <span className="text-slate-100">{fmt(m25.combined_close_set_density)}</span>
                      </span>
                    </div>
                    {buckets.length > 0 && (
                      <div className="mt-1 flex flex-wrap gap-1" data-testid="hs-audit-buckets">
                        {buckets.map((b) => (
                          <span
                            key={b}
                            className="px-1.5 py-0.5 rounded-sm text-[9px] font-semibold bg-amber-900/40 text-amber-200 border border-amber-800"
                            data-testid={`hs-bucket-${b}`}
                          >
                            ⚑ {b}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Alignment Index — audit-only consensus quality card */}
              {prediction.comparison?.alignment_audit?.tier && (() => {
                const a = prediction.comparison.alignment_audit;
                const palette = {
                  STRONG: { bg: 'bg-emerald-950/30', border: 'border-emerald-800', text: 'text-emerald-300', accent: 'text-emerald-400/70', label: 'STRONG' },
                  GOOD: { bg: 'bg-teal-950/30', border: 'border-teal-800', text: 'text-teal-300', accent: 'text-teal-400/70', label: 'GOOD' },
                  WARNING: { bg: 'bg-yellow-950/30', border: 'border-yellow-800', text: 'text-yellow-300', accent: 'text-yellow-400/70', label: 'WARNING' },
                  DANGER: { bg: 'bg-orange-950/30', border: 'border-orange-800', text: 'text-orange-300', accent: 'text-orange-400/70', label: 'DANGER' },
                  SPLIT_BRAIN: { bg: 'bg-rose-950/30', border: 'border-rose-800', text: 'text-rose-300', accent: 'text-rose-400/70', label: 'SPLIT-BRAIN' },
                }[a.tier] || { bg: 'bg-zinc-900/30', border: 'border-zinc-800', text: 'text-zinc-300', accent: 'text-zinc-400/70', label: a.tier };
                return (
                  <div
                    data-testid="alignment-index-card"
                    className={`rounded-sm px-2 py-1.5 text-[10px] border ${palette.bg} ${palette.border} ${palette.text}`}
                    style={mono}
                    title="Audit only — consensus quality across SetScore vs Pascal / MW_v2 / LW / Market. Does not affect EV, Kelly, routing or alerts."
                  >
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold" data-testid="alignment-tier">
                        ◎ CONSENSUS ALIGNMENT: {palette.label}
                      </span>
                      <span className={palette.accent} data-testid="alignment-gap">
                        gap: {a.alignment_gap_pp?.toFixed(1)}pp
                      </span>
                      <span className={palette.accent} data-testid="alignment-direction">
                        {a.direction === 'opposed' ? '↔ opposed' : a.direction === 'aligned' ? '→ aligned' : '? unknown'}
                      </span>
                      <span className={palette.accent} data-testid="alignment-support">
                        support: {a.engines_supporting_setscore} vs {a.engines_against_setscore}
                      </span>
                      {a.consensus_mean_p1 != null && a.setscore_p1 != null && (
                        <span className={palette.accent}>
                          SS {(a.setscore_p1 * 100).toFixed(1)}% / cons {(a.consensus_mean_p1 * 100).toFixed(1)}%
                        </span>
                      )}
                    </div>
                    <div className={`${palette.accent} mt-0.5`}>
                      audit-only · no impact on EV / Kelly / routing
                    </div>
                  </div>
                );
              })()}

              {/* MW-Fragility → Switch to O/U diagnostic */}
              {prediction.comparison?.mw_fragility_guard?.tagged && (
                <div
                  data-testid="mw-fragility-guard-badge"
                  className="rounded-sm px-2 py-1.5 text-[10px] border
                    bg-amber-950/30 border-amber-800 text-amber-300"
                  style={mono}
                  title={(prediction.comparison.mw_fragility_guard.reasons || []).join(' · ')}
                >
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold">
                      ⚡ {prediction.comparison.mw_fragility_guard.tag}
                    </span>
                    <span className="text-amber-400/70">
                      penalty: {((prediction.comparison.mw_fragility_guard.confidence_penalty ?? 0) * 100).toFixed(0)}pp
                    </span>
                    <span className="text-amber-400/70">
                      long-match: {((prediction.comparison.mw_fragility_guard.long_set_prob ?? 0) * 100).toFixed(1)}%
                    </span>
                    <span className="text-amber-400/70">
                      E[total] {prediction.comparison.mw_fragility_guard.expected_total?.toFixed(1) ?? '—'}
                      {' vs '}ou line {prediction.comparison.mw_fragility_guard.reference_ou_line ?? '—'}
                    </span>
                  </div>
                  <div className="text-amber-400/60 mt-0.5">
                    {prediction.comparison.mw_fragility_guard.ui_explanation}
                  </div>
                </div>
              )}

              {/* MW_NONE_LEAK_GUARD: audit-only diagnostic for MW
                  picks with no structural support (mw/none pattern,
                  historical ROI -25.5%). Renders BEFORE FAST_MATCH_RISK
                  so the operator sees it prominently when it fires.
                  Persistence handled in `_build_comparison`. */}
              {prediction.comparison?.mw_none_leak_guard?.tagged && (
                <div
                  data-testid="mw-none-leak-guard-badge"
                  className={`rounded-sm px-2 py-1.5 text-[10px] border ${
                    prediction.comparison.mw_none_leak_guard.ui_impact?.ui_severity === 'RED'
                      ? 'bg-rose-950/40 border-rose-700 text-rose-200'
                      : 'bg-amber-950/40 border-amber-700 text-amber-200'
                  }`}
                  style={mono}
                  title={(prediction.comparison.mw_none_leak_guard.reasons || []).join(' · ')}
                >
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold">⚠ MW_NONE_LEAK_GUARD</span>
                    <span
                      data-testid="mw-none-leak-severity"
                      className={`px-1.5 py-0.5 rounded-sm text-[9px] font-bold tracking-wider ${
                        prediction.comparison.mw_none_leak_guard.ui_impact?.ui_severity === 'RED'
                          ? 'bg-rose-700/50 text-rose-100'
                          : 'bg-amber-700/50 text-amber-100'
                      }`}
                    >
                      {prediction.comparison.mw_none_leak_guard.ui_impact?.ui_severity || 'WARN'}
                      {' · '}
                      {prediction.comparison.mw_none_leak_guard.ui_impact?.action}
                    </span>
                    <span className="text-amber-300/80">
                      penalty: {((prediction.comparison.mw_none_leak_guard.ui_impact?.confidence_penalty ?? 0) * 100).toFixed(0)}pp
                    </span>
                    <span className="text-amber-300/80">
                      risk: {((prediction.comparison.mw_none_leak_guard.risk_score ?? 0) * 100).toFixed(0)}%
                    </span>
                    <span className="text-amber-300/60">
                      pattern: {prediction.comparison.mw_none_leak_guard.pattern_key}
                    </span>
                  </div>
                  <div className="mt-1 text-zinc-300/90">
                    Historical leak detected: MW pick without structural support.
                    Pattern <span className="font-semibold">mw/none</span> ha ROI
                    negativo nelle stats recenti. Richiede EV / model agreement
                    più forti prima di inviare.
                  </div>
                  {(prediction.comparison.mw_none_leak_guard.reasons || []).length > 0 && (
                    <ul className="mt-1 ml-3 list-disc text-amber-300/70">
                      {(prediction.comparison.mw_none_leak_guard.reasons || []).slice(0, 6).map((r, i) => (
                        <li key={i}>{r}</li>
                      ))}
                    </ul>
                  )}
                </div>
              )}

              {/* FAST_MATCH_RISK — short-WIN profile (audit only).
                  v2 (2026-05-05): trigger uses fast_short_win_max
                  (3-0 + 3-1 per side) ≥ 0.38 with severity escalation
                  (STRONG when max ≥ 0.40 AND Over ≤ 0.47). Persists
                  to `tt_setscore_predictions`. NO pick generation. */}
              {prediction.comparison?.fast_match_risk_guard?.tagged && (
                <div
                  data-testid="fast-match-risk-guard-badge"
                  className={`rounded-sm px-2 py-1.5 text-[10px] border ${
                    prediction.comparison.fast_match_risk_guard.severity === 'STRONG'
                      ? 'bg-red-950/40 border-red-700 text-red-300'
                      : 'bg-orange-950/30 border-orange-800 text-orange-300'
                  }`}
                  style={mono}
                  title={prediction.comparison.fast_match_risk_guard.reason}
                >
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold">
                      ⚡ FAST_MATCH_RISK
                      {prediction.comparison.fast_match_risk_guard.severity === 'STRONG' && (
                        <span
                          data-testid="fast-match-risk-severity-strong"
                          className="ml-2 px-1.5 py-0.5 rounded-sm bg-red-700/50 text-red-100 text-[9px] font-bold tracking-wider"
                        >
                          STRONG
                        </span>
                      )}
                      {prediction.comparison.fast_match_risk_guard.severity === 'MODERATE' && (
                        <span
                          data-testid="fast-match-risk-severity-moderate"
                          className="ml-2 px-1.5 py-0.5 rounded-sm bg-orange-700/40 text-orange-100 text-[9px] font-bold tracking-wider"
                        >
                          MODERATE
                        </span>
                      )}
                    </span>
                    <span className="opacity-70">
                      Short-win profile detected.
                    </span>
                  </div>
                  <div className="mt-1 grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-0.5 opacity-90">
                    <span>
                      <span className="opacity-70">Short-win risk:</span>{' '}
                      P1 <b>{((prediction.comparison.fast_match_risk_guard.short_win_p1 ?? 0) * 100).toFixed(1)}%</b>
                      {' / '}P2 <b>{((prediction.comparison.fast_match_risk_guard.short_win_p2 ?? 0) * 100).toFixed(1)}%</b>
                    </span>
                    <span>
                      <span className="opacity-70">3-0 risk:</span>{' '}
                      P1 <b>{((prediction.comparison.fast_match_risk_guard.p1_3_0_prob ?? 0) * 100).toFixed(1)}%</b>
                      {' / '}P2 <b>{((prediction.comparison.fast_match_risk_guard.p2_3_0_prob ?? 0) * 100).toFixed(1)}%</b>
                    </span>
                    <span>
                      <span className="opacity-70">3-2 drag:</span>{' '}
                      P1 <b>{((prediction.comparison.fast_match_risk_guard.p1_3_2_prob ?? 0) * 100).toFixed(1)}%</b>
                      {' / '}P2 <b>{((prediction.comparison.fast_match_risk_guard.p2_3_2_prob ?? 0) * 100).toFixed(1)}%</b>
                    </span>
                    <span>
                      <span className="opacity-70">
                        OU pressure @ selected line:{' '}
                        <b className="text-zinc-200">
                          {prediction.comparison.fast_match_risk_guard.selected_ou_line
                            ?? prediction.comparison.fast_match_risk_guard.ou_line_used
                            ?? '—'}
                        </b>
                        {prediction.comparison.fast_match_risk_guard.selected_ou_line_source && (
                          <span
                            data-testid="fmr-line-source"
                            className={`ml-1 text-[9px] px-1 py-0.5 rounded-sm border ${
                              prediction.comparison.fast_match_risk_guard.selected_ou_line_source === 'BOOK'
                                ? 'bg-emerald-900/40 text-emerald-200 border-emerald-700'
                                : prediction.comparison.fast_match_risk_guard.selected_ou_line_source === 'MARKET_MAIN'
                                ? 'bg-sky-900/40 text-sky-200 border-sky-700'
                                : prediction.comparison.fast_match_risk_guard.selected_ou_line_source === 'LEAGUE_DEFAULT'
                                ? 'bg-amber-900/40 text-amber-200 border-amber-700'
                                : 'bg-rose-900/40 text-rose-200 border-rose-700'
                            }`}
                          >
                            {prediction.comparison.fast_match_risk_guard.selected_ou_line_source}
                          </span>
                        )}
                        :
                      </span>{' '}
                      Over <b>{((prediction.comparison.fast_match_risk_guard.over_probability_at_selected_line
                                ?? prediction.comparison.fast_match_risk_guard.over_prob ?? 0) * 100).toFixed(1)}%</b>
                      {' / '}Under <b>{((prediction.comparison.fast_match_risk_guard.under_probability_at_selected_line
                                ?? prediction.comparison.fast_match_risk_guard.under_prob ?? 0) * 100).toFixed(1)}%</b>
                    </span>
                    {prediction.comparison.fast_match_risk_guard.nearest_book_line != null
                      && prediction.comparison.fast_match_risk_guard.nearest_book_line
                          !== prediction.comparison.fast_match_risk_guard.selected_ou_line && (
                      <span className="sm:col-span-2 text-[10px] opacity-80">
                        nearest book line: <b>{prediction.comparison.fast_match_risk_guard.nearest_book_line}</b>
                        {prediction.comparison.fast_match_risk_guard.engine_ou_line_used != null
                          && prediction.comparison.fast_match_risk_guard.engine_ou_line_used
                              !== prediction.comparison.fast_match_risk_guard.selected_ou_line && (
                          <> · engine curve read at <b>{prediction.comparison.fast_match_risk_guard.engine_ou_line_used}</b></>
                        )}
                      </span>
                    )}
                    {prediction.comparison.fast_match_risk_guard.using_fallback
                      && prediction.comparison.fast_match_risk_guard.ou_line_warning && (
                      <span
                        data-testid="fmr-fallback-warning"
                        className="sm:col-span-2 text-[10px] text-amber-200 bg-amber-950/40 border border-amber-800 px-1.5 py-0.5 rounded-sm"
                      >
                        ⚠ {prediction.comparison.fast_match_risk_guard.ou_line_warning}
                      </span>
                    )}
                    {prediction.comparison.fast_match_risk_guard.expected_total != null && (
                      <span className="sm:col-span-2">
                        <span className="opacity-70">Expected total:</span>{' '}
                        <b>{prediction.comparison.fast_match_risk_guard.expected_total}</b>
                        {prediction.comparison.fast_match_risk_guard.std_total != null && (
                          <> ± {prediction.comparison.fast_match_risk_guard.std_total}</>
                        )}
                      </span>
                    )}
                    <span className="sm:col-span-2">
                      <span className="opacity-70">Severity:</span>{' '}
                      <b>{prediction.comparison.fast_match_risk_guard.severity ?? '—'}</b>
                    </span>
                  </div>
                  <div className="mt-1 italic opacity-70">
                    Short win profile detected: elevated probability of 3-0 or 3-1,
                    reduced long-match probability. Use caution on MW borderline;
                    check Under market only if odds create EV.
                  </div>
                </div>
              )}

              {/* Fatigue-Induced Over diagnostic */}
              {prediction.comparison?.fatigue_over_guard?.tagged && (
                <div
                  data-testid="fatigue-over-guard-badge"
                  className="rounded-sm px-2 py-1.5 text-[10px] border
                    bg-teal-950/30 border-teal-800 text-teal-300"
                  style={mono}
                  title={(prediction.comparison.fatigue_over_guard.reasons || []).join(' · ')}
                >
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold">
                      💤 {prediction.comparison.fatigue_over_guard.tag}
                    </span>
                    <span className="text-teal-400/70">
                      penalty: {((prediction.comparison.fatigue_over_guard.confidence_penalty ?? 0) * 100).toFixed(0)}pp
                    </span>
                    <span className="text-teal-400/70">
                      balance |Δ|: {((prediction.comparison.fatigue_over_guard.balance_abs ?? 0) * 100).toFixed(1)}pp
                    </span>
                    <span className="text-teal-400/70">
                      fatigue: {prediction.comparison.fatigue_over_guard.fatigued_side ?? '—'}
                      {' '}(p1={prediction.comparison.fatigue_over_guard.p1_matches_today ?? '—'}
                      {' '}p2={prediction.comparison.fatigue_over_guard.p2_matches_today ?? '—'})
                    </span>
                    <span className="text-teal-400/70">
                      long-match: {((prediction.comparison.fatigue_over_guard.long_set_prob ?? 0) * 100).toFixed(1)}%
                    </span>
                    <span className="text-teal-400/70">
                      E[total] {prediction.comparison.fatigue_over_guard.expected_total?.toFixed(1) ?? '—'}
                      {' vs '}ou line {prediction.comparison.fatigue_over_guard.reference_ou_line ?? '—'}
                    </span>
                  </div>
                  <div className="text-teal-400/60 mt-0.5">
                    {prediction.comparison.fatigue_over_guard.ui_explanation}
                  </div>
                </div>
              )}

              {/* Fatigue-Collapse Risk diagnostic (opposite regime) */}
              {prediction.comparison?.fatigue_collapse_guard?.tagged && (
                <div
                  data-testid="fatigue-collapse-guard-badge"
                  className="rounded-sm px-2 py-1.5 text-[10px] border
                    bg-fuchsia-950/30 border-fuchsia-800 text-fuchsia-300"
                  style={mono}
                  title={(prediction.comparison.fatigue_collapse_guard.reasons || []).join(' · ')}
                >
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold">
                      📉 {prediction.comparison.fatigue_collapse_guard.tag}
                    </span>
                    <span className="text-fuchsia-400/70">
                      penalty: {((prediction.comparison.fatigue_collapse_guard.confidence_penalty ?? 0) * 100).toFixed(0)}pp
                    </span>
                    <span className="text-fuchsia-400/70">
                      collapsing: {prediction.comparison.fatigue_collapse_guard.collapsing_side ?? '—'}
                    </span>
                    <span className="text-fuchsia-400/70">
                      WR_last5 p1={prediction.comparison.fatigue_collapse_guard.p1_wr_last5?.toFixed(2) ?? '—'}
                      {' '}p2={prediction.comparison.fatigue_collapse_guard.p2_wr_last5?.toFixed(2) ?? '—'}
                    </span>
                    <span className="text-fuchsia-400/70">
                      Δset today p1={prediction.comparison.fatigue_collapse_guard.p1_set_diff_today ?? '—'}
                      {' '}p2={prediction.comparison.fatigue_collapse_guard.p2_set_diff_today ?? '—'}
                    </span>
                  </div>
                  <div className="text-fuchsia-400/60 mt-0.5">
                    {prediction.comparison.fatigue_collapse_guard.ui_explanation}
                  </div>
                </div>
              )}

              {/* Refresh-now result strip (only shows after click) */}
              {refreshNowResult && (
                <div
                  data-testid="refresh-now-result"
                  className={`rounded-sm px-2 py-1.5 text-[10px] border ${
                    refreshNowResult.status === 'ok'
                      ? 'bg-emerald-950/30 border-emerald-800 text-emerald-300'
                      : 'bg-amber-950/30 border-amber-800 text-amber-300'
                  }`}
                  style={mono}
                >
                  <span className="text-zinc-500">refresh:</span>{' '}
                  {refreshNowResult.status} · total{' '}
                  {refreshNowResult.elapsed_sec_total}s · betsapi+features{' '}
                  {refreshNowResult.step_refresh?.elapsed_sec}s · predict{' '}
                  {refreshNowResult.step_predict?.elapsed_sec}s
                  {refreshNowResult.freshness_snapshot && (
                    <>
                      {' · '}P1 matches today:{' '}
                      <b>{refreshNowResult.freshness_snapshot.p1_matches_today ?? '—'}</b>
                      {' · '}P2 matches today:{' '}
                      <b>{refreshNowResult.freshness_snapshot.p2_matches_today ?? '—'}</b>
                    </>
                  )}
                  {(refreshNowResult.step_refresh?.error
                    || refreshNowResult.step_predict?.error) && (
                    <div className="text-amber-400 mt-0.5">
                      err:{' '}
                      {refreshNowResult.step_refresh?.error
                        || refreshNowResult.step_predict?.error}
                    </div>
                  )}
                </div>
              )}

              {/* Signal Quality Badge — populated atomically by /refresh-now.
                  Read-only: deterministic scorer, no LLM, no DB writes.
                  Surfaces color, score, primary market, EV, Kelly, pros/risks,
                  and a `Send to Personal Telegram` button (modal). */}
              {refreshNowResult?.signal_quality && (
                <SignalQualityBadge
                  data={refreshNowResult.signal_quality}
                  matchInfo={selectedMatch}
                  onSendPersonal={(form) => {
                    setPersonalSendForm(form);
                    setPersonalSendOpen(true);
                  }}
                />
              )}

              {/* Inputs */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px]"
                   style={mono} data-testid="prediction-inputs">
                <div className="rounded-sm p-2" style={{ background: '#0F0F11' }}>
                  <div className="text-zinc-500">θ p1</div>
                  <div className="text-zinc-100 text-sm">
                    {fmtDelta(inputs?.theta_p1)}
                  </div>
                  <div className="text-[9px] text-zinc-500">
                    σ²={fmtFloat(inputs?.sigma2_p1, 3)}
                  </div>
                </div>
                <div className="rounded-sm p-2" style={{ background: '#0F0F11' }}>
                  <div className="text-zinc-500">θ p2</div>
                  <div className="text-zinc-100 text-sm">
                    {fmtDelta(inputs?.theta_p2)}
                  </div>
                  <div className="text-[9px] text-zinc-500">
                    σ²={fmtFloat(inputs?.sigma2_p2, 3)}
                  </div>
                </div>
                <div className="rounded-sm p-2" style={{ background: '#0F0F11' }}>
                  <div className="text-zinc-500">n_sets obs</div>
                  <div className="text-zinc-100 text-sm">
                    {fmtNum(inputs?.n_sets_p1)} / {fmtNum(inputs?.n_sets_p2)}
                  </div>
                </div>
                <div className="rounded-sm p-2" style={{ background: '#0F0F11' }}>
                  <div className="text-zinc-500">φ tournament</div>
                  <div className="text-zinc-100 text-sm">
                    {fmtDelta(inputs?.tournament_phi)}
                  </div>
                </div>
              </div>

              {/* Same-Day Freshness panel — surfaces today's session rhythm
                  alongside the fitted prior so operators can override picks
                  whose latent skill estimate disagrees with today's form. */}
              {inputs?.freshness && (
                <div className="rounded-sm p-3 mt-2"
                     style={{ background: '#0F0F11', border: '1px solid #27272A' }}
                     data-testid="setscore-freshness-panel">
                  <div className="text-[10px] text-zinc-500 uppercase tracking-wider mb-2 flex items-center gap-2">
                    Same-Day Freshness
                    {inputs.freshness.rhythm_edge_side === 'p1' && (
                      <Pill color="emerald" testid="freshness-rhythm-p1">rhythm → P1</Pill>
                    )}
                    {inputs.freshness.rhythm_edge_side === 'p2' && (
                      <Pill color="emerald" testid="freshness-rhythm-p2">rhythm → P2</Pill>
                    )}
                    {inputs.freshness.fatigue_warning && (
                      <Pill color="rose" testid="freshness-fatigue">
                        fatigue: {inputs.freshness.fatigue_warning}
                      </Pill>
                    )}
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px]" style={mono}>
                    <div className="rounded-sm p-2" style={{ background: '#18181B' }}>
                      <div className="text-zinc-500">P1 match oggi</div>
                      <div className="text-zinc-100 text-sm">
                        {fmtNum(inputs.freshness.p1_matches_today ?? 0)}
                        {inputs.freshness.p1_same_day_set_diff != null && (
                          <span className="text-[9px] text-zinc-500 ml-1">
                            (Δ set {fmtDelta(inputs.freshness.p1_same_day_set_diff)})
                          </span>
                        )}
                      </div>
                      {inputs.freshness.p1_same_day_winrate != null && (
                        <div className="text-[9px] text-zinc-500">
                          WR oggi {(inputs.freshness.p1_same_day_winrate * 100).toFixed(0)}%
                        </div>
                      )}
                    </div>
                    <div className="rounded-sm p-2" style={{ background: '#18181B' }}>
                      <div className="text-zinc-500">P2 match oggi</div>
                      <div className="text-zinc-100 text-sm">
                        {fmtNum(inputs.freshness.p2_matches_today ?? 0)}
                        {inputs.freshness.p2_same_day_set_diff != null && (
                          <span className="text-[9px] text-zinc-500 ml-1">
                            (Δ set {fmtDelta(inputs.freshness.p2_same_day_set_diff)})
                          </span>
                        )}
                      </div>
                      {inputs.freshness.p2_same_day_winrate != null && (
                        <div className="text-[9px] text-zinc-500">
                          WR oggi {(inputs.freshness.p2_same_day_winrate * 100).toFixed(0)}%
                        </div>
                      )}
                    </div>
                    <div className="rounded-sm p-2" style={{ background: '#18181B' }}>
                      <div className="text-zinc-500">P1 fresh WR10</div>
                      <div className="text-zinc-100 text-sm">
                        {inputs.freshness.p1_fresh_form_weighted_last10 != null
                          ? `${(inputs.freshness.p1_fresh_form_weighted_last10 * 100).toFixed(1)}%`
                          : '—'}
                      </div>
                      {inputs.freshness.p1_minutes_since_last_match != null && (
                        <div className="text-[9px] text-zinc-500">
                          rest {Math.round(inputs.freshness.p1_minutes_since_last_match)}m
                        </div>
                      )}
                    </div>
                    <div className="rounded-sm p-2" style={{ background: '#18181B' }}>
                      <div className="text-zinc-500">P2 fresh WR10</div>
                      <div className="text-zinc-100 text-sm">
                        {inputs.freshness.p2_fresh_form_weighted_last10 != null
                          ? `${(inputs.freshness.p2_fresh_form_weighted_last10 * 100).toFixed(1)}%`
                          : '—'}
                      </div>
                      {inputs.freshness.p2_minutes_since_last_match != null && (
                        <div className="text-[9px] text-zinc-500">
                          rest {Math.round(inputs.freshness.p2_minutes_since_last_match)}m
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* CURRENT FORM ADJUSTMENT panel (2026-05-07) — audit-only.
                  Shows the form-based logit lever applied to MW p_raw,
                  the resulting pre/post probabilities, the form-direction
                  classification (overall + relative to selected side), and
                  a MODEL SUPPORT block with the multi-model agreement
                  score. HC and O/U are NOT touched by this lever. */}
              {inputs?.current_form_adjustment && (
                <div className="rounded-sm p-3 mt-2"
                     style={{ background: '#0F0F11', border: '1px solid #27272A' }}
                     data-testid="setscore-current-form-panel">
                  <div className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1 flex items-center gap-2 flex-wrap">
                    Current Form Adjustment
                    <span
                      data-testid="current-form-mode-badge"
                      className={`text-[9px] px-1.5 py-0.5 rounded-sm border ${
                        inputs.current_form_adjustment.applied_to_p_raw
                          ? 'bg-emerald-900/40 border-emerald-700 text-emerald-200'
                          : 'bg-zinc-800/60 border-zinc-700 text-zinc-400'
                      }`}
                    >
                      {inputs.current_form_adjustment.applied_to_p_raw
                        ? 'ENABLED · APPLIED'
                        : (inputs.current_form_adjustment.enabled
                            ? 'ENABLED · NO SIGNAL'
                            : 'AUDIT-ONLY')}
                    </span>
                    {(() => {
                      // 2026-05-07 — only show "form → Px" when the new
                      // 48h-aware module agrees there is a real edge.
                      // Prevents misleading FORM → labels from WR10-only
                      // signals or BOTH_COLD scenarios.
                      const cf48 = inputs.current_form_48h || {};
                      const side48 = cf48.current_form_side;
                      if (side48 === 'p1')
                        return <Pill color="emerald" testid="current-form-overall-p1">form → P1</Pill>;
                      if (side48 === 'p2')
                        return <Pill color="emerald" testid="current-form-overall-p2">form → P2</Pill>;
                      if (cf48.both_cold)
                        return <Pill color="amber" testid="current-form-both-cold">both cold</Pill>;
                      if (cf48.no_current_form_sample_side === 'BOTH')
                        return <Pill color="zinc" testid="current-form-no-sample">no current form sample</Pill>;
                      return <Pill color="zinc" testid="current-form-overall-neutral">form neutral</Pill>;
                    })()}
                    {(() => {
                      // SUPPORTS / OPPOSES SELECTED suppressed when the
                      // 48h module says there is no real edge — using the
                      // OLD form_delta-based label there would be
                      // misleading (the user explicitly flagged this).
                      const cf48 = inputs.current_form_48h || {};
                      const has_real_edge =
                        cf48.current_form_side === 'p1' ||
                        cf48.current_form_side === 'p2';
                      if (!has_real_edge) return null;
                      const sel = (inputs.current_form_adjustment.classification || {}).selected;
                      if (sel === 'FORM_SUPPORTS_SELECTED')
                        return <Pill color="emerald" testid="current-form-selected-supports">supports selected</Pill>;
                      if (sel === 'FORM_OPPOSES_SELECTED')
                        return <Pill color="rose" testid="current-form-selected-opposes">opposes selected</Pill>;
                      if (sel === 'FORM_NEUTRAL_SELECTED')
                        return <Pill color="zinc" testid="current-form-selected-neutral">neutral on selected</Pill>;
                      return null;
                    })()}
                  </div>

                  {/* SCOPE NOTICE — explicit so HC/OU don't appear corrected */}
                  <div
                    data-testid="current-form-scope-notice"
                    className="text-[10px] mb-2 px-2 py-1 rounded-sm"
                    style={{
                      background: 'rgba(245,158,11,0.08)',
                      border: '1px solid rgba(245,158,11,0.30)',
                      color: '#FCD34D',
                      ...mono,
                    }}
                  >
                    MW adjusted only — HC/O-U still based on raw SetScore until adjusted simulation is validated.
                  </div>

                  {/* SET SCORE ADJUSTMENT STACK (Phase 1+2 — 2026-05-07) — audit-only */}
                  {inputs.contextual_setscore && inputs.current_form_48h && (
                    <div
                      data-testid="setscore-adjustment-stack-block"
                      className="mb-2 rounded-sm p-2 text-[11px]"
                      style={{ background: '#0B0B0E', border: '1px solid #27272A', ...mono }}
                    >
                      <div className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1 flex items-center gap-2 flex-wrap">
                        Set Score Adjustment Stack
                        <Pill color="zinc" testid="adjustment-mode-badge">
                          {inputs.contextual_setscore.adjustment_mode || 'AUDIT_ONLY'}
                        </Pill>
                        <Pill color={inputs.current_form_48h.both_cold ? 'amber' : (inputs.current_form_48h.current_form_side === 'neutral' ? 'zinc' : 'emerald')} testid="cf48-side-badge">
                          form 48h: {inputs.current_form_48h.current_form_side}
                        </Pill>
                        <Pill color="zinc" testid="cf48-source-badge">
                          source: {inputs.current_form_48h.current_form_source}
                        </Pill>
                        <Pill color={inputs.current_form_48h.current_form_strength === 'cold' ? 'rose' : (inputs.current_form_48h.current_form_strength === 'strong' ? 'emerald' : 'zinc')} testid="cf48-strength-badge">
                          strength: {inputs.current_form_48h.current_form_strength}
                        </Pill>
                        {inputs.current_form_48h.both_cold && (
                          <Pill color="rose" testid="cf48-both-cold">BOTH_COLD</Pill>
                        )}
                        {inputs.current_form_48h.no_current_form_sample_side && (
                          <Pill color="amber" testid="cf48-no-sample">
                            NO_CURRENT_FORM_SAMPLE: {inputs.current_form_48h.no_current_form_sample_side}
                          </Pill>
                        )}
                        {inputs.current_form_48h.stale_wr10_side && (
                          <Pill color="zinc" testid="cf48-stale-wr10">
                            STALE_WR10: {inputs.current_form_48h.stale_wr10_side}
                          </Pill>
                        )}
                        {inputs.current_form_48h.wr10_historical_edge_side &&
                         inputs.current_form_48h.wr10_historical_edge_side !== 'neutral' && (
                          <Pill color="sky" testid="cf48-wr10-historical">
                            WR10_HISTORICAL_EDGE: {inputs.current_form_48h.wr10_historical_edge_side}
                          </Pill>
                        )}
                      </div>

                      {/* Probability levels */}
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-2">
                        <div className="rounded-sm p-1.5" style={{ background: '#18181B' }}>
                          <div className="text-[9px] text-zinc-500">Raw SetScore P1</div>
                          <div data-testid="stack-raw-p1" className="text-zinc-100 text-sm">
                            {(inputs.contextual_setscore.raw_setscore_p1 * 100).toFixed(2)}%
                          </div>
                        </div>
                        <div className="rounded-sm p-1.5" style={{ background: '#18181B' }}>
                          <div className="text-[9px] text-zinc-500">Form 48h adjusted P1</div>
                          <div data-testid="stack-form48h-p1" className="text-zinc-100 text-sm">
                            {(inputs.contextual_setscore.form_48h_adjusted_p1 * 100).toFixed(2)}%
                          </div>
                        </div>
                        <div className="rounded-sm p-1.5" style={{ background: '#18181B' }}>
                          <div className="text-[9px] text-zinc-500">Form shift</div>
                          <div data-testid="stack-form-shift-pp" className="text-zinc-100 text-sm">
                            {fmtDelta(inputs.contextual_setscore.form_shift_pp.toFixed(2))} pp
                          </div>
                        </div>
                        <div className="rounded-sm p-1.5" style={{ background: '#18181B' }}>
                          <div className="text-[9px] text-zinc-500">Contextual SetScore P1</div>
                          <div data-testid="stack-contextual-p1" className="text-zinc-100 text-sm">
                            {(inputs.contextual_setscore.contextual_setscore_p1 * 100).toFixed(2)}%
                          </div>
                        </div>
                        <div className="rounded-sm p-1.5" style={{ background: '#18181B' }}>
                          <div className="text-[9px] text-zinc-500">Final adjusted P1</div>
                          <div data-testid="stack-final-p1" className="text-zinc-100 text-sm">
                            {(inputs.contextual_setscore.final_adjusted_p1 * 100).toFixed(2)}%
                          </div>
                        </div>
                        <div className="rounded-sm p-1.5" style={{ background: '#18181B' }}>
                          <div className="text-[9px] text-zinc-500">Combined shift</div>
                          <div data-testid="stack-combined-shift-pp" className="text-zinc-100 text-sm">
                            {fmtDelta(inputs.contextual_setscore.combined_shift_pp.toFixed(2))} pp
                          </div>
                        </div>
                      </div>

                      {/* Per-player 48h stats */}
                      <div className="grid grid-cols-2 gap-2 mt-2">
                        <div className="rounded-sm p-1.5" style={{ background: '#0F0F11' }}>
                          <div className="text-[9px] text-zinc-500 mb-1">P1 — 48h window</div>
                          <div className="text-[11px] text-zinc-200">
                            <span data-testid="cf48-p1-norm">norm {inputs.current_form_48h.p1_score_norm.toFixed(3)}</span>
                            {' · '}
                            <span data-testid="cf48-p1-raw">raw {inputs.current_form_48h.p1_score_raw.toFixed(3)}</span>
                          </div>
                          <div className="text-[10px] text-zinc-400">
                            <span data-testid="cf48-p1-matches48h">{inputs.current_form_48h.p1_matches_48h} match 48h</span>
                            {' · '}
                            <span data-testid="cf48-p1-set-diff48h">Δ sets {inputs.current_form_48h.p1_set_diff_48h}</span>
                            {' · '}
                            <span data-testid="cf48-p1-wr48h">
                              WR {inputs.current_form_48h.p1_wr_48h != null
                                ? (inputs.current_form_48h.p1_wr_48h * 100).toFixed(0) + '%'
                                : '—'}
                            </span>
                            {' · '}
                            <span data-testid="cf48-p1-last-age-h">
                              last {inputs.current_form_48h.p1_last_match_age_hours != null
                                ? inputs.current_form_48h.p1_last_match_age_hours.toFixed(1) + 'h'
                                : '—'}
                            </span>
                          </div>
                        </div>
                        <div className="rounded-sm p-1.5" style={{ background: '#0F0F11' }}>
                          <div className="text-[9px] text-zinc-500 mb-1">P2 — 48h window</div>
                          <div className="text-[11px] text-zinc-200">
                            <span data-testid="cf48-p2-norm">norm {inputs.current_form_48h.p2_score_norm.toFixed(3)}</span>
                            {' · '}
                            <span data-testid="cf48-p2-raw">raw {inputs.current_form_48h.p2_score_raw.toFixed(3)}</span>
                          </div>
                          <div className="text-[10px] text-zinc-400">
                            <span data-testid="cf48-p2-matches48h">{inputs.current_form_48h.p2_matches_48h} match 48h</span>
                            {' · '}
                            <span data-testid="cf48-p2-set-diff48h">Δ sets {inputs.current_form_48h.p2_set_diff_48h}</span>
                            {' · '}
                            <span data-testid="cf48-p2-wr48h">
                              WR {inputs.current_form_48h.p2_wr_48h != null
                                ? (inputs.current_form_48h.p2_wr_48h * 100).toFixed(0) + '%'
                                : '—'}
                            </span>
                            {' · '}
                            <span data-testid="cf48-p2-last-age-h">
                              last {inputs.current_form_48h.p2_last_match_age_hours != null
                                ? inputs.current_form_48h.p2_last_match_age_hours.toFixed(1) + 'h'
                                : '—'}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div
                        data-testid="stack-confidence-clarification"
                        className="text-[9px] text-zinc-500 mt-2 italic"
                      >
                        Confidence measures profile clarity, not betting probability or EV. Stack is audit-only until <code>setscore_final_adjusted_probability_enabled</code> is true.
                      </div>
                    </div>
                  )}

                  {/* 2026-05-08 — LEGACY_WR10_CONTEXT_ONLY guard.
                      When the new 48h-aware module reports NO current form
                      sample (source=NONE / no_current_form_sample_side=BOTH /
                      both_cold), the form_logit_adj below is computed from
                      a WR10 historical fallback — NOT from real current
                      48h form. Mark the legacy form scores grid + Raw/Form-
                      adjusted/Shift trio + Form Adjustment Agreement block
                      as historical-context-only so they cannot be mistaken
                      for live current form. */}
                  {(() => {
                    const cf48 = inputs.current_form_48h || {};
                    const noCurrentSample =
                      cf48.no_current_form_sample_side === 'BOTH' ||
                      cf48.current_form_source === 'NONE' ||
                      cf48.both_cold === true;
                    if (!noCurrentSample) return null;
                    return (
                      <div
                        data-testid="current-form-legacy-wr10-only-notice"
                        className="text-[10px] mt-2 px-2 py-1 rounded-sm flex items-start gap-2"
                        style={{
                          background: 'rgba(168,85,247,0.08)',
                          border: '1px solid rgba(168,85,247,0.30)',
                          color: '#C4B5FD',
                          ...mono,
                        }}
                      >
                        <span
                          className="px-1.5 py-0.5 rounded-sm text-[9px] font-semibold tracking-wider flex-shrink-0"
                          style={{
                            background: 'rgba(168,85,247,0.20)',
                            color: '#DDD6FE',
                          }}
                        >
                          LEGACY_WR10_CONTEXT_ONLY
                        </span>
                        <span>
                          No real 48h current-form sample for {
                            cf48.no_current_form_sample_side === 'BOTH' ? 'both players'
                              : (cf48.no_current_form_sample_side === 'p1' ? 'P1'
                              : (cf48.no_current_form_sample_side === 'p2' ? 'P2' : 'one or both players'))
                          }. The blocks below (form score, form Δ, logit adj,
                          Raw vs Form-adjusted shift, Form Adjustment Agreement)
                          are derived from a WR10 historical fallback — they
                          are NOT live current form. Treat as historical
                          context only. The Set Score Adjustment Stack above
                          is the authoritative current-form view (form shift = 0.00 pp).
                        </span>
                      </div>
                    );
                  })()}

                  {/* Form scores grid (normalized [0..1] for display, raw
                      [-1..+1] shown as small caption). */}
                  <div
                    className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px]"
                    style={{
                      ...mono,
                      opacity: ((inputs.current_form_48h || {}).no_current_form_sample_side === 'BOTH'
                        || (inputs.current_form_48h || {}).current_form_source === 'NONE'
                        || (inputs.current_form_48h || {}).both_cold === true) ? 0.55 : 1,
                    }}
                    data-testid="current-form-legacy-scores-grid">
                    <div className="rounded-sm p-2" style={{ background: '#18181B' }}>
                      <div className="text-zinc-500">P1 form score (norm)</div>
                      <div
                        data-testid="current-form-score-p1"
                        className="text-zinc-100 text-sm"
                      >
                        {inputs.current_form_adjustment.p1_current_form_score_norm != null
                          ? inputs.current_form_adjustment.p1_current_form_score_norm.toFixed(3)
                          : (inputs.current_form_adjustment.form_score_p1?.score_norm != null
                              ? inputs.current_form_adjustment.form_score_p1.score_norm.toFixed(3)
                              : '—')}
                      </div>
                      <div className="text-[9px] text-zinc-500">
                        raw {inputs.current_form_adjustment.p1_form_raw != null
                          ? inputs.current_form_adjustment.p1_form_raw.toFixed(3)
                          : '—'}
                        {' · '}signal: {inputs.current_form_adjustment.form_score_p1?.has_signal ? 'yes' : 'no'}
                      </div>
                    </div>
                    <div className="rounded-sm p-2" style={{ background: '#18181B' }}>
                      <div className="text-zinc-500">P2 form score (norm)</div>
                      <div
                        data-testid="current-form-score-p2"
                        className="text-zinc-100 text-sm"
                      >
                        {inputs.current_form_adjustment.p2_current_form_score_norm != null
                          ? inputs.current_form_adjustment.p2_current_form_score_norm.toFixed(3)
                          : (inputs.current_form_adjustment.form_score_p2?.score_norm != null
                              ? inputs.current_form_adjustment.form_score_p2.score_norm.toFixed(3)
                              : '—')}
                      </div>
                      <div className="text-[9px] text-zinc-500">
                        raw {inputs.current_form_adjustment.p2_form_raw != null
                          ? inputs.current_form_adjustment.p2_form_raw.toFixed(3)
                          : '—'}
                        {' · '}signal: {inputs.current_form_adjustment.form_score_p2?.has_signal ? 'yes' : 'no'}
                      </div>
                    </div>
                    <div className="rounded-sm p-2" style={{ background: '#18181B' }}>
                      <div className="text-zinc-500">form Δ (P1 − P2)</div>
                      <div
                        data-testid="current-form-delta"
                        className="text-zinc-100 text-sm"
                      >
                        {inputs.current_form_adjustment.form_delta_norm != null
                          ? fmtDelta(inputs.current_form_adjustment.form_delta_norm.toFixed(3))
                          : (inputs.current_form_adjustment.form_delta != null
                              ? fmtDelta(inputs.current_form_adjustment.form_delta.toFixed(3))
                              : '—')}
                      </div>
                      <div className="text-[9px] text-zinc-500">
                        raw Δ {inputs.current_form_adjustment.form_delta != null
                          ? fmtDelta(inputs.current_form_adjustment.form_delta.toFixed(3))
                          : '—'}
                        {' · '}weight {inputs.current_form_adjustment.form_weight}
                        {' · '}cap ±{inputs.current_form_adjustment.logit_cap}
                      </div>
                    </div>
                    <div className="rounded-sm p-2" style={{ background: '#18181B' }}>
                      <div className="text-zinc-500">logit adj</div>
                      <div
                        data-testid="current-form-logit-adj"
                        className="text-zinc-100 text-sm"
                      >
                        {inputs.current_form_adjustment.form_logit_adj != null
                          ? fmtDelta(inputs.current_form_adjustment.form_logit_adj.toFixed(3))
                          : '—'}
                      </div>
                      <div className="text-[9px] text-zinc-500">applied to logit(MW p1)</div>
                    </div>
                  </div>

                  {/* Raw / Form-adjusted / Shift — display values are the
                      audit "what-if": they match Model Support exactly,
                      regardless of whether the lever is currently applied
                      to the live engine output. */}
                  <div
                    className="grid grid-cols-3 gap-2 mt-2 text-[11px]"
                    style={{
                      ...mono,
                      opacity: ((inputs.current_form_48h || {}).no_current_form_sample_side === 'BOTH'
                        || (inputs.current_form_48h || {}).current_form_source === 'NONE'
                        || (inputs.current_form_48h || {}).both_cold === true) ? 0.55 : 1,
                    }}
                    data-testid="current-form-legacy-trio">
                    <div className="rounded-sm p-2" style={{ background: '#18181B' }}>
                      <div className="text-zinc-500">Raw SetScore P1</div>
                      <div
                        data-testid="current-form-mw-pre"
                        className="text-zinc-100 text-sm"
                      >
                        {inputs.mw_p1_pre_form != null
                          ? `${(inputs.mw_p1_pre_form * 100).toFixed(2)}%`
                          : '—'}
                      </div>
                    </div>
                    <div className="rounded-sm p-2" style={{ background: '#18181B' }}>
                      <div className="text-zinc-500">Form-adjusted P1</div>
                      <div
                        data-testid="current-form-mw-post"
                        className="text-zinc-100 text-sm"
                      >
                        {inputs.mw_p1_post_form != null
                          ? `${(inputs.mw_p1_post_form * 100).toFixed(2)}%`
                          : '—'}
                      </div>
                    </div>
                    <div className="rounded-sm p-2" style={{ background: '#18181B' }}>
                      <div className="text-zinc-500">Shift</div>
                      <div
                        data-testid="current-form-mw-delta-pp"
                        className="text-zinc-100 text-sm"
                      >
                        {inputs.mw_p1_delta_pp_after_form != null
                          ? fmtDelta(inputs.mw_p1_delta_pp_after_form.toFixed(2)) + ' pp'
                          : '—'}
                      </div>
                    </div>
                  </div>

                  {/* Audit-only reminder — when the adjustment is computed
                      but the flag is OFF, the displayed adjusted value is
                      still informational only. */}
                  {!inputs.current_form_adjustment.applied_to_p_raw && (
                    <div
                      data-testid="current-form-audit-only-notice"
                      className="text-[10px] mt-2 px-2 py-1 rounded-sm"
                      style={{
                        background: 'rgba(113,113,122,0.10)',
                        border: '1px solid rgba(113,113,122,0.40)',
                        color: '#A1A1AA',
                        ...mono,
                      }}
                    >
                      Audit-only: final MW/HC/O-U still use raw SetScore until setting is enabled.
                    </div>
                  )}

                  {/* FORM ADJUSTMENT AGREEMENT (2026-05-07 renamed)
                      NOTE: this block measures ONLY the delta between
                      raw SetScore and form-adjusted SetScore — it is NOT
                      a cross-model consensus check. Pascal/LW/MW_v2/market
                      are surfaced separately in the MATCH WINNER detail
                      row + the SetScore-isolated MW caution risk line. */}
                  {inputs.current_form_adjustment.model_agreement && (
                    <div
                      data-testid="form-adjustment-agreement-block"
                      className="mt-2 rounded-sm p-2 text-[11px]"
                      style={{
                        background: '#18181B',
                        ...mono,
                        opacity: ((inputs.current_form_48h || {}).no_current_form_sample_side === 'BOTH'
                          || (inputs.current_form_48h || {}).current_form_source === 'NONE'
                          || (inputs.current_form_48h || {}).both_cold === true) ? 0.55 : 1,
                      }}
                    >
                      <div className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1 flex items-center gap-2 flex-wrap">
                        Form Adjustment Agreement
                        {(() => {
                          const v = inputs.current_form_adjustment.model_agreement.verdict;
                          const map = {
                            STRONG_AGREEMENT: 'emerald',
                            MODERATE: 'sky',
                            WEAK: 'amber',
                            DIVERGENT: 'rose',
                            INSUFFICIENT: 'zinc',
                          };
                          return (
                            <Pill color={map[v] || 'zinc'} testid="form-adjustment-agreement-verdict">
                              {v}
                            </Pill>
                          );
                        })()}
                        {inputs.current_form_adjustment.model_agreement.agreement_score != null && (
                          <span className="text-zinc-400 text-[10px]">
                            agreement {(inputs.current_form_adjustment.model_agreement.agreement_score * 100).toFixed(0)}%
                            {' · '}spread {inputs.current_form_adjustment.model_agreement.spread_pp?.toFixed(2)}pp
                          </span>
                        )}
                      </div>
                      <div
                        data-testid="form-adjustment-agreement-caption"
                        className="text-[9px] text-zinc-500 mb-2 leading-snug"
                      >
                        Measures raw vs form-adjusted SetScore only.
                        For Pascal / LW / MW_v2 / market consensus see the MATCH WINNER row.
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                        {Object.entries(inputs.current_form_adjustment.model_agreement.breakdown || {}).map(([k, v]) => (
                          <div
                            key={k}
                            className="rounded-sm p-1.5"
                            style={{ background: '#0F0F11' }}
                            data-testid={`form-adjustment-agreement-row-${k}`}
                          >
                            <div className="text-[9px] text-zinc-500">{k}</div>
                            <div className="text-zinc-100 text-sm">
                              {(v * 100).toFixed(2)}%
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* No-book diagnostic banner — surfaces when ALL 3 markets
                  have null books, which usually means the bundle endpoint
                  could not find a prediction for this match_id (phantom
                  match) OR no scraper has fetched odds for it yet. */}
              {predictionOdds && (
                predictionOdds.error
                || (
                  !predictionOdds?.mw?.book
                  && !predictionOdds?.hc_minus_1_5?.book
                  && (predictionOdds?.ou || []).every((r) => !r?.book)
                )
              ) && (
                <div data-testid="no-book-banner"
                     className="rounded-sm p-3 text-[11px] leading-relaxed"
                     style={{
                       border: '1px solid rgba(245,158,11,0.40)',
                       background: 'rgba(245,158,11,0.06)',
                       color: '#FCD34D',
                       ...mono,
                     }}>
                  <span className="font-semibold">Quote bookmaker non disponibili</span> per
                  questo match. Cause possibili:
                  <ul className="mt-1 ml-4 list-disc text-zinc-400">
                    <li>
                      <span className="text-zinc-300">match_id phantom</span>:{' '}
                      lookup in <code className="text-zinc-300">tt_setscore_predictions</code>{' '}
                      ha restituito{' '}
                      <code className="text-zinc-300">
                        {predictionOdds.error || 'nessuna riga'}
                      </code>. Il match potrebbe non esistere in{' '}
                      <code className="text-zinc-300">tt_matches</code>{' '}
                      oppure essere stato rimosso dopo la prediction.
                    </li>
                    <li>
                      <span className="text-zinc-300">scraper non ha ancora coperto</span>{' '}
                      questo match: alcuni tornei (Setka Cup, schedule
                      lontano nel tempo) sono ingestiti in batch periodici.
                    </li>
                    <li>
                      <span className="text-zinc-300">match già finito</span>:{' '}
                      gli scraper non aggiornano più le quote.
                    </li>
                  </ul>
                  <div className="mt-2 text-zinc-500">
                    → Torna al match pickup, usa "force refresh" e ricontrolla
                    fra 60-120s. Se persiste, l'ID match potrebbe essere stale.
                  </div>
                </div>
              )}

              {/* SetScore ↔ Market divergence warning (2026-04-29) */}
              {prediction?.setscore_divergence_severity &&
               prediction.setscore_divergence_severity !== "none" && (
                <div
                  data-testid="setscore-divergence-badge"
                  data-severity={prediction.setscore_divergence_severity}
                  className={`rounded-sm p-2.5 text-xs flex items-start gap-2 border ${
                    prediction.setscore_divergence_severity === "HARD_WARNING"
                      ? "bg-rose-900/20 border-rose-500/40 text-rose-200"
                      : "bg-amber-900/20 border-amber-500/40 text-amber-200"
                  }`}
                >
                  <span className="font-bold text-[10px] uppercase tracking-wider shrink-0 mt-0.5">
                    {prediction.setscore_divergence_severity === "HARD_WARNING"
                      ? "⚠ HARD WARNING"
                      : "⚠ WARNING"}
                  </span>
                  <span className="font-mono text-[11px]">
                    SET_SCORE_MARKET_DIVERGENCE — |P_SetScore − P_Market| ={" "}
                    <span className="font-semibold">
                      {prediction.setscore_market_divergence_pp?.toFixed(1)}pp
                    </span>
                    <span className="text-zinc-500"> · threshold 20pp / hard 30pp</span>
                  </span>
                </div>
              )}

              {/* SET PROFILE FIRST — audit-only archetype engine */}
              {selectedMatch?.id && (
                <SetProfilePanel
                  matchId={selectedMatch.id}
                  pin={pin}
                  autoLoad={!!refreshNowResult}
                />
              )}

              {/* MW market */}
              <div className="rounded-sm p-3" style={{ background: '#0F0F11' }}
                   data-testid="prediction-mw">
                <div className="text-[10px] uppercase text-zinc-500 mb-2"
                     style={heading}>
                  Match Winner
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs" style={mono}>
                  <div>
                    <div className="text-zinc-500">p1 win</div>
                    <div className="text-emerald-300 text-xl">
                      {fmtPct(outputs?.mw?.p1?.p_calibrated)}
                    </div>
                    <div className="text-[10px] text-zinc-500">
                      raw={fmtPct(outputs?.mw?.p1?.p_raw)} ·
                      fair={fmtFloat(outputs?.mw?.p1?.fair_odds_mean, 2)} ·
                      [p_lo80={fmtPct(outputs?.mw?.p1?.p_lower_80)}]
                    </div>
                    <BookEvBadge
                      label="book"
                      odd={predictionOdds?.mw?.book?.odd_p1}
                      ev={predictionOdds?.mw?.book?.ev_p1_pct}
                      evCons={predictionOdds?.mw?.book?.ev_p1_cons_pct}
                      bookmaker={predictionOdds?.mw?.book?.bookmaker}
                      ageSec={predictionOdds?.mw?.book?.age_sec}
                      isStale={predictionOdds?.mw?.book?.is_stale}
                      testid="mw-p1-book-ev"
                    />
                  </div>
                  <div>
                    <div className="text-zinc-500">p2 win</div>
                    <div className="text-sky-300 text-xl">
                      {fmtPct(outputs?.mw?.p2?.p_calibrated)}
                    </div>
                    <div className="text-[10px] text-zinc-500">
                      raw={fmtPct(outputs?.mw?.p2?.p_raw)} ·
                      fair={fmtFloat(outputs?.mw?.p2?.fair_odds_mean, 2)} ·
                      [p_lo80={fmtPct(outputs?.mw?.p2?.p_lower_80)}]
                    </div>
                    <BookEvBadge
                      label="book"
                      odd={predictionOdds?.mw?.book?.odd_p2}
                      ev={predictionOdds?.mw?.book?.ev_p2_pct}
                      evCons={predictionOdds?.mw?.book?.ev_p2_cons_pct}
                      bookmaker={predictionOdds?.mw?.book?.bookmaker}
                      ageSec={predictionOdds?.mw?.book?.age_sec}
                      isStale={predictionOdds?.mw?.book?.is_stale}
                      testid="mw-p2-book-ev"
                    />
                  </div>
                </div>
                <div className="mt-2 pt-2 border-t border-zinc-900 text-[10px] grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2"
                     style={mono} data-testid="prediction-comparison">
                  <div data-testid="cmp-engine-p1">
                    <span className="text-zinc-500">setscore p1:</span>{' '}
                    <span className="text-emerald-300 font-semibold">
                      {fmtPct(cmp?.engine?.p1 ?? outputs?.mw?.p1?.p_calibrated)}
                    </span>
                  </div>
                  <div data-testid="cmp-pascal-p1">
                    <span className="text-zinc-500">pascal p1:</span>{' '}
                    <span className="text-zinc-300">
                      {fmtPct(cmp?.pascal?.p1)}
                    </span>
                    <span className={`ml-1 ${Number(cmp?.delta_engine_vs_pascal_p1) > 0
                      ? 'text-emerald-400' : 'text-rose-400'}`}>
                      (Δ {fmtDelta(cmp?.delta_engine_vs_pascal_p1)})
                    </span>
                  </div>
                  <div data-testid="cmp-lw-p1">
                    <span className="text-zinc-500">lw p1:</span>{' '}
                    <span className="text-zinc-300">
                      {fmtPct(cmp?.lw?.p1)}
                    </span>
                    <span className={`ml-1 ${Number(cmp?.delta_engine_vs_lw_p1) > 0
                      ? 'text-emerald-400' : 'text-rose-400'}`}>
                      (Δ {fmtDelta(cmp?.delta_engine_vs_lw_p1)})
                    </span>
                  </div>
                  <div data-testid="cmp-mw-v2-p1">
                    <span className="text-zinc-500">mw_v2 p1:</span>{' '}
                    <span className="text-zinc-300">
                      {fmtPct(cmp?.mw_v2?.p1)}
                    </span>
                    <span className={`ml-1 ${Number(cmp?.delta_engine_vs_mw_v2_p1) > 0
                      ? 'text-emerald-400' : 'text-rose-400'}`}>
                      (Δ {fmtDelta(cmp?.delta_engine_vs_mw_v2_p1)})
                    </span>
                  </div>
                  <div data-testid="cmp-market-p1">
                    <span className="text-zinc-500">market p1:</span>{' '}
                    <span className="text-zinc-300">
                      {fmtPct(cmp?.market?.p1)}
                    </span>
                    <span className={`ml-1 ${Number(cmp?.delta_engine_vs_market_p1) > 0
                      ? 'text-emerald-400' : 'text-rose-400'}`}>
                      (Δ {fmtDelta(cmp?.delta_engine_vs_market_p1)})
                    </span>
                  </div>
                  <div data-testid="cmp-actual">
                    <span className="text-zinc-500">actual:</span>{' '}
                    <span className="text-zinc-300">
                      {cmp?.actual ? cmp.actual.winner.toUpperCase() : 'pending'}
                    </span>
                  </div>
                </div>
              </div>

              {/* HC + OU */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <div className="rounded-sm p-3" style={{ background: '#0F0F11' }}
                     data-testid="prediction-hc">
                  <div className="text-[10px] uppercase text-zinc-500 mb-2"
                       style={heading}>Handicap (set-based)</div>
                  <div className="text-xs space-y-1" style={mono}>
                    <div className="flex justify-between items-center gap-2">
                      <span className="text-zinc-500">HC -1.5 p1</span>
                      <span className="text-zinc-200">
                        {fmtPct(outputs?.hc_1_5?.p1?.p_calibrated)}
                      </span>
                      <BookEvBadge
                        odd={predictionOdds?.hc_minus_1_5?.book?.home_odd}
                        ev={predictionOdds?.hc_minus_1_5?.book?.ev_p1_pct}
                        evCons={predictionOdds?.hc_minus_1_5?.book?.ev_p1_cons_pct}
                        bookmaker={predictionOdds?.hc_minus_1_5?.book?.bookmaker}
                        ageSec={predictionOdds?.hc_minus_1_5?.book?.age_sec}
                        isStale={predictionOdds?.hc_minus_1_5?.book?.is_stale}
                        testid="hc-p1-book-ev"
                        compact
                      />
                    </div>
                    <div className="flex justify-between items-center gap-2">
                      <span className="text-zinc-500">HC -1.5 p2</span>
                      <span className="text-zinc-200">
                        {fmtPct(outputs?.hc_1_5?.p2?.p_calibrated)}
                      </span>
                      <BookEvBadge
                        odd={predictionOdds?.hc_minus_1_5?.book?.away_odd}
                        ev={predictionOdds?.hc_minus_1_5?.book?.ev_p2_pct}
                        evCons={predictionOdds?.hc_minus_1_5?.book?.ev_p2_cons_pct}
                        bookmaker={predictionOdds?.hc_minus_1_5?.book?.bookmaker}
                        ageSec={predictionOdds?.hc_minus_1_5?.book?.age_sec}
                        isStale={predictionOdds?.hc_minus_1_5?.book?.is_stale}
                        testid="hc-p2-book-ev"
                        compact
                      />
                    </div>
                    <div className="flex justify-between">
                      <span className="text-zinc-600">HC -2.5 p1 (quarantined)</span>
                      <span className="text-zinc-500">
                        {fmtPct(outputs?.hc_2_5?.p1?.p_calibrated)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-zinc-600">HC -2.5 p2 (quarantined)</span>
                      <span className="text-zinc-500">
                        {fmtPct(outputs?.hc_2_5?.p2?.p_calibrated)}
                      </span>
                    </div>
                  </div>

                  {/* HC Odds Mapping Audit (2026-05-14) — data integrity */}
                  {prediction?.hc_odds_audit && (() => {
                    const a = prediction.hc_odds_audit;
                    const p1 = a.phase1_raw_audit || {};
                    const p2 = a.phase2_normalization || {};
                    const p3 = a.phase3_candidates || {};
                    const p4 = a.phase4_conflict || {};
                    const cands = p3.candidates || {};
                    const conflict = p4.market_model_favorite_conflict;
                    return (
                      <div className="mt-3 pt-2 border-t border-zinc-800 text-[10px]"
                            data-testid="hc-odds-audit-panel" style={mono}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-zinc-400 uppercase tracking-wide font-semibold">
                            ◇ HC Odds Mapping Audit
                          </span>
                          <span className={conflict ? 'text-rose-400' : 'text-emerald-400/70'}>
                            {conflict ? 'CONFLICT' : 'aligned'}
                          </span>
                        </div>
                        {/* Phase 1: raw */}
                        <div className="space-y-0.5 mb-1">
                          <div className="text-zinc-500">phase 1 · raw odds ({p1.n_raw_rows ?? 0} rows)</div>
                          {p1.raw_hc_odds_missing
                            ? <div className="text-amber-400">raw_hc_odds_missing</div>
                            : (p1.rows || []).slice(0, 3).map((r, i) => (
                              <div key={i} className="text-zinc-400 pl-2" data-testid={`hc-raw-row-${i}`}>
                                {r.bookmaker} · line {r.raw_handicap_line > 0 ? '+' : ''}{r.raw_handicap_line}
                                · home @{r.raw_home_odds} · away @{r.raw_away_odds}
                              </div>
                            ))}
                        </div>
                        {/* Phase 2: normalized polarity rule */}
                        <div className="space-y-0.5 mb-1">
                          <div className="text-zinc-500">phase 2 · normalized · rule: line&lt;0 = HC_FAV, line&gt;0 = HC_DOG</div>
                          {(p2.rows || []).slice(0, 4).map((r, i) => (
                            <div key={i} className="text-zinc-400 pl-2" data-testid={`hc-norm-row-${i}`}>
                              {r.normalized_player_side?.toUpperCase()} ({r.normalized_player_name || '?'}) ·
                              {' '}{r.normalized_line > 0 ? '+' : ''}{r.normalized_line} @{r.normalized_odds}
                              {' · '}<span className={r.bookmaker_role === 'HC_FAV' ? 'text-cyan-300' : 'text-zinc-300'}>{r.bookmaker_role}</span>
                            </div>
                          ))}
                        </div>
                        {/* Phase 3: candidates */}
                        <div className="space-y-0.5 mb-1">
                          <div className="text-zinc-500">phase 3 · candidates (no synthesis)</div>
                          {Object.entries(cands).map(([k, v]) => (
                            <div key={k} className="pl-2" data-testid={`hc-cand-${k}`}>
                              <span className="text-zinc-500">{k}:</span>{' '}
                              {v.available
                                ? <span className="text-emerald-300">@{v.odds} · {v.role_observed} · {v.bookmaker}</span>
                                : <span className="text-rose-400/70">unavailable · {v.unavailable_reason}</span>}
                            </div>
                          ))}
                        </div>
                        {/* Phase 4: conflict */}
                        <div className={`space-y-0.5 ${conflict ? 'text-rose-300' : 'text-zinc-300'}`}>
                          <div className="text-zinc-500">phase 4 · model vs book favorite</div>
                          <div className="pl-2" data-testid="hc-p4-summary">
                            model_fav: <span className="text-zinc-200">{p4.model_favorite_side || '—'}</span>
                            {' · '}book_fav: <span className="text-zinc-200">{p4.bookmaker_favorite_side || '—'}</span>
                            {' · '}routing: <span className={conflict ? 'text-rose-400' : 'text-emerald-300'}>{p4.routing_recommendation || '—'}</span>
                          </div>
                          {(p4.reasons || []).length > 0 && (
                            <div className="pl-2 text-rose-400/80" data-testid="hc-p4-reasons">
                              reasons: {p4.reasons.join(' · ')}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })()}
                </div>

                <div className="rounded-sm p-3" style={{ background: '#0F0F11' }}
                     data-testid="prediction-ou">
                  <div className="text-[10px] uppercase text-zinc-500 mb-2 flex items-center justify-between gap-2"
                       style={heading}>
                    <span>Over / Under</span>
                    <span
                      className="text-[9px] normal-case tracking-normal text-zinc-500 font-normal"
                      title="The EV column below is RAW: p_calibrated × odds − 1. The Paper Signal / SetProfile EV uses p_lo80 (conservative) — the two will differ when p_lo80 < p_calibrated."
                      data-testid="ou-table-ev-raw-caption"
                    >
                      EV = raw (p_calibrated × odds − 1)
                    </span>
                  </div>
                  <div className="text-xs space-y-1" style={mono}>
                    <div className="text-zinc-500 mb-1">
                      Expected total: {fmtFloat(outputs?.expected_total_points?.mean, 1)}{' '}
                      ± {fmtFloat(outputs?.expected_total_points?.std, 1)}
                    </div>
                    {outputs?.ou?.slice(0, 9).map((entry) => {
                      const ouOdds = (predictionOdds?.ou || [])
                        .find((r) => Number(r.line) === Number(entry.line));
                      return (
                        <div key={entry.line}
                             className="flex justify-between items-center gap-2">
                          <span className="text-zinc-500 w-16">Over {entry.line}</span>
                          <span className="text-zinc-200">
                            {fmtPct(entry.over?.p_calibrated)}{' '}
                            <span className="text-zinc-500">
                              / {fmtPct(entry.under?.p_calibrated)} U
                            </span>
                          </span>
                          <span className="ml-auto flex items-center gap-1.5 text-[10px]">
                            {ouOdds?.book ? (
                              <>
                                <span className="text-zinc-500">O</span>
                                <span className="text-zinc-200">
                                  {fmtFloat(ouOdds.book.over_odd, 2)}
                                </span>
                                <span
                                  title={`EV raw (p_calibrated × ${fmtFloat(ouOdds.book.over_odd, 2)} − 1)${ouOdds.book.ev_over_cons_pct != null ? ` · cons ${ouOdds.book.ev_over_cons_pct >= 0 ? '+' : ''}${ouOdds.book.ev_over_cons_pct.toFixed(1)}%` : ''}`}
                                  className={
                                  ouOdds.book.ev_over_pct >= 5 ? 'text-emerald-300 font-semibold'
                                  : ouOdds.book.ev_over_pct >= 0 ? 'text-emerald-400'
                                  : ouOdds.book.ev_over_pct >= -5 ? 'text-zinc-400'
                                  : 'text-rose-400'
                                }>
                                  {ouOdds.book.ev_over_pct == null ? '—'
                                    : `${ouOdds.book.ev_over_pct >= 0 ? '+' : ''}${ouOdds.book.ev_over_pct.toFixed(1)}%`}
                                </span>
                                {ouOdds.book.ev_over_cons_pct != null && (
                                  <span
                                    className="text-zinc-600 text-[9px]"
                                    title="Conservative EV (uses p_lower_95)"
                                    data-testid={`ou-ev-over-cons-${entry.line}`}
                                  >
                                    ({ouOdds.book.ev_over_cons_pct >= 0 ? '+' : ''}{ouOdds.book.ev_over_cons_pct.toFixed(1)}%c)
                                  </span>
                                )}
                                <span className="text-zinc-700">|</span>
                                <span className="text-zinc-500">U</span>
                                <span className="text-zinc-200">
                                  {fmtFloat(ouOdds.book.under_odd, 2)}
                                </span>
                                <span
                                  title={`EV raw (p_calibrated × ${fmtFloat(ouOdds.book.under_odd, 2)} − 1)${ouOdds.book.ev_under_cons_pct != null ? ` · cons ${ouOdds.book.ev_under_cons_pct >= 0 ? '+' : ''}${ouOdds.book.ev_under_cons_pct.toFixed(1)}%` : ''}`}
                                  className={
                                  ouOdds.book.ev_under_pct >= 5 ? 'text-emerald-300 font-semibold'
                                  : ouOdds.book.ev_under_pct >= 0 ? 'text-emerald-400'
                                  : ouOdds.book.ev_under_pct >= -5 ? 'text-zinc-400'
                                  : 'text-rose-400'
                                }>
                                  {ouOdds.book.ev_under_pct == null ? '—'
                                    : `${ouOdds.book.ev_under_pct >= 0 ? '+' : ''}${ouOdds.book.ev_under_pct.toFixed(1)}%`}
                                </span>
                                {ouOdds.book.ev_under_cons_pct != null && (
                                  <span
                                    className="text-zinc-600 text-[9px]"
                                    title="Conservative EV (uses p_lower_95)"
                                    data-testid={`ou-ev-under-cons-${entry.line}`}
                                  >
                                    ({ouOdds.book.ev_under_cons_pct >= 0 ? '+' : ''}{ouOdds.book.ev_under_cons_pct.toFixed(1)}%c)
                                  </span>
                                )}
                                {ouOdds.book.is_stale && (
                                  <span className="text-rose-400">·stale</span>
                                )}
                              </>
                            ) : (
                              <span className="text-zinc-700">no book</span>
                            )}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Scoreline */}
              <div className="rounded-sm p-3" style={{ background: '#0F0F11' }}
                   data-testid="prediction-scoreline">
                <div className="text-[10px] uppercase text-zinc-500 mb-2"
                     style={heading}>Scoreline distribution</div>
                <div className="grid grid-cols-3 md:grid-cols-6 gap-1.5 text-[11px]"
                     style={mono}>
                  {outputs?.scoreline_probs && ['3_0_p1', '3_1_p1', '3_2_p1',
                    '3_2_p2', '3_1_p2', '3_0_p2'].map((k) => {
                    const p = outputs.scoreline_probs[k] || 0;
                    const label = k.replace('_p1', ' p1').replace('_p2', ' p2')
                      .replace('_', '-');
                    return (
                      <div key={k} className="rounded-sm p-1.5"
                           style={{ background: '#18181B' }}>
                        <div className="text-zinc-500 text-[9px]">{label}</div>
                        <div className="text-zinc-100">{fmtPct(p, 1)}</div>
                        <div className="h-1 bg-zinc-900 rounded-sm mt-1">
                          <div className="h-1 bg-emerald-600 rounded-sm"
                               style={{ width: `${Math.min(100, p * 100 * 3)}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="text-[10px] text-zinc-500" style={mono}>
                predicted_at: {fmtDate(prediction.predicted_at)} · persisted in
                tt_setscore_predictions
              </div>
              </>
              )}
            </div>
          )}
          {prediction?.error && (
            <div className="text-rose-300 text-xs" style={mono}>
              Errore: {prediction.error}
            </div>
          )}
        </div>
      </div>

      {/* SetScore Close Capture (read-only health) */}
      <SectionTitle icon={Activity} title="Close Capture (every 2 min)"
        right={
          <button
            onClick={loadCloseCapture}
            data-testid="close-capture-refresh-btn"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-xs text-zinc-300 hover:bg-zinc-900"
            style={{ border: '1px solid #3F3F46' }}
          >
            <RefreshCw size={12} /> Refresh
          </button>
        } />
      <div className="rounded-sm border border-zinc-900 p-4"
           style={{ background: '#0A0A0A' }}
           data-testid="close-capture-section">
        {!closeCapture && (
          <div className="text-[11px] text-zinc-500" style={mono}>
            Forces an odds snapshot in the last 0-5 min before kickoff
            for matches with a PENDING SetScore prediction. Boosts the
            EXACT_CLOSE share of the CLV settler. Rate-limited (max 20/run,
            3 min cooldown, auto backoff on 429). PAPER_SIGNAL only.
          </div>
        )}
        {closeCapture && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs"
               style={mono} data-testid="close-capture-grid">
            <div className="rounded-sm p-2 border border-zinc-900"
                 data-testid="close-capture-last-run">
              <div className="text-[10px] text-zinc-500 uppercase tracking-wider">
                last run
              </div>
              <div className="text-sm text-zinc-200">
                {closeCapture.close_capture_last_run?.run_id?.slice(-13) ?? '—'}
              </div>
              <div className="text-[10px] text-zinc-500">
                refreshed: {closeCapture.close_capture_last_run?.matches_refreshed ?? 0}
                {' · '}seen: {closeCapture.close_capture_last_run?.matches_seen ?? 0}
                {' · '}errors: {closeCapture.close_capture_last_run?.errors ?? 0}
                {closeCapture.close_capture_last_run?.in_backoff ? ' · BACKOFF' : ''}
              </div>
            </div>
            <div className="rounded-sm p-2 border border-zinc-900"
                 data-testid="close-capture-24h">
              <div className="text-[10px] text-zinc-500 uppercase tracking-wider">
                24h
              </div>
              <div className="text-sm text-zinc-200">
                {closeCapture.close_capture_24h?.runs ?? 0} runs
                {' · '}{closeCapture.close_capture_24h?.matches_refreshed ?? 0} refreshed
              </div>
              <div className="text-[10px] text-zinc-500">
                skipped_recent: {closeCapture.close_capture_24h?.matches_skipped_recent ?? 0}
                {' · '}errors: {closeCapture.close_capture_24h?.errors ?? 0}
              </div>
            </div>
            <div className="rounded-sm p-2 border border-zinc-900"
                 data-testid="close-capture-exact-close">
              <div className="text-[10px] text-zinc-500 uppercase tracking-wider">
                EXACT_CLOSE
              </div>
              <div className="text-sm font-semibold text-emerald-300">
                {closeCapture.exact_close_count ?? 0}
              </div>
              <div className="text-[10px] text-zinc-500">
                snapshots within ±15 min of kickoff
              </div>
            </div>
            <div className="rounded-sm p-2 border border-zinc-900"
                 data-testid="close-capture-best-prematch">
              <div className="text-[10px] text-zinc-500 uppercase tracking-wider">
                BEST_AVAILABLE_PREMATCH
              </div>
              <div className="text-sm text-zinc-200">
                {closeCapture.best_available_prematch_count ?? 0}
              </div>
              <div className="text-[10px] text-zinc-500">
                15-120 min pre-kickoff
              </div>
            </div>
          </div>
        )}
      </div>


      {/* Recent Predictions */}
      <SectionTitle icon={Target} title="Recent Persisted Predictions" right={
        <button
          onClick={loadRecent}
          disabled={recentLoading}
          data-testid="refresh-recent-btn"
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-xs text-zinc-400 hover:text-zinc-200 disabled:opacity-50"
          style={{ border: '1px solid #27272A' }}
        >
          {recentLoading ? <Loader2 size={12} className="animate-spin" />
            : <RefreshCw size={12} />} Reload
        </button>
      } />
      <div className="rounded-sm overflow-hidden" style={card}>
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="recent-predictions-table">
            <thead>
              <tr className="text-[10px] text-zinc-500 uppercase tracking-wider">
                <th className="py-2 px-3 text-left" style={heading}>predicted_at</th>
                <th className="py-2 px-3 text-left" style={heading}>match</th>
                <th className="py-2 px-3 text-right" style={heading}>setscore p1</th>
                <th className="py-2 px-3 text-right" style={heading}>pascal</th>
                <th className="py-2 px-3 text-right" style={heading}>lw</th>
                <th className="py-2 px-3 text-right" style={heading}>mw_v2</th>
                <th className="py-2 px-3 text-right" style={heading}>market</th>
                <th className="py-2 px-3 text-right" style={heading}>Δ pascal</th>
                <th className="py-2 px-3 text-right" style={heading}>Δ lw</th>
                <th className="py-2 px-3 text-right" style={heading}>Δ mw_v2</th>
                <th className="py-2 px-3 text-right" style={heading}>Δ mkt</th>
                <th className="py-2 px-3 text-right" style={heading}>E[pts]</th>
                <th className="py-2 px-3 text-right" style={heading}>status</th>
                <th className="py-2 px-3 text-center" style={heading}>trace</th>
              </tr>
            </thead>
            <tbody>
              {recent?.items?.map((r) => (
                <tr key={r.match_id}
                    data-testid={`recent-row-${r.match_id}`}
                    className="border-t border-zinc-900 hover:bg-zinc-900/50 cursor-pointer"
                    onClick={() => {
                      setSelectedMatch({ id: r.match_id,
                        player1_name: r.player1_name,
                        player2_name: r.player2_name,
                        tournament_name: r.tournament_name,
                        match_datetime: r.match_datetime,
                        status: r.match_status });
                      runPredict(r.match_id);
                    }}>
                  <td className="py-1.5 px-3 text-zinc-500" style={mono}>
                    {fmtDate(r.predicted_at)}
                  </td>
                  <td className="py-1.5 px-3 text-zinc-200" style={mono}>
                    {r.player1_name} <span className="text-zinc-600">vs</span>{' '}
                    {r.player2_name}
                    <div className="text-[9px] text-zinc-600">
                      {r.tournament_name || '—'}
                    </div>
                  </td>
                  <td className="py-1.5 px-3 text-right text-emerald-300 font-semibold" style={mono}>
                    {fmtPct(r.engine_p1)}
                  </td>
                  <td className="py-1.5 px-3 text-right text-zinc-300" style={mono}>
                    {fmtPct(r.pascal_p1)}
                  </td>
                  <td className="py-1.5 px-3 text-right text-zinc-300" style={mono}>
                    {fmtPct(r.lw_p1)}
                  </td>
                  <td className="py-1.5 px-3 text-right text-zinc-400" style={mono}>
                    {fmtPct(r.mw_v2_p1)}
                  </td>
                  <td className="py-1.5 px-3 text-right text-zinc-300" style={mono}>
                    {fmtPct(r.market_p1)}
                  </td>
                  <td className={`py-1.5 px-3 text-right ${
                    Number(r.delta_vs_pascal_p1) > 0 ? 'text-emerald-400'
                      : 'text-rose-400'}`} style={mono}>
                    {fmtDelta(r.delta_vs_pascal_p1)}
                  </td>
                  <td className={`py-1.5 px-3 text-right ${
                    Number(r.delta_vs_lw_p1) > 0 ? 'text-emerald-400'
                      : 'text-rose-400'}`} style={mono}>
                    {fmtDelta(r.delta_vs_lw_p1)}
                  </td>
                  <td className={`py-1.5 px-3 text-right ${
                    Number(r.delta_vs_mw_v2_p1) > 0 ? 'text-emerald-400'
                      : 'text-rose-400'}`} style={mono}>
                    {fmtDelta(r.delta_vs_mw_v2_p1)}
                  </td>
                  <td className={`py-1.5 px-3 text-right ${
                    Number(r.delta_vs_market_p1) > 0 ? 'text-emerald-400'
                      : 'text-rose-400'}`} style={mono}>
                    {fmtDelta(r.delta_vs_market_p1)}
                  </td>
                  <td className="py-1.5 px-3 text-right text-zinc-400" style={mono}>
                    {r.expected_total_points?.mean
                      ? Number(r.expected_total_points.mean).toFixed(1) : '—'}
                  </td>
                  <td className="py-1.5 px-3 text-right" style={mono}>
                    <Pill color={r.match_status === 'finished' ? 'emerald' :
                      r.match_status === 'live' ? 'rose' : 'zinc'}>
                      {r.match_status || '—'}
                      {r.winner_side ? ` · ${r.winner_side}` : ''}
                    </Pill>
                  </td>
                  <td className="py-1.5 px-3 text-center" style={mono}>
                    <button
                      data-testid={`trace-btn-${r.match_id}`}
                      title="Open SetProfile Calculation Trace for this match"
                      onClick={(e) => {
                        e.stopPropagation();
                        try {
                          navigator.clipboard?.writeText(r.match_id);
                        } catch (_) { /* noop */ }
                        window.dispatchEvent(new CustomEvent(
                          'pointedge:setprofile-trace-load',
                          { detail: { matchId: r.match_id } },
                        ));
                      }}
                      className="px-1.5 py-0.5 rounded-sm bg-amber-900/30 border border-amber-800 text-amber-200 hover:bg-amber-900/60 text-[10px]"
                    >
                      🐛 trace
                    </button>
                  </td>
                </tr>
              ))}
              {(!recent?.items || recent.items.length === 0) && (
                <tr><td colSpan={14} className="py-6 text-center text-zinc-500 text-xs"
                        style={mono}>
                  Nessuna prediction persistita. Seleziona un match sopra per generare
                  la prima.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Backtest */}
      <SectionTitle icon={PlayCircle} title="Backtest Runs" right={
        <button
          onClick={runBacktest}
          disabled={btRunning}
          data-testid="run-backtest-btn"
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-xs text-violet-300 hover:bg-violet-950/30 disabled:opacity-50"
          style={{ border: '1px solid #5B21B6' }}
        >
          {btRunning ? <Loader2 size={12} className="animate-spin" />
            : <PlayCircle size={12} />}
          Run backtest (500 match)
        </button>
      } />
      <div className="rounded-sm overflow-hidden" style={card}>
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="backtest-table">
            <thead>
              <tr className="text-[10px] text-zinc-500 uppercase tracking-wider">
                <th className="py-2 px-3 text-left" style={heading}>generated_at</th>
                <th className="py-2 px-3 text-left" style={heading}>window</th>
                <th className="py-2 px-3 text-right" style={heading}>n</th>
                <th className="py-2 px-3 text-right" style={heading}>Brier</th>
                <th className="py-2 px-3 text-right" style={heading}>LogLoss</th>
                <th className="py-2 px-3 text-right" style={heading}>Acc</th>
                <th className="py-2 px-3 text-right" style={heading}>ROI sim</th>
              </tr>
            </thead>
            <tbody>
              {btReport?.runs?.map((r, idx) => {
                const nMatches = r.n_matches ?? r.n_used ?? r.n;
                const hasData = nMatches != null && nMatches > 0;
                return (
                <tr key={idx} className="border-t border-zinc-900"
                    data-testid={`backtest-row-${idx}`}>
                  <td className="py-1.5 px-3 text-zinc-500" style={mono}>
                    {fmtDate(r.generated_at)}
                  </td>
                  <td className="py-1.5 px-3 text-zinc-400" style={mono}>
                    {(r.from_date || r.window?.from) || '∞'} → {(r.to_date || r.window?.to) || 'now'}
                  </td>
                  <td className="py-1.5 px-3 text-right text-zinc-200" style={mono}>
                    {fmtNum(nMatches)}
                  </td>
                  <td className="py-1.5 px-3 text-right text-emerald-300" style={mono}>
                    {fmtFloat(r.brier_engine ?? r.brier ?? r.markets?.mw?.brier, 4)}
                  </td>
                  <td className="py-1.5 px-3 text-right text-sky-300" style={mono}>
                    {fmtFloat(r.logloss_engine ?? r.logloss ?? r.log_loss ?? r.markets?.mw?.log_loss, 4)}
                  </td>
                  <td className="py-1.5 px-3 text-right text-zinc-200" style={mono}>
                    {fmtPct(r.accuracy_engine ?? r.accuracy ?? r.markets?.mw?.accuracy)}
                  </td>
                  <td className="py-1.5 px-3 text-right text-amber-300" style={mono}>
                    {r.roi_simulated != null ? fmtPct(r.roi_simulated, 2) : '—'}
                  </td>
                </tr>
              );})}
              {/* Empty-state rows: surface skip-reason diagnostics so the
                  operator knows WHY a backtest produced 0 valid samples. */}
              {btReport?.runs?.filter(r => (r.n_matches ?? r.n_used ?? r.n) === 0).slice(0, 1).map((r, idx) => (
                <tr key={`diag-${idx}`} className="border-t border-amber-900/40 bg-amber-950/10"
                    data-testid={`backtest-empty-diag-${idx}`}>
                  <td colSpan={7} className="py-2 px-3 text-[11px] text-amber-300" style={mono}>
                    Run vuoto — motivo dominante:{' '}
                    <strong>{r.empty_reason || 'unknown'}</strong>.{' '}
                    Scansionati {r.n_scanned ?? '—'},{' '}
                    settled {r.n_settled ?? '—'},{' '}
                    valid {r.n_valid_matches ?? '—'},{' '}
                    cold-start skip {r.n_cold_start_skipped ?? '—'}.{' '}
                    {r.skip_reasons && (
                      <span className="text-amber-400/70">
                        skip_reasons: {Object.entries(r.skip_reasons).filter(([,v])=>v>0).map(([k,v])=>`${k}=${v}`).join(', ') || 'none'}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
              {(!btReport?.runs || btReport.runs.length === 0) && (
                <tr><td colSpan={7} className="py-6 text-center text-zinc-500 text-xs"
                        style={mono}>
                  Nessun backtest run ancora. Clicca "Run backtest" per generare il primo.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Real Odds & EV Check — read-only audit */}
      <RealOddsEvCheck
        data={realOdds}
        loading={realOddsLoading}
        horizon={realOddsHorizon}
        setHorizon={setRealOddsHorizon}
        limit={realOddsLimit}
        setLimit={setRealOddsLimit}
        onlyWithBook={realOddsOnlyWithBook}
        setOnlyWithBook={setRealOddsOnlyWithBook}
        minEv={realOddsMinEv}
        setMinEv={setRealOddsMinEv}
        hideStale={realOddsHideStale}
        setHideStale={setRealOddsHideStale}
        onReload={loadRealOddsCheck}
      />

      <div className="rounded-sm p-3 text-[11px] text-zinc-500 leading-relaxed"
           style={{ ...mono, ...card }}>
        <span className="text-amber-400">Note:</span> engine operante esclusivamente
        in <span className="text-violet-300">PAPER_SIGNAL</span>.
        Zero impatto su <span className="text-zinc-300">mw_v2</span>,
        <span className="text-zinc-300"> totals_engine</span>, Telegram,
        Kelly e Policy Guard. Fit nightly deterministico (replay cronologico).
        Predictions persistite in <span className="text-zinc-300">tt_setscore_predictions</span>.
      </div>

      {/* Personal Telegram Signals — modal + daily recap. Renders here
          so that any SignalQualityBadge in the page can hand-off to the
          modal without prop-drilling. */}
      <PersonalSignalModal
        open={personalSendOpen}
        form={personalSendForm}
        pin={pin}
        onClose={() => setPersonalSendOpen(false)}
        onSent={() => loadPersonalRecap()}
      />
    </div>
  );
}

// ============================================================
// Real Odds & EV Check section (read-only)
// Cross-references SetScore predictions with the latest available book
// odds (MW + HC -1.5 + O/U lines) to instantly compute EV.
// ============================================================
function evColor(ev) {
  if (ev === null || ev === undefined) return 'text-zinc-600';
  const n = Number(ev);
  if (Number.isNaN(n)) return 'text-zinc-600';
  if (n >= 5) return 'text-emerald-300 font-semibold';
  if (n >= 0) return 'text-emerald-400';
  if (n >= -5) return 'text-zinc-400';
  return 'text-rose-400';
}

function fmtEv(ev) {
  if (ev === null || ev === undefined) return '—';
  const n = Number(ev);
  if (Number.isNaN(n)) return '—';
  const sign = n >= 0 ? '+' : '';
  return `${sign}${n.toFixed(2)}%`;
}

function bestEvOfRow(item) {
  const evs = [];
  const mwBook = item?.mw?.book;
  if (mwBook) {
    evs.push(mwBook.ev_p1_pct, mwBook.ev_p2_pct);
  }
  const hcBook = item?.hc_minus_1_5?.book;
  if (hcBook) {
    evs.push(hcBook.ev_p1_pct, hcBook.ev_p2_pct);
  }
  for (const ou of item?.ou || []) {
    if (ou?.book) {
      evs.push(ou.book.ev_over_pct, ou.book.ev_under_pct);
    }
  }
  const numeric = evs
    .filter((v) => v !== null && v !== undefined && !Number.isNaN(Number(v)))
    .map(Number);
  if (numeric.length === 0) return null;
  return Math.max(...numeric);
}

function rowHasFreshBook(item) {
  const mwBook = item?.mw?.book;
  if (mwBook && !mwBook.is_stale) return true;
  const hcBook = item?.hc_minus_1_5?.book;
  if (hcBook && !hcBook.is_stale) return true;
  for (const ou of item?.ou || []) {
    if (ou?.book && !ou.book.is_stale) return true;
  }
  return false;
}

function SendInlineCell({ label, side, enabled, state, matchId, onSend }) {
  const status = state?.status || 'idle';
  const tg = state?.result?.telegram_status;
  const reason = state?.result?.gate_reason;

  if (status === 'sending') {
    return (
      <button
        disabled
        data-testid={`send-paper-${matchId}-${side}-loading`}
        className="block w-full mb-1 px-2 py-1 rounded-sm text-[10px] text-zinc-400 disabled:opacity-60"
        style={{ border: '1px solid #27272A' }}
      >
        <Loader2 size={10} className="animate-spin inline mr-1" />
        sending {side}
      </button>
    );
  }
  if (status === 'ok') {
    return (
      <div data-testid={`send-paper-${matchId}-${side}-ok`}
           className="block w-full mb-1 px-2 py-1 rounded-sm text-[10px] text-emerald-300"
           style={{ border: '1px solid #064E3B', background: '#022C22' }}
           title={`signal_id ${state?.result?.signal_id || ''} · tg ${tg || ''}`}>
        ✓ {state?.result?.status === 'SENT' ? 'sent' : 'logged'} {side}
      </div>
    );
  }
  if (status === 'err') {
    return (
      <div data-testid={`send-paper-${matchId}-${side}-err`}
           className="block w-full mb-1 px-2 py-1 rounded-sm text-[10px] text-rose-300"
           style={{ border: '1px solid #7F1D1D', background: '#1C0808' }}
           title={reason || ''}>
        ✕ {side} · {(state?.result?.status || 'ERR').replace('BLOCKED_', '')}
      </div>
    );
  }
  if (!enabled) {
    return (
      <div data-testid={`send-paper-${matchId}-${side}-disabled`}
           className="block w-full mb-1 px-2 py-1 rounded-sm text-[10px] text-zinc-700"
           title={`Inattivo: serve EV ≥ +5% e quote FRESH (≤ 600s)`}>
        — {side}
      </div>
    );
  }
  return (
    <button
      onClick={() => onSend(matchId, side)}
      data-testid={`send-paper-${matchId}-${side}-btn`}
      className="block w-full mb-1 px-2 py-1 rounded-sm text-[10px] text-violet-300 hover:bg-violet-950/40"
      style={{ border: '1px solid #5B21B6' }}
      title={`Invia paper signal ${label}`}
    >
      <Send size={10} className="inline mr-1" />
      {side.toUpperCase()}
    </button>
  );
}

function RealOddsEvCheck({
  data, loading, horizon, setHorizon, limit, setLimit,
  onlyWithBook, setOnlyWithBook, minEv, setMinEv,
  hideStale, setHideStale, onReload,
}) {
  const items = data?.items || [];
  const [pin] = useState(() => sessionStorage.getItem('admin_pin') || '');
  // Per-row per-side state: 'idle' | 'sending' | 'ok' | 'err' + last result
  const [sendState, setSendState] = useState({});

  const sendPaperSignal = useCallback(async (matchId, side) => {
    const key = `${matchId}_${side}`;
    setSendState((s) => ({ ...s, [key]: { status: 'sending' } }));
    try {
      const r = await adminPost('/tt/setscore/paper-signal-send-one', pin, {
        match_id: matchId, side,
      });
      const ok = r?.status === 'SENT' || r?.status === 'LOGGED_ONLY';
      setSendState((s) => ({
        ...s,
        [key]: { status: ok ? 'ok' : 'err', result: r },
      }));
    } catch (e) {
      setSendState((s) => ({
        ...s,
        [key]: { status: 'err', result: { gate_reason: String(e?.message || e) } },
      }));
    }
  }, [pin]);

  const filtered = useMemo(() => {
    return items.filter((it) => {
      if (hideStale && !rowHasFreshBook(it)) return false;
      if (minEv > 0) {
        const best = bestEvOfRow(it);
        if (best === null || best < minEv) return false;
      }
      return true;
    });
  }, [items, minEv, hideStale]);

  return (
    <div className="space-y-3" data-testid="real-odds-ev-check-section">
      <SectionTitle
        icon={Target}
        title="Real Odds & EV Check (read-only)"
        right={
          <div className="flex items-center gap-2 flex-wrap">
            <Pill color="zinc" testid="real-odds-stale-threshold">
              STALE &gt; {data?.stale_after_sec ?? 600}s
            </Pill>
            <Pill color="zinc" testid="real-odds-count-pill">
              {data?.count ?? 0} match · {filtered.length} mostrati
            </Pill>
            <button
              onClick={onReload}
              disabled={loading}
              data-testid="real-odds-refresh-btn"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-xs text-zinc-400 hover:text-zinc-200 disabled:opacity-50"
              style={{ border: '1px solid #27272A' }}
            >
              {loading ? <Loader2 size={12} className="animate-spin" />
                : <RefreshCw size={12} />}
              Refresh
            </button>
          </div>
        }
      />

      {/* Filters strip */}
      <div className="rounded-sm p-3 flex items-center gap-4 flex-wrap text-[11px]"
           style={{ ...mono, ...card }}>
        <label className="flex items-center gap-2 text-zinc-400">
          <span>Horizon</span>
          <select
            value={horizon}
            onChange={(e) => setHorizon(Number(e.target.value))}
            data-testid="real-odds-horizon-select"
            className="bg-zinc-900 border border-zinc-800 text-zinc-200 px-2 py-1 rounded-sm"
          >
            <option value={6}>6h</option>
            <option value={12}>12h</option>
            <option value={24}>24h</option>
            <option value={48}>48h</option>
            <option value={72}>72h</option>
          </select>
        </label>
        <label className="flex items-center gap-2 text-zinc-400">
          <span>Max righe</span>
          <select
            value={limit}
            onChange={(e) => setLimit(Number(e.target.value))}
            data-testid="real-odds-limit-select"
            className="bg-zinc-900 border border-zinc-800 text-zinc-200 px-2 py-1 rounded-sm"
          >
            <option value={15}>15</option>
            <option value={30}>30</option>
            <option value={60}>60</option>
            <option value={120}>120</option>
          </select>
        </label>
        <label className="flex items-center gap-2 text-zinc-400">
          <span>EV min</span>
          <select
            value={minEv}
            onChange={(e) => setMinEv(Number(e.target.value))}
            data-testid="real-odds-minev-select"
            className="bg-zinc-900 border border-zinc-800 text-zinc-200 px-2 py-1 rounded-sm"
          >
            <option value={0}>tutti</option>
            <option value={0.01}>≥ 0%</option>
            <option value={3}>≥ +3%</option>
            <option value={5}>≥ +5%</option>
            <option value={10}>≥ +10%</option>
          </select>
        </label>
        <label className="flex items-center gap-2 text-zinc-400 cursor-pointer">
          <input
            type="checkbox"
            checked={onlyWithBook}
            onChange={(e) => setOnlyWithBook(e.target.checked)}
            data-testid="real-odds-onlybook-cb"
          />
          <span>solo match con quote</span>
        </label>
        <label className="flex items-center gap-2 text-zinc-400 cursor-pointer">
          <input
            type="checkbox"
            checked={hideStale}
            onChange={(e) => setHideStale(e.target.checked)}
            data-testid="real-odds-hidestale-cb"
          />
          <span>nascondi tutto-stale</span>
        </label>
        <span className="text-zinc-600 ml-auto">
          generato: {fmtDate(data?.generated_at)}
        </span>
      </div>

      <div className="rounded-sm overflow-hidden" style={card}>
        <table className="w-full text-[11px]" style={mono}>
          <thead>
            <tr className="border-b border-zinc-800 text-zinc-500 uppercase tracking-wider text-[10px]">
              <th className="py-2 px-2 text-left">Match</th>
              <th className="py-2 px-2 text-right">MW p1 / p2 (cal)</th>
              <th className="py-2 px-2 text-right">MW book (P1/P2)</th>
              <th className="py-2 px-2 text-right">EV MW P1</th>
              <th className="py-2 px-2 text-right">EV MW P2</th>
              <th className="py-2 px-2 text-center">Send MW</th>
              <th className="py-2 px-2 text-right">HC −1.5 p1/p2</th>
              <th className="py-2 px-2 text-right">HC book (H/A)</th>
              <th className="py-2 px-2 text-right">EV HC P1</th>
              <th className="py-2 px-2 text-right">EV HC P2</th>
              <th className="py-2 px-2 text-left">O/U lines</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={11} className="py-6 text-center text-zinc-500"
                     data-testid="real-odds-loading">
                <Loader2 size={14} className="animate-spin inline mr-2" />
                Loading real odds…
              </td></tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={11} className="py-6 text-center text-zinc-500"
                     data-testid="real-odds-empty">
                Nessun match con quote disponibili nei filtri correnti.
              </td></tr>
            )}
            {!loading && filtered.map((it) => {
              const mw = it.mw || {};
              const hc = it.hc_minus_1_5 || {};
              const mwBook = mw.book;
              const hcBook = hc.book;
              // Eligibility for the inline Send buttons:
              // require fresh MW odds AND ev_mean ≥ +5% on the side.
              const canSendP1 = !!mwBook && !mwBook.is_stale
                && (mwBook.ev_p1_pct ?? -999) >= 5;
              const canSendP2 = !!mwBook && !mwBook.is_stale
                && (mwBook.ev_p2_pct ?? -999) >= 5;
              const stateP1 = sendState[`${it.match_id}_p1`];
              const stateP2 = sendState[`${it.match_id}_p2`];
              return (
                <tr key={it.match_id}
                    className="border-b border-zinc-900 hover:bg-zinc-900/30"
                    data-testid={`real-odds-row-${it.match_id}`}>
                  <td className="py-2 px-2">
                    <div className="text-zinc-200">
                      {it.player1_name || '?'} <span className="text-zinc-600">vs</span>{' '}
                      {it.player2_name || '?'}
                    </div>
                    <div className="text-[10px] text-zinc-500">
                      {it.tournament_name} · {fmtDate(it.match_datetime)}
                      {it.cold_start && (
                        <span className="ml-2 text-amber-400">cold</span>
                      )}
                      {it.model_uncertainty_flag && (
                        <span className="ml-2 text-rose-400">uncertain</span>
                      )}
                    </div>
                  </td>
                  <td className="py-2 px-2 text-right text-zinc-200">
                    {fmtPct(mw.p1_p_cal, 1)} / {fmtPct(mw.p2_p_cal, 1)}
                    <div className="text-[10px] text-zinc-500">
                      fair {fmtFloat(mw.p1_fair_cons, 2)} / {fmtFloat(mw.p2_fair_cons, 2)}
                    </div>
                  </td>
                  <td className="py-2 px-2 text-right">
                    {mwBook ? (
                      <>
                        <div className="text-zinc-200">
                          {fmtFloat(mwBook.odd_p1, 2)} / {fmtFloat(mwBook.odd_p2, 2)}
                        </div>
                        <div className="text-[10px]">
                          <span className="text-zinc-500">{mwBook.bookmaker}</span>
                          {' · '}
                          <span className={mwBook.is_stale ? 'text-rose-400' : 'text-emerald-400'}>
                            {fmtAge(mwBook.age_sec)}
                          </span>
                        </div>
                      </>
                    ) : <span className="text-zinc-600">—</span>}
                  </td>
                  <td className={`py-2 px-2 text-right ${evColor(mwBook?.ev_p1_pct)}`}>
                    {fmtEv(mwBook?.ev_p1_pct)}
                    <div className="text-[10px] text-zinc-500">
                      cons {fmtEv(mwBook?.ev_p1_cons_pct)}
                    </div>
                  </td>
                  <td className={`py-2 px-2 text-right ${evColor(mwBook?.ev_p2_pct)}`}>
                    {fmtEv(mwBook?.ev_p2_pct)}
                    <div className="text-[10px] text-zinc-500">
                      cons {fmtEv(mwBook?.ev_p2_cons_pct)}
                    </div>
                  </td>
                  <td className="py-2 px-2 text-center">
                    <SendInlineCell
                      label={`P1 ${it.player1_name || ''}`.trim()}
                      side="p1"
                      enabled={canSendP1}
                      state={stateP1}
                      matchId={it.match_id}
                      onSend={sendPaperSignal}
                    />
                    <SendInlineCell
                      label={`P2 ${it.player2_name || ''}`.trim()}
                      side="p2"
                      enabled={canSendP2}
                      state={stateP2}
                      matchId={it.match_id}
                      onSend={sendPaperSignal}
                    />
                  </td>
                  <td className="py-2 px-2 text-right text-zinc-200">
                    {fmtPct(hc.p1_p_cal, 1)} / {fmtPct(hc.p2_p_cal, 1)}
                  </td>
                  <td className="py-2 px-2 text-right">
                    {hcBook ? (
                      <>
                        <div className="text-zinc-200">
                          {fmtFloat(hcBook.home_odd, 2)} / {fmtFloat(hcBook.away_odd, 2)}
                        </div>
                        <div className="text-[10px]">
                          <span className="text-zinc-500">{hcBook.bookmaker}</span>
                          {' · '}
                          <span className={hcBook.is_stale ? 'text-rose-400' : 'text-emerald-400'}>
                            {fmtAge(hcBook.age_sec)}
                          </span>
                        </div>
                      </>
                    ) : <span className="text-zinc-600">—</span>}
                  </td>
                  <td className={`py-2 px-2 text-right ${evColor(hcBook?.ev_p1_pct)}`}>
                    {fmtEv(hcBook?.ev_p1_pct)}
                  </td>
                  <td className={`py-2 px-2 text-right ${evColor(hcBook?.ev_p2_pct)}`}>
                    {fmtEv(hcBook?.ev_p2_pct)}
                  </td>
                  <td className="py-2 px-2">
                    {(it.ou || []).length === 0 ? (
                      <span className="text-zinc-600">—</span>
                    ) : (
                      <div className="space-y-0.5">
                        {(it.ou || []).map((ou) => (
                          <div key={ou.line}
                               className="flex items-center gap-2 text-[10px]">
                            <span className="text-zinc-300 w-12">
                              {fmtFloat(ou.line, 1)}
                            </span>
                            <span className="text-zinc-500">
                              O {fmtFloat(ou?.book?.over_odd, 2)}
                            </span>
                            <span className={evColor(ou?.book?.ev_over_pct)}>
                              {fmtEv(ou?.book?.ev_over_pct)}
                            </span>
                            <span className="text-zinc-700">|</span>
                            <span className="text-zinc-500">
                              U {fmtFloat(ou?.book?.under_odd, 2)}
                            </span>
                            <span className={evColor(ou?.book?.ev_under_pct)}>
                              {fmtEv(ou?.book?.ev_under_pct)}
                            </span>
                            {ou?.book?.is_stale && (
                              <span className="text-rose-400">·stale</span>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="rounded-sm p-3 text-[10px] text-zinc-500 leading-relaxed"
           style={{ ...mono, ...card }}>
        <span className="text-zinc-400">EV =</span> p_calibrata × quota − 1.
        <span className="text-zinc-600"> ·</span>{' '}
        <span className="text-zinc-400">EV cons =</span> usa{' '}
        <span className="text-zinc-300">p_lower_95</span> (intervallo conservativo).
        <span className="text-zinc-600"> ·</span> Quote da{' '}
        <span className="text-zinc-300">tt_odds_snapshots</span>,{' '}
        <span className="text-zinc-300">tt_handicap_market_snapshots</span>{' '}
        (handicap=−1.5 only) e{' '}
        <span className="text-zinc-300">tt_totals_market_snapshots</span>.
        Strict <span className="text-emerald-300">READ-ONLY</span>: nessuno scraper
        viene attivato.
      </div>
    </div>
  );
}

// ----- UpcomingBadgeRow: per-row Smart Filter badges -----
const _PROFILE_BADGE = {
  STOMP_CLEAN:     'bg-violet-950/60 border-violet-800 text-violet-200',
  CONTROLLED_FAST: 'bg-emerald-950/60 border-emerald-800 text-emerald-200',
  CONTROLLED_LONG: 'bg-cyan-950/60 border-cyan-800 text-cyan-200',
  BALANCED_LONG:   'bg-amber-950/60 border-amber-800 text-amber-200',
  TRUE_CHAOS:      'bg-rose-950/60 border-rose-800 text-rose-200',
  BALANCED:        'bg-zinc-900 border-zinc-700 text-zinc-300',
};
const _MARKET_LABEL = {
  mw: 'MW', hc_fav: 'HC fav', hc_dog: 'HC dog', over: 'Over', under: 'Under',
};
const _GUARD_LABEL = {
  FAST_STRONG: 'FAST STRONG',
  FATIGUE_COLLAPSE: 'COLLAPSE',
  FATIGUE_OVER: 'FATIGUE OVER',
  MW_FRAGILE: 'MW FRAGILE',
  DIVERGENCE_HARD: 'DIVERGENCE HARD',
  OVERCONFIDENT: 'OVERCONFIDENT',
};

function UpcomingBadgeRow({ summary, matchId }) {
  if (!summary) return null;
  const {
    profile_type, recommended_market, recommended_ev_pct,
    odds_age_minutes, strongest_guard, any_strong_guard,
    low_confidence_active,
  } = summary;
  if (!profile_type && !recommended_market && !strongest_guard
      && odds_age_minutes == null) {
    return null;
  }
  const profCls = _PROFILE_BADGE[profile_type] || 'bg-zinc-900 border-zinc-800 text-zinc-400';
  const evVal = recommended_ev_pct == null ? null : Number(recommended_ev_pct);
  const evCls = evVal == null ? 'text-zinc-500'
    : evVal >= 5 ? 'text-emerald-300'
    : evVal >= 0 ? 'text-zinc-300'
    : 'text-rose-300';
  const oddsAgeFmt = odds_age_minutes == null
    ? null
    : odds_age_minutes < 60 ? `${Math.round(odds_age_minutes)}m`
    : `${(odds_age_minutes / 60).toFixed(1)}h`;
  return (
    <div
      data-testid={`upcoming-badges-${matchId}`}
      className="flex flex-wrap items-center gap-1 mt-1 text-[9px]"
    >
      {profile_type && (
        <span
          data-testid={`badge-profile-${matchId}`}
          className={`px-1 py-0.5 rounded-sm border ${profCls}`}
        >
          {profile_type}
        </span>
      )}
      {recommended_market && (
        <span
          data-testid={`badge-market-${matchId}`}
          className="px-1 py-0.5 rounded-sm border border-zinc-800 bg-zinc-900 text-zinc-300"
        >
          {_MARKET_LABEL[recommended_market] || recommended_market}
          {low_confidence_active ? ' scout' : ''}
        </span>
      )}
      {any_strong_guard && strongest_guard && (
        <span
          data-testid={`badge-guard-${matchId}`}
          className="px-1 py-0.5 rounded-sm border border-rose-800 bg-rose-950/60 text-rose-200"
        >
          {_GUARD_LABEL[strongest_guard] || strongest_guard}
        </span>
      )}
      {evVal != null && (
        <span
          data-testid={`badge-ev-${matchId}`}
          className={`px-1 py-0.5 rounded-sm border border-zinc-800 bg-zinc-950 ${evCls}`}
        >
          EV {evVal >= 0 ? '+' : ''}{evVal.toFixed(1)}%
        </span>
      )}
      {oddsAgeFmt && (
        <span
          data-testid={`badge-odds-age-${matchId}`}
          className="px-1 py-0.5 rounded-sm border border-zinc-800 bg-zinc-950 text-zinc-500"
          title="age of prediction_odds snapshot"
        >
          book {oddsAgeFmt}
        </span>
      )}
    </div>
  );
}
